define('@ember-data/store/-private', ['exports', '@ember/application', '@ember/debug', '@ember/error', '@ember/utils', '@ember/array', '@ember/object', '@ember/runloop', '@ember/service', '@ember/test', 'ember', 'require', 'rsvp', '@ember/string', '@ember/array/proxy', '@ember/object/computed', '@ember/object/promise-proxy-mixin', '@ember/object/proxy', '@ember/object/compat', '@glimmer/tracking', 'ember-cached-decorator-polyfill'], (function (exports, application, debug, EmberError, utils, EmberArray, object, runloop, Service, test, Ember, require$1, RSVP, string, ArrayProxy, computed, PromiseProxyMixin, ObjectProxy, compat, tracking, emberCachedDecoratorPolyfill) { 'use strict';

  function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

  var EmberError__default = /*#__PURE__*/_interopDefaultLegacy(EmberError);
  var Service__default = /*#__PURE__*/_interopDefaultLegacy(Service);
  var Ember__default = /*#__PURE__*/_interopDefaultLegacy(Ember);
  var require__default = /*#__PURE__*/_interopDefaultLegacy(require$1);
  var RSVP__default = /*#__PURE__*/_interopDefaultLegacy(RSVP);
  var ArrayProxy__default = /*#__PURE__*/_interopDefaultLegacy(ArrayProxy);
  var PromiseProxyMixin__default = /*#__PURE__*/_interopDefaultLegacy(PromiseProxyMixin);
  var ObjectProxy__default = /*#__PURE__*/_interopDefaultLegacy(ObjectProxy);

  /**
    @module @ember-data/store
  */

  // Used by the store to normalize IDs entering the store.  Despite the fact
  // that developers may provide IDs as numbers (e.g., `store.findRecord('person', 1)`),
  // it is important that internally we use strings, since IDs may be serialized
  // and lose type information.  For example, Ember's router may put a record's
  // ID into the URL, and if we later try to deserialize that URL and find the
  // corresponding record, we will not know if it is a string or a number.

  function coerceId(id) {
    if (id === null || id === undefined || id === '') {
      return null;
    }
    if (typeof id === 'string') {
      return id;
    }
    if (typeof id === 'symbol') {
      return id.toString();
    }
    return '' + id;
  }
  function ensureStringId(id) {
    let normalized = null;
    if (typeof id === 'string') {
      normalized = id.length > 0 ? id : null;
    } else if (typeof id === 'number' && !isNaN(id)) {
      normalized = '' + id;
    }
    if (normalized === null) {
      throw new Error(`Expected id to be a string or number, received ${String(id)}`);
    }
    return normalized;
  }

  /**
    @module @ember-data/store
  */

  // All modelNames are dasherized internally. Changing this function may
  // require changes to other normalization hooks (such as typeForRoot).

  /**
   This method normalizes a modelName into the format Ember Data uses
   internally by dasherizing it.

    @method normalizeModelName
    @static
    @public
    @for @ember-data/store
    @param {String} modelName
    @return {String} normalizedModelName
  */
  function normalizeModelName(modelName) {
    return string.dasherize(modelName);
  }

  /*
    DEBUG only fields. Keeping this in a separate interface
    which Typescript then merges with the class definition allow us to
    have a DEBUG only property without paying the cost of that field being
    present and initialized (usually to void 0) in production.
  */

  /**
   * @class WeakCache
   * @extends WeakMap
   * @internal
   */
  class WeakCache extends WeakMap {
    constructor(_fieldName) {
      super();
    }

    /**
     * Retrieve the value for a key from the WeakCache with the expectation
     * that the key exists. Throws the error generated by _expectMsg in DEBUG if
     * that key does not exist.
     *
     * @method getWithError
     * @param obj
     * @internal
     */
    getWithError(obj) {
      let v = this.get(obj);
      return v;
    }

    /**
     * Retrieve the value for a key from the WeakCache, or generate one
     * using the configured _generator method if no value is yet present.
     *
     * @method lookup
     * @param obj
     * @internal
     */
    lookup(obj) {
      let v = super.get(obj);
      if (v === undefined) {
        v = this._generator(obj);
        super.set(obj, v);
      }
      return v;
    }
  }
  var WeakCache$1 = WeakCache;

  function isNonEmptyString(str) {
    return typeof str === 'string' && str.length > 0;
  }

  /**
    @module @ember-data/store
  */

  const IDENTIFIERS = new WeakSet();
  function isStableIdentifier(identifier) {
    return IDENTIFIERS.has(identifier);
  }
  function markStableIdentifier(identifier) {
    IDENTIFIERS.add(identifier);
  }
  function unmarkStableIdentifier(identifier) {
    IDENTIFIERS.delete(identifier);
  }

  /**
    @module @ember-data/store
  */

  const CRYPTO = (() => {
    const hasWindow = typeof window !== 'undefined';
    const isFastBoot = typeof FastBoot !== 'undefined';
    if (isFastBoot) {
      return {
        getRandomValues(buffer) {
          try {
            return FastBoot.require('crypto').randomFillSync(buffer);
          } catch (err) {
            throw new Error('Using createRecord in Fastboot requires you to add the "crypto" package to "fastbootDependencies" in your package.json');
          }
        }
      };
    } else if (hasWindow && typeof window.crypto !== 'undefined') {
      return window.crypto;
    } else {
      throw new Error('ember-data: Cannot find a valid way to generate local identifiers');
    }
  })();

  // we might be able to optimize this by requesting more bytes than we need at a time
  function rng() {
    // WHATWG crypto RNG - http://wiki.whatwg.org/wiki/Crypto
    let rnds8 = new Uint8Array(16);
    return CRYPTO.getRandomValues(rnds8);
  }

  /*
   * Convert array of 16 byte values to UUID string format of the form:
   * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
   */
  const byteToHex = [];
  for (let i = 0; i < 256; ++i) {
    byteToHex[i] = (i + 0x100).toString(16).substr(1);
  }
  function bytesToUuid(buf) {
    let bth = byteToHex;
    // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
    return [bth[buf[0]], bth[buf[1]], bth[buf[2]], bth[buf[3]], '-', bth[buf[4]], bth[buf[5]], '-', bth[buf[6]], bth[buf[7]], '-', bth[buf[8]], bth[buf[9]], '-', bth[buf[10]], bth[buf[11]], bth[buf[12]], bth[buf[13]], bth[buf[14]], bth[buf[15]]].join('');
  }
  function uuidv4() {
    let rnds = rng();

    // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
    rnds[6] = rnds[6] & 0x0f | 0x40;
    rnds[8] = rnds[8] & 0x3f | 0x80;
    return bytesToUuid(rnds);
  }

  let configuredForgetMethod;
  let configuredGenerationMethod;
  let configuredResetMethod;
  let configuredUpdateMethod;
  function setIdentifierGenerationMethod(method) {
    configuredGenerationMethod = method;
  }
  function setIdentifierUpdateMethod(method) {
    configuredUpdateMethod = method;
  }
  function setIdentifierForgetMethod(method) {
    configuredForgetMethod = method;
  }
  function setIdentifierResetMethod(method) {
    configuredResetMethod = method;
  }
  function defaultGenerationMethod(data, bucket) {
    if ('lid' in data && isNonEmptyString(data.lid)) {
      return data.lid;
    }
    if ('id' in data) {
      let {
        type,
        id
      } = data;
      // TODO: add test for id not a string
      if (isNonEmptyString(coerceId(id))) {
        return `@ember-data:lid-${normalizeModelName(type)}-${id}`;
      }
    }
    return uuidv4();
  }
  function defaultEmptyCallback() {}

  /**
   * Each instance of {Store} receives a unique instance of a IdentifierCache.
   *
   * This cache is responsible for assigning or retrieving the unique identify
   * for arbitrary resource data encountered by the store. Data representing
   * a unique resource or record should always be represented by the same
   * identifier.
   *
   * It can be configured by consuming applications.
   *
   * @class IdentifierCache
     @public
   */
  class IdentifierCache {
    constructor() {
      // Typescript still leaks private properties in the final
      // compiled class, so we may want to move these from _underscore
      // to a WeakMap to avoid leaking
      // currently we leak this for test purposes
      this._cache = {
        lids: Object.create(null),
        types: Object.create(null)
      };
      this._generate = void 0;
      this._update = void 0;
      this._forget = void 0;
      this._reset = void 0;
      this._merge = void 0;
      // we cache the user configuredGenerationMethod at init because it must
      // be configured prior and is not allowed to be changed
      this._generate = configuredGenerationMethod || defaultGenerationMethod;
      this._update = configuredUpdateMethod || defaultEmptyCallback;
      this._forget = configuredForgetMethod || defaultEmptyCallback;
      this._reset = configuredResetMethod || defaultEmptyCallback;
      this._merge = defaultEmptyCallback;
    }

    /**
     * Internal hook to allow management of merge conflicts with identifiers.
     *
     * we allow late binding of this private internal merge so that `internalModelFactory`
     * can insert itself here to handle elimination of duplicates
     *
     * @method __configureMerge
     * @private
     */
    __configureMerge(method) {
      this._merge = method || defaultEmptyCallback;
    }

    /**
     * @method _getRecordIdentifier
     * @private
     */

    _getRecordIdentifier(resource, shouldGenerate) {
      if (shouldGenerate === void 0) {
        shouldGenerate = false;
      }
      // short circuit if we're already the stable version
      if (isStableIdentifier(resource)) {
        return resource;
      }
      let lid = coerceId(resource.lid);
      let identifier = lid !== null ? this._cache.lids[lid] : undefined;
      if (identifier !== undefined) {
        return identifier;
      }
      if (shouldGenerate === false) {
        if (!('type' in resource) || !('id' in resource) || !resource.type || !resource.id) {
          return;
        }
      }
      let type = resource.type && normalizeModelName(resource.type);
      let id = coerceId(resource.id);
      let keyOptions = getTypeIndex(this._cache.types, type);

      // go straight for the stable RecordIdentifier key'd to `lid`
      if (lid !== null) {
        identifier = keyOptions.lid[lid];
      }

      // we may have not seen this resource before
      // but just in case we check our own secondary lookup (`id`)
      if (identifier === undefined && id !== null) {
        identifier = keyOptions.id[id];
      }
      if (identifier === undefined) {
        // we have definitely not seen this resource before
        // so we allow the user configured `GenerationMethod` to tell us
        let newLid = this._generate(resource, 'record');

        // we do this _even_ when `lid` is present because secondary lookups
        // may need to be populated, but we enforce not giving us something
        // different than expected
        if (lid !== null && newLid !== lid) {
          throw new Error(`You should not change the <lid> of a RecordIdentifier`);
        } else if (lid === null) {
          // allow configuration to tell us that we have
          // seen this `lid` before. E.g. a secondary lookup
          // connects this resource to a previously seen
          // resource.
          identifier = keyOptions.lid[newLid];
        }
        if (shouldGenerate === true) {
          if (identifier === undefined) {
            // if we still don't have an identifier, time to generate one
            identifier = makeStableRecordIdentifier(id, type, newLid);
            this._cache.lids[identifier.lid] = identifier;

            // populate our primary lookup table
            // TODO consider having the `lid` cache be
            // one level up
            keyOptions.lid[identifier.lid] = identifier;
            // TODO exists temporarily to support `peekAll`
            // but likely to move
            keyOptions._allIdentifiers.push(identifier);
          }

          // populate our own secondary lookup table
          // even for the "successful" secondary lookup
          // by `_generate()`, since we missed the cache
          // previously
          // we use identifier.id instead of id here
          // because they may not match and we prefer
          // what we've set via resource data
          if (identifier.id !== null) {
            keyOptions.id[identifier.id] = identifier;

            // TODO allow filling out of `id` here
            // for the `username` non-client created
            // case.
          }
        }
      }
      return identifier;
    }

    /**
     * allows us to peek without generating when needed
     * useful for the "create" case when we need to see if
     * we are accidentally overwritting something
     *
     * @method peekRecordIdentifier
     * @param resource
     * @returns {StableRecordIdentifier | undefined}
     * @private
     */
    peekRecordIdentifier(resource) {
      return this._getRecordIdentifier(resource, false);
    }

    /**
      Returns the Identifier for the given Resource, creates one if it does not yet exist.
       Specifically this means that we:
       - validate the `id` `type` and `lid` combo against known identifiers
      - return an object with an `lid` that is stable (repeated calls with the same
        `id` + `type` or `lid` will return the same `lid` value)
      - this referential stability of the object itself is guaranteed
       @method getOrCreateRecordIdentifier
      @param resource
      @returns {StableRecordIdentifier}
      @public
    */
    getOrCreateRecordIdentifier(resource) {
      return this._getRecordIdentifier(resource, true);
    }

    /**
     Returns a new Identifier for the supplied data. Call this method to generate
     an identifier when a new resource is being created local to the client and
     potentially does not have an `id`.
      Delegates generation to the user supplied `GenerateMethod` if one has been provided
     with the signature `generateMethod({ type }, 'record')`.
      @method createIdentifierForNewRecord
     @param data
     @returns {StableRecordIdentifier}
     @public
    */
    createIdentifierForNewRecord(data) {
      let newLid = this._generate(data, 'record');
      let identifier = makeStableRecordIdentifier(data.id || null, data.type, newLid);
      let keyOptions = getTypeIndex(this._cache.types, data.type);
      this._cache.lids[identifier.lid] = identifier;

      // populate the type+lid cache
      keyOptions.lid[newLid] = identifier;
      // ensure a peekAll sees our new identifier too
      // TODO move this outta here?
      keyOptions._allIdentifiers.push(identifier);
      return identifier;
    }

    /**
     Provides the opportunity to update secondary lookup tables for existing identifiers
     Called after an identifier created with `createIdentifierForNewRecord` has been
     committed.
      Assigned `id` to an `Identifier` if `id` has not previously existed; however,
     attempting to change the `id` or calling update without providing an `id` when
     one is missing will throw an error.
       - sets `id` (if `id` was previously `null`)
      - `lid` and `type` MUST NOT be altered post creation
       If a merge occurs, it is possible the returned identifier does not match the originally
      provided identifier. In this case the abandoned identifier will go through the usual
      `forgetRecordIdentifier` codepaths.
       @method updateRecordIdentifier
      @param identifierObject
      @param data
      @returns {StableRecordIdentifier}
      @public
    */
    updateRecordIdentifier(identifierObject, data) {
      let identifier = this.getOrCreateRecordIdentifier(identifierObject);
      let newId = 'id' in data ? coerceId(data.id) : null;
      let existingIdentifier = detectMerge(this._cache.types, identifier, data, newId, this._cache.lids);
      if (!existingIdentifier) {
        // If the incoming type does not match the identifier type, we need to create an identifier for the incoming
        // data so we can merge the incoming data with the existing identifier, see #7325 and #7363
        if ('type' in data && data.type && identifier.type !== normalizeModelName(data.type)) {
          let incomingDataResource = {
            ...data
          };
          // Need to strip the lid from the incomingData in order force a new identifier creation
          delete incomingDataResource.lid;
          existingIdentifier = this.getOrCreateRecordIdentifier(incomingDataResource);
        }
      }
      if (existingIdentifier) {
        let keyOptions = getTypeIndex(this._cache.types, identifier.type);
        identifier = this._mergeRecordIdentifiers(keyOptions, identifier, existingIdentifier, data, newId);
      }
      let id = identifier.id;
      performRecordIdentifierUpdate(identifier, data, this._update);
      newId = identifier.id;

      // add to our own secondary lookup table
      if (id !== newId && newId !== null) {
        let keyOptions = getTypeIndex(this._cache.types, identifier.type);
        keyOptions.id[newId] = identifier;
        if (id !== null) {
          delete keyOptions.id[id];
        }
      }
      return identifier;
    }

    /**
     * @method _mergeRecordIdentifiers
     * @private
     */
    _mergeRecordIdentifiers(keyOptions, identifier, existingIdentifier, data, newId) {
      // delegate determining which identifier to keep to the configured MergeMethod
      let kept = this._merge(identifier, existingIdentifier, data);
      let abandoned = kept === identifier ? existingIdentifier : identifier;

      // cleanup the identifier we no longer need
      this.forgetRecordIdentifier(abandoned);

      // ensure a secondary cache entry for this id for the identifier we do keep
      keyOptions.id[newId] = kept;
      // ensure a secondary cache entry for this id for the abandoned identifier's type we do keep
      let baseKeyOptions = getTypeIndex(this._cache.types, existingIdentifier.type);
      baseKeyOptions.id[newId] = kept;

      // make sure that the `lid` on the data we are processing matches the lid we kept
      data.lid = kept.lid;
      return kept;
    }

    /**
     Provides the opportunity to eliminate an identifier from secondary lookup tables
     as well as eliminates it from ember-data's own lookup tables and book keeping.
      Useful when a record has been deleted and the deletion has been persisted and
     we do not care about the record anymore. Especially useful when an `id` of a
     deleted record might be reused later for a new record.
      @method forgetRecordIdentifier
     @param identifierObject
     @public
    */
    forgetRecordIdentifier(identifierObject) {
      let identifier = this.getOrCreateRecordIdentifier(identifierObject);
      let keyOptions = getTypeIndex(this._cache.types, identifier.type);
      if (identifier.id !== null) {
        delete keyOptions.id[identifier.id];
      }
      delete this._cache.lids[identifier.lid];
      delete keyOptions.lid[identifier.lid];
      let index = keyOptions._allIdentifiers.indexOf(identifier);
      keyOptions._allIdentifiers.splice(index, 1);
      unmarkStableIdentifier(identifierObject);
      this._forget(identifier, 'record');
    }
    destroy() {
      this._reset();
    }
  }
  function getTypeIndex(typeMap, type) {
    let typeIndex = typeMap[type];
    if (typeIndex === undefined) {
      typeIndex = {
        lid: Object.create(null),
        id: Object.create(null),
        _allIdentifiers: []
      };
      typeMap[type] = typeIndex;
    }
    return typeIndex;
  }
  function makeStableRecordIdentifier(id, type, lid, bucket, clientOriginated) {
    let recordIdentifier = {
      lid,
      id,
      type
    };
    markStableIdentifier(recordIdentifier);
    return recordIdentifier;
  }
  function performRecordIdentifierUpdate(identifier, data, updateFn) {
    {
      updateFn(identifier, data, 'record');
    }

    // upgrade the ID, this is a "one time only" ability
    // for the multiple-cache-key scenario we "could"
    // use a heuristic to guess the best id for display
    // (usually when `data.id` is available and `data.attributes` is not)
    if ('id' in data && data.id !== undefined) {
      identifier.id = coerceId(data.id);
    }
  }
  function detectMerge(typesCache, identifier, data, newId, lids) {
    const {
      id,
      type,
      lid
    } = identifier;
    if (id !== null && id !== newId && newId !== null) {
      let keyOptions = getTypeIndex(typesCache, identifier.type);
      let existingIdentifier = keyOptions.id[newId];
      return existingIdentifier !== undefined ? existingIdentifier : false;
    } else {
      let newType = 'type' in data && data.type && normalizeModelName(data.type);

      // If the ids and type are the same but lid is not the same, we should trigger a merge of the identifiers
      if (id !== null && id === newId && newType === type && data.lid && data.lid !== lid) {
        let existingIdentifier = lids[data.lid];
        return existingIdentifier !== undefined ? existingIdentifier : false;
        // If the lids are the same, and ids are the same, but types are different we should trigger a merge of the identifiers
      } else if (id !== null && id === newId && newType && newType !== type && data.lid && data.lid === lid) {
        let keyOptions = getTypeIndex(typesCache, newType);
        let existingIdentifier = keyOptions.id[id];
        return existingIdentifier !== undefined ? existingIdentifier : false;
      }
    }
    return false;
  }

  function constructResource(type, id, lid) {
    if (typeof type === 'object' && type !== null) {
      let resource = type;
      if (isStableIdentifier(resource)) {
        return resource;
      }
      if ('id' in resource) {
        resource.id = coerceId(resource.id);
      }
      return resource;
    } else {
      const trueId = coerceId(id);
      if (!isNonEmptyString(trueId)) {
        if (isNonEmptyString(lid)) {
          return {
            lid
          };
        }
        throw new Error('Expected either id or lid to be a valid string');
      }
      if (isNonEmptyString(lid)) {
        return {
          type,
          id: trueId,
          lid
        };
      }
      return {
        type,
        id: trueId
      };
    }
  }

  /**
    @module @ember-data/store
  */

  /**
    A `PromiseArray` is an object that acts like both an `Ember.Array`
    and a promise. When the promise is resolved the resulting value
    will be set to the `PromiseArray`'s `content` property. This makes
    it easy to create data bindings with the `PromiseArray` that will be
    updated when the promise resolves.

    This class should not be imported and instantiated directly.

    For more information see the [Ember.PromiseProxyMixin
    documentation](/ember/release/classes/PromiseProxyMixin).

    Example

    ```javascript
    let promiseArray = PromiseArray.create({
      promise: $.getJSON('/some/remote/data.json')
    });

    promiseArray.get('length'); // 0

    promiseArray.then(function() {
      promiseArray.get('length'); // 100
    });
    ```

    @class PromiseArray
    @public
    @extends Ember.ArrayProxy
    @uses Ember.PromiseProxyMixin
  */
  const PromiseArray = ArrayProxy__default["default"].extend(PromiseProxyMixin__default["default"], {
    meta: computed.reads('content.meta')
  });

  /**
    A `PromiseObject` is an object that acts like both an `EmberObject`
    and a promise. When the promise is resolved, then the resulting value
    will be set to the `PromiseObject`'s `content` property. This makes
    it easy to create data bindings with the `PromiseObject` that will
    be updated when the promise resolves.

    This class should not be imported and instantiated directly.

    For more information see the [Ember.PromiseProxyMixin
    documentation](/ember/release/classes/PromiseProxyMixin.html).

    Example

    ```javascript
    let promiseObject = PromiseObject.create({
      promise: $.getJSON('/some/remote/data.json')
    });

    promiseObject.get('name'); // null

    promiseObject.then(function() {
      promiseObject.get('name'); // 'Tomster'
    });
    ```

    @class PromiseObject
    @public
    @extends Ember.ObjectProxy
    @uses Ember.PromiseProxyMixin
  */
  let PromiseObject = ObjectProxy__default["default"].extend(PromiseProxyMixin__default["default"]);
  function promiseObject(promise, label) {
    return PromiseObject.create({
      promise: RSVP.Promise.resolve(promise, label)
    });
  }
  function promiseArray(promise, label) {
    return PromiseArray.create({
      promise: RSVP.Promise.resolve(promise, label)
    });
  }
  const ALLOWABLE_PROPS = ['__ec_yieldable__', '__ec_cancel__'];
  function deprecatedPromiseObject(promise) {
    const handler = {
      get(target, prop) {
        if (ALLOWABLE_PROPS.includes(prop)) {
          return target[prop];
        }

        /* global Reflect */
        return Reflect.get(...arguments).bind(target);
      }
    };

    /* global Proxy */
    return new Proxy(promise, handler);
  }

  /**
    @module @ember-data/store
  */
  /**
   * Get the materialized model from the internalModel/promise
   * that returns an internal model and return it in a promiseObject.
   *
   * Useful for returning from find methods
   *
   * @internal
   */
  function promiseRecord(internalModelPromise, label) {
    let toReturn = internalModelPromise.then(internalModel => internalModel.getRecord());
    return promiseObject(toReturn, label);
  }

  // TODO: expose Ember._Backburner as `import { _Backburner } from '@ember/runloop'` in ember-rfc176-data + emberjs/ember.js
  /*
    syncRelationships is used by the UI to grab updates from the graph
    and update the ManyArrays.

    We may be able to remove this once the new relationship layer is
    complete.
  */
  const backburner = new Ember__default["default"]._Backburner(['coalesce', 'sync', 'notify']);

  /**
    @module @ember-data/adapter/error
  */

  const SOURCE_POINTER_REGEXP = /^\/?data\/(attributes|relationships)\/(.*)/;
  const SOURCE_POINTER_PRIMARY_REGEXP = /^\/?data/;
  const PRIMARY_ATTRIBUTE_KEY = 'base';

  /**
    Convert an hash of errors into an array with errors in JSON-API format.
     ```javascript
    import DS from 'ember-data';

     const { errorsHashToArray } = DS;
     
     let errors = {
      base: 'Invalid attributes on saving this record',
      name: 'Must be present',
      age: ['Must be present', 'Must be a number']
    };
     let errorsArray = errorsHashToArray(errors);
    // [
    //   {
    //     title: "Invalid Document",
    //     detail: "Invalid attributes on saving this record",
    //     source: { pointer: "/data" }
    //   },
    //   {
    //     title: "Invalid Attribute",
    //     detail: "Must be present",
    //     source: { pointer: "/data/attributes/name" }
    //   },
    //   {
    //     title: "Invalid Attribute",
    //     detail: "Must be present",
    //     source: { pointer: "/data/attributes/age" }
    //   },
    //   {
    //     title: "Invalid Attribute",
    //     detail: "Must be a number",
    //     source: { pointer: "/data/attributes/age" }
    //   }
    // ]
    ```
    @method errorsHashToArray
    @for @ember-data/adapter/error
    @static
    @public
    @param {Object} errors hash with errors as properties
    @return {Array} array of errors in JSON-API format
  */
  function errorsHashToArray(errors) {
    let out = [];
    if (utils.isPresent(errors)) {
      Object.keys(errors).forEach(key => {
        let messages = EmberArray.makeArray(errors[key]);
        for (let i = 0; i < messages.length; i++) {
          let title = 'Invalid Attribute';
          let pointer = `/data/attributes/${key}`;
          if (key === PRIMARY_ATTRIBUTE_KEY) {
            title = 'Invalid Document';
            pointer = `/data`;
          }
          out.push({
            title: title,
            detail: messages[i],
            source: {
              pointer: pointer
            }
          });
        }
      });
    }
    return out;
  }

  /**
    Convert an array of errors in JSON-API format into an object.

    ```javascript
    import DS from 'ember-data';

    const { errorsArrayToHash } = DS;

    let errorsArray = [
      {
        title: 'Invalid Attribute',
        detail: 'Must be present',
        source: { pointer: '/data/attributes/name' }
      },
      {
        title: 'Invalid Attribute',
        detail: 'Must be present',
        source: { pointer: '/data/attributes/age' }
      },
      {
        title: 'Invalid Attribute',
        detail: 'Must be a number',
        source: { pointer: '/data/attributes/age' }
      }
    ];

    let errors = errorsArrayToHash(errorsArray);
    // {
    //   "name": ["Must be present"],
    //   "age":  ["Must be present", "must be a number"]
    // }
    ```

    @method errorsArrayToHash
    @static
    @for @ember-data/adapter/error
    @public
    @param {Array} errors array of errors in JSON-API format
    @return {Object}
  */
  function errorsArrayToHash(errors) {
    let out = {};
    if (utils.isPresent(errors)) {
      errors.forEach(error => {
        if (error.source && error.source.pointer) {
          let key = error.source.pointer.match(SOURCE_POINTER_REGEXP);
          if (key) {
            key = key[2];
          } else if (error.source.pointer.search(SOURCE_POINTER_PRIMARY_REGEXP) !== -1) {
            key = PRIMARY_ATTRIBUTE_KEY;
          }
          if (key) {
            out[key] = out[key] || [];
            out[key].push(error.detail || error.title);
          }
        }
      });
    }
    return out;
  }

  let RequestStateEnum = /*#__PURE__*/function (RequestStateEnum) {
    RequestStateEnum["pending"] = "pending";
    RequestStateEnum["fulfilled"] = "fulfilled";
    RequestStateEnum["rejected"] = "rejected";
    return RequestStateEnum;
  }({});

  const Touching = Symbol('touching');
  const RequestPromise = Symbol('promise');
  function hasRecordIdentifier(op) {
    return 'recordIdentifier' in op;
  }
  class RequestCache {
    constructor() {
      this._pending = Object.create(null);
      this._done = Object.create(null);
      this._subscriptions = Object.create(null);
    }
    enqueue(promise, queryRequest) {
      let query = queryRequest.data[0];
      if (hasRecordIdentifier(query)) {
        let lid = query.recordIdentifier.lid;
        let type = query.op === 'saveRecord' ? 'mutation' : 'query';
        if (!this._pending[lid]) {
          this._pending[lid] = [];
        }
        let request = {
          state: RequestStateEnum.pending,
          request: queryRequest,
          type
        };
        request[Touching] = [query.recordIdentifier];
        request[RequestPromise] = promise;
        this._pending[lid].push(request);
        this._triggerSubscriptions(request);
        promise.then(result => {
          this._dequeue(lid, request);
          let finalizedRequest = {
            state: RequestStateEnum.fulfilled,
            request: queryRequest,
            type,
            response: {
              data: result
            }
          };
          finalizedRequest[Touching] = request[Touching];
          this._addDone(finalizedRequest);
          this._triggerSubscriptions(finalizedRequest);
        }, error => {
          this._dequeue(lid, request);
          let finalizedRequest = {
            state: RequestStateEnum.rejected,
            request: queryRequest,
            type,
            response: {
              data: error && error.error
            }
          };
          finalizedRequest[Touching] = request[Touching];
          this._addDone(finalizedRequest);
          this._triggerSubscriptions(finalizedRequest);
        });
      }
    }
    _triggerSubscriptions(req) {
      req[Touching].forEach(identifier => {
        if (this._subscriptions[identifier.lid]) {
          this._subscriptions[identifier.lid].forEach(callback => callback(req));
        }
      });
    }
    _dequeue(lid, request) {
      this._pending[lid] = this._pending[lid].filter(req => req !== request);
    }
    _addDone(request) {
      request[Touching].forEach(identifier => {
        if (!this._done[identifier.lid]) {
          this._done[identifier.lid] = [];
        }
        // TODO add support for multiple
        let requestDataOp = request.request.data[0].op;
        this._done[identifier.lid] = this._done[identifier.lid].filter(req => {
          // TODO add support for multiple
          let data;
          if (req.request.data instanceof Array) {
            data = req.request.data[0];
          } else {
            data = req.request.data;
          }
          return data.op !== requestDataOp;
        });
        this._done[identifier.lid].push(request);
      });
    }
    subscribeForRecord(identifier, callback) {
      if (!this._subscriptions[identifier.lid]) {
        this._subscriptions[identifier.lid] = [];
      }
      this._subscriptions[identifier.lid].push(callback);
    }
    getPendingRequestsForRecord(identifier) {
      if (this._pending[identifier.lid]) {
        return this._pending[identifier.lid];
      }
      return [];
    }
    getLastRequestForRecord(identifier) {
      let requests = this._done[identifier.lid];
      if (requests) {
        return requests[requests.length - 1];
      }
      return null;
    }
  }

  /*
   * Returns the RecordData instance associated with a given
   * Model or InternalModel.
   *
   * Intentionally "loose" to allow anything with an _internalModel
   * property until InternalModel is eliminated.
   *
   * Intentionally not typed to `InternalModel` due to circular dependency
   *  which that creates.
   *
   * Overtime, this should shift to a "weakmap" based lookup in the
   *  "Ember.getOwner(obj)" style.
   */

  const RecordDataForIdentifierCache = new WeakCache$1('');
  function setRecordDataFor(identifier, recordData) {
    RecordDataForIdentifierCache.set(identifier, recordData);
  }
  function removeRecordDataFor(identifier) {
    RecordDataForIdentifierCache.delete(identifier);
  }
  function recordDataFor(instance) {
    if (RecordDataForIdentifierCache.has(instance)) {
      return RecordDataForIdentifierCache.get(instance);
    }
    let internalModel = instance._internalModel || instance.internalModel || instance;
    return internalModel._recordData || null;
  }

  function schemaIsDSModel(schema) {
    return schema.isModel === true;
  }
  /**
    Snapshot is not directly instantiable.
    Instances are provided to a consuming application's
    adapters and serializers for certain requests.

    @class Snapshot
    @public
  */
  class Snapshot {
    /**
     * @method constructor
     * @constructor
     * @private
     * @param options
     * @param identifier
     * @param _store
     */
    constructor(options, identifier, _store) {
      this.__attributes = null;
      this._belongsToRelationships = Object.create(null);
      this._belongsToIds = Object.create(null);
      this._hasManyRelationships = Object.create(null);
      this._hasManyIds = Object.create(null);
      this._store = _store;
      let internalModel = this._internalModel = _store._internalModelForResource(identifier);
      this.modelName = identifier.type;

      /**
        The unique RecordIdentifier associated with this Snapshot.
         @property identifier
        @public
        @type {StableRecordIdentifier}
      */
      this.identifier = identifier;

      /*
        If the internalModel does not yet have a record, then we are
        likely a snapshot being provided to a find request, so we
        populate __attributes lazily. Else, to preserve the "moment
        in time" in which a snapshot is created, we greedily grab
        the values.
       */
      if (internalModel.hasRecord) {
        this._attributes;
      }

      /**
       The id of the snapshot's underlying record
        Example
        ```javascript
       // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
       postSnapshot.id; // => '1'
       ```
        @property id
       @type {String}
       @public
       */
      this.id = identifier.id;

      /**
       A hash of adapter options
       @property adapterOptions
       @type {Object}
       @public
       */
      this.adapterOptions = options.adapterOptions;

      /**
       If `include` was passed to the options hash for the request, the value
       would be available here.
        @property include
       @type {String|Array}
       @public
       */
      this.include = options.include;

      /**
       The name of the type of the underlying record for this snapshot, as a string.
        @property modelName
       @type {String}
       @public
       */
      this.modelName = internalModel.modelName;
      if (internalModel.hasRecord) {
        this._changedAttributes = recordDataFor(internalModel).changedAttributes();
      }
    }

    /**
     The underlying record for this snapshot. Can be used to access methods and
     properties defined on the record.
      Example
      ```javascript
     let json = snapshot.record.toJSON();
     ```
      @property record
     @type {Model}
     @public
     */
    get record() {
      return this._internalModel.getRecord();
    }
    get _attributes() {
      if (this.__attributes !== null) {
        return this.__attributes;
      }
      let record = this.record;
      let attributes = this.__attributes = Object.create(null);
      let attrs = Object.keys(this._store._attributesDefinitionFor(this.identifier));
      attrs.forEach(keyName => {
        if (schemaIsDSModel(this.type)) {
          // if the schema is for a DSModel then the instance is too
          attributes[keyName] = object.get(record, keyName);
        } else {
          attributes[keyName] = recordDataFor(this._internalModel).getAttr(keyName);
        }
      });
      return attributes;
    }

    /**
     The type of the underlying record for this snapshot, as a Model.
      @property type
      @public
     @type {Model}
     */
    get type() {
      return this._internalModel.modelClass;
    }
    get isNew() {
      return this._internalModel.isNew();
    }

    /**
     Returns the value of an attribute.
      Example
      ```javascript
     // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
     postSnapshot.attr('author'); // => 'Tomster'
     postSnapshot.attr('title'); // => 'Ember.js rocks'
     ```
      Note: Values are loaded eagerly and cached when the snapshot is created.
      @method attr
     @param {String} keyName
     @return {Object} The attribute value or undefined
     @public
     */
    attr(keyName) {
      if (keyName in this._attributes) {
        return this._attributes[keyName];
      }
    }

    /**
     Returns all attributes and their corresponding values.
      Example
      ```javascript
     // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
     postSnapshot.attributes(); // => { author: 'Tomster', title: 'Ember.js rocks' }
     ```
      @method attributes
     @return {Object} All attributes of the current snapshot
     @public
     */
    attributes() {
      return {
        ...this._attributes
      };
    }

    /**
     Returns all changed attributes and their old and new values.
      Example
      ```javascript
     // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
     postModel.set('title', 'Ember.js rocks!');
     postSnapshot.changedAttributes(); // => { title: ['Ember.js rocks', 'Ember.js rocks!'] }
     ```
      @method changedAttributes
     @return {Object} All changed attributes of the current snapshot
     @public
     */
    changedAttributes() {
      let changedAttributes = Object.create(null);
      if (!this._changedAttributes) {
        return changedAttributes;
      }
      let changedAttributeKeys = Object.keys(this._changedAttributes);
      for (let i = 0, length = changedAttributeKeys.length; i < length; i++) {
        let key = changedAttributeKeys[i];
        changedAttributes[key] = this._changedAttributes[key].slice();
      }
      return changedAttributes;
    }

    /**
     Returns the current value of a belongsTo relationship.
      `belongsTo` takes an optional hash of options as a second parameter,
     currently supported options are:
      - `id`: set to `true` if you only want the ID of the related record to be
     returned.
      Example
      ```javascript
     // store.push('post', { id: 1, title: 'Hello World' });
     // store.createRecord('comment', { body: 'Lorem ipsum', post: post });
     commentSnapshot.belongsTo('post'); // => Snapshot
     commentSnapshot.belongsTo('post', { id: true }); // => '1'
      // store.push('comment', { id: 1, body: 'Lorem ipsum' });
     commentSnapshot.belongsTo('post'); // => undefined
     ```
      Calling `belongsTo` will return a new Snapshot as long as there's any known
     data for the relationship available, such as an ID. If the relationship is
     known but unset, `belongsTo` will return `null`. If the contents of the
     relationship is unknown `belongsTo` will return `undefined`.
      Note: Relationships are loaded lazily and cached upon first access.
      @method belongsTo
     @param {String} keyName
     @param {Object} [options]
     @public
     @return {(Snapshot|String|null|undefined)} A snapshot or ID of a known
     relationship or null if the relationship is known but unset. undefined
     will be returned if the contents of the relationship is unknown.
     */
    belongsTo(keyName, options) {
      let returnModeIsId = !!(options && options.id);
      let inverseInternalModel;
      let result;
      let store = this._internalModel.store;
      if (returnModeIsId === true && keyName in this._belongsToIds) {
        return this._belongsToIds[keyName];
      }
      if (returnModeIsId === false && keyName in this._belongsToRelationships) {
        return this._belongsToRelationships[keyName];
      }
      store._relationshipMetaFor(this.modelName, null, keyName);
      const graphFor = require('@ember-data/record-data/-private').graphFor;
      const {
        identifier
      } = this;
      const relationship = graphFor(this._store._storeWrapper).get(identifier, keyName);
      let value = relationship.getData();
      let data = value && value.data;
      inverseInternalModel = data ? store._internalModelForResource(data) : null;
      if (value && value.data !== undefined) {
        if (inverseInternalModel && !inverseInternalModel.isDeleted()) {
          if (returnModeIsId) {
            result = inverseInternalModel.id;
          } else {
            result = inverseInternalModel.createSnapshot();
          }
        } else {
          result = null;
        }
      }
      if (returnModeIsId) {
        this._belongsToIds[keyName] = result;
      } else {
        this._belongsToRelationships[keyName] = result;
      }
      return result;
    }

    /**
     Returns the current value of a hasMany relationship.
      `hasMany` takes an optional hash of options as a second parameter,
     currently supported options are:
      - `ids`: set to `true` if you only want the IDs of the related records to be
     returned.
      Example
      ```javascript
     // store.push('post', { id: 1, title: 'Hello World', comments: [2, 3] });
     postSnapshot.hasMany('comments'); // => [Snapshot, Snapshot]
     postSnapshot.hasMany('comments', { ids: true }); // => ['2', '3']
      // store.push('post', { id: 1, title: 'Hello World' });
     postSnapshot.hasMany('comments'); // => undefined
     ```
      Note: Relationships are loaded lazily and cached upon first access.
      @method hasMany
     @param {String} keyName
     @param {Object} [options]
     @public
     @return {(Array|undefined)} An array of snapshots or IDs of a known
     relationship or an empty array if the relationship is known but unset.
     undefined will be returned if the contents of the relationship is unknown.
     */
    hasMany(keyName, options) {
      let returnModeIsIds = !!(options && options.ids);
      let results;
      let cachedIds = this._hasManyIds[keyName];
      let cachedSnapshots = this._hasManyRelationships[keyName];
      if (returnModeIsIds === true && keyName in this._hasManyIds) {
        return cachedIds;
      }
      if (returnModeIsIds === false && keyName in this._hasManyRelationships) {
        return cachedSnapshots;
      }
      let store = this._internalModel.store;
      store._relationshipMetaFor(this.modelName, null, keyName);
      const graphFor = require('@ember-data/record-data/-private').graphFor;
      const {
        identifier
      } = this;
      const relationship = graphFor(this._store._storeWrapper).get(identifier, keyName);
      let value = relationship.getData();
      if (value.data) {
        results = [];
        value.data.forEach(member => {
          let internalModel = store._internalModelForResource(member);
          if (!internalModel.isDeleted()) {
            if (returnModeIsIds) {
              results.push(member.id || null);
            } else {
              results.push(internalModel.createSnapshot());
            }
          }
        });
      }

      // we assign even if `undefined` so that we don't reprocess the relationship
      // on next access. This works with the `keyName in` checks above.
      if (returnModeIsIds) {
        this._hasManyIds[keyName] = results;
      } else {
        this._hasManyRelationships[keyName] = results;
      }
      return results;
    }

    /**
      Iterates through all the attributes of the model, calling the passed
      function on each attribute.
       Example
       ```javascript
      snapshot.eachAttribute(function(name, meta) {
        // ...
      });
      ```
       @method eachAttribute
      @param {Function} callback the callback to execute
      @param {Object} [binding] the value to which the callback's `this` should be bound
      @public
    */
    eachAttribute(callback, binding) {
      let attrDefs = this._store._attributesDefinitionFor(this.identifier);
      Object.keys(attrDefs).forEach(key => {
        callback.call(binding, key, attrDefs[key]);
      });
    }

    /**
      Iterates through all the relationships of the model, calling the passed
      function on each relationship.
       Example
       ```javascript
      snapshot.eachRelationship(function(name, relationship) {
        // ...
      });
      ```
       @method eachRelationship
      @param {Function} callback the callback to execute
      @param {Object} [binding] the value to which the callback's `this` should be bound
      @public
    */
    eachRelationship(callback, binding) {
      let relationshipDefs = this._store._relationshipsDefinitionFor(this.identifier);
      Object.keys(relationshipDefs).forEach(key => {
        callback.call(binding, key, relationshipDefs[key]);
      });
    }

    /**
      Serializes the snapshot using the serializer for the model.
       Example
       ```app/adapters/application.js
      import Adapter from '@ember-data/adapter';
       export default Adapter.extend({
        createRecord(store, type, snapshot) {
          let data = snapshot.serialize({ includeId: true });
          let url = `/${type.modelName}`;
           return fetch(url, {
            method: 'POST',
            body: data,
          }).then((response) => response.json())
        }
      });
      ```
       @method serialize
      @param {Object} options
      @return {Object} an object whose values are primitive JSON values only
      @public
     */
    serialize(options) {
      return this._store.serializerFor(this.modelName).serialize(this, options);
    }
  }

  /**
    @module @ember-data/store
  */

  function _bind(fn) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return function () {
      return fn.apply(undefined, args);
    };
  }
  function _guard(promise, test) {
    let guarded = promise.finally(() => {
      if (!test()) {
        guarded._subscribers.length = 0;
      }
    });
    return guarded;
  }
  function _objectIsAlive(object$1) {
    return !(object.get(object$1, 'isDestroyed') || object.get(object$1, 'isDestroying'));
  }
  function guardDestroyedStore(promise, store, label) {
    let wrapperPromise = RSVP.resolve(promise, label).then(v => promise);
    return _guard(wrapperPromise, () => {
      return _objectIsAlive(store);
    });
  }

  function normalizeResponseHelper(serializer, store, modelClass, payload, id, requestType) {
    let normalizedResponse = serializer.normalizeResponse(store, modelClass, payload, id, requestType);
    return normalizedResponse;
  }

  const SaveOp = Symbol('SaveOp');
  /**
   * Manages the state of network requests initiated by the store
   *
   * @class FetchManager
   * @private
   */
  class FetchManager {
    // saves which are pending in the runloop

    // fetches pending in the runloop, waiting to be coalesced

    constructor(_store) {
      this._store = _store;
      // used to keep track of all the find requests that need to be coalesced
      this._pendingFetch = new Map();
      this._pendingSave = [];
      this.requestCache = new RequestCache();
      this.isDestroyed = false;
    }

    /**
      This method is called by `record.save`, and gets passed a
      resolver for the promise that `record.save` returns.
       It schedules saving to happen at the end of the run loop.
       @internal
    */
    scheduleSave(identifier, options) {
      if (options === void 0) {
        options = {};
      }
      let promiseLabel = 'DS: Model#save ' + this;
      let resolver = RSVP__default["default"].defer(promiseLabel);
      let query = {
        op: 'saveRecord',
        recordIdentifier: identifier,
        options
      };
      let queryRequest = {
        data: [query]
      };
      let snapshot = new Snapshot(options, identifier, this._store);
      let pendingSaveItem = {
        snapshot: snapshot,
        resolver: resolver,
        identifier,
        options,
        queryRequest
      };
      this._pendingSave.push(pendingSaveItem);
      runloop._backburner.scheduleOnce('actions', this, this._flushPendingSaves);
      this.requestCache.enqueue(resolver.promise, pendingSaveItem.queryRequest);
      return resolver.promise;
    }
    _flushPendingSave(pending) {
      let {
        snapshot,
        resolver,
        identifier,
        options
      } = pending;
      let adapter = this._store.adapterFor(identifier.type);
      let operation = options[SaveOp];

      // TODO We have to cast due to our reliance on this private property
      // this will be refactored away once we change our pending API to be identifier based
      let internalModel = snapshot._internalModel;
      let modelName = snapshot.modelName;
      let store = this._store;
      let modelClass = store.modelFor(modelName);
      let promise = RSVP.Promise.resolve().then(() => adapter[operation](store, modelClass, snapshot));
      let serializer = store.serializerFor(modelName);
      let label = `DS: Extract and notify about ${operation} completion of ${internalModel}`;
      promise = guardDestroyedStore(promise, store, label);
      promise = _guard(promise, _bind(_objectIsAlive, internalModel));
      promise = promise.then(adapterPayload => {
        if (adapterPayload) {
          return normalizeResponseHelper(serializer, store, modelClass, adapterPayload, snapshot.id, operation);
        }
      }, function (error) {
        if (error && error.isAdapterError === true && error.code === 'InvalidError') {
          let parsedErrors = error.errors;
          if (typeof serializer.extractErrors === 'function') {
            parsedErrors = serializer.extractErrors(store, modelClass, error, snapshot.id);
          } else {
            parsedErrors = errorsArrayToHash(error.errors);
          }
          throw {
            error,
            parsedErrors
          };
        } else {
          throw {
            error
          };
        }
      }, label);
      resolver.resolve(promise);
    }

    /**
      This method is called at the end of the run loop, and
      flushes any records passed into `scheduleSave`
       @method flushPendingSave
      @internal
    */
    _flushPendingSaves() {
      let pending = this._pendingSave.slice();
      this._pendingSave = [];
      for (let i = 0, j = pending.length; i < j; i++) {
        let pendingItem = pending[i];
        this._flushPendingSave(pendingItem);
      }
    }
    scheduleFetch(identifier, options, shouldTrace) {
      // TODO Probably the store should pass in the query object

      let query = {
        op: 'findRecord',
        recordIdentifier: identifier,
        options
      };
      let queryRequest = {
        data: [query]
      };
      let pendingFetches = this._pendingFetch.get(identifier.type);

      // We already have a pending fetch for this
      if (pendingFetches) {
        let matchingPendingFetch = pendingFetches.filter(fetch => fetch.identifier.id === identifier.id)[0];
        if (matchingPendingFetch) {
          return matchingPendingFetch.resolver.promise;
        }
      }
      let id = identifier.id;
      let modelName = identifier.type;
      let resolver = RSVP__default["default"].defer(`Fetching ${modelName}' with id: ${id}`);
      let pendingFetchItem = {
        identifier,
        resolver,
        options,
        queryRequest
      };
      let promise = resolver.promise;
      if (this._pendingFetch.size === 0) {
        runloop._backburner.schedule('actions', this, this.flushAllPendingFetches);
      }
      let fetches = this._pendingFetch;
      if (!fetches.has(modelName)) {
        fetches.set(modelName, []);
      }
      fetches.get(modelName).push(pendingFetchItem);
      this.requestCache.enqueue(promise, pendingFetchItem.queryRequest);
      return promise;
    }
    _fetchRecord(fetchItem) {
      let identifier = fetchItem.identifier;
      let modelName = identifier.type;
      let adapter = this._store.adapterFor(modelName);
      let snapshot = new Snapshot(fetchItem.options, identifier, this._store);
      let klass = this._store.modelFor(identifier.type);
      let promise = RSVP.Promise.resolve().then(() => {
        return adapter.findRecord(this._store, klass, identifier.id, snapshot);
      });
      let id = identifier.id;
      let label = `DS: Handle Adapter#findRecord of '${modelName}' with id: '${id}'`;
      promise = guardDestroyedStore(promise, this._store, label);
      promise = promise.then(adapterPayload => {
        let serializer = this._store.serializerFor(modelName);
        let payload = normalizeResponseHelper(serializer, this._store, klass, adapterPayload, id, 'findRecord');
        return payload;
      }, error => {
        throw error;
      }, `DS: Extract payload of '${modelName}'`);
      fetchItem.resolver.resolve(promise);
    }

    // TODO should probably refactor expectedSnapshots to be identifiers
    handleFoundRecords(seeking, coalescedPayload, expectedSnapshots) {
      // resolve found records
      let found = Object.create(null);
      let payloads = coalescedPayload.data;
      let coalescedIncluded = coalescedPayload.included || [];
      for (let i = 0, l = payloads.length; i < l; i++) {
        let payload = payloads[i];
        let pair = seeking[payload.id];
        found[payload.id] = payload;
        let included = coalescedIncluded.concat(payloads);

        // TODO remove original data from included
        if (pair) {
          let resolver = pair.resolver;
          resolver.resolve({
            data: payload,
            included
          });
        }
      }

      // reject missing records

      // TODO NOW clean this up to refer to payloads
      let missingSnapshots = [];
      for (let i = 0, l = expectedSnapshots.length; i < l; i++) {
        let snapshot = expectedSnapshots[i];
        assertIsString(snapshot.id);

        // We know id is a string because you can't fetch
        // without one.
        if (!found[snapshot.id]) {
          missingSnapshots.push(snapshot);
        }
      }
      if (missingSnapshots.length) {
        this.rejectFetchedItems(seeking, missingSnapshots);
      }
    }
    rejectFetchedItems(seeking, snapshots, error) {
      for (let i = 0, l = snapshots.length; i < l; i++) {
        let snapshot = snapshots[i];
        assertIsString(snapshot.id);
        // TODO refactor to identifier.lid to avoid this cast to string
        //  we can do this case because you can only fetch an identifier
        //  that has an ID
        let pair = seeking[snapshot.id];
        if (pair) {
          pair.resolver.reject(error || new Error(`Expected: '<${snapshot.modelName}:${snapshot.id}>' to be present in the adapter provided payload, but it was not found.`));
        }
      }
    }
    _findMany(adapter, store, modelName, snapshots, identifiers, optionsMap) {
      let modelClass = store.modelFor(modelName); // `adapter.findMany` gets the modelClass still
      let ids = snapshots.map(s => s.id);
      let promise = adapter.findMany(store, modelClass, ids, EmberArray.A(snapshots));
      let label = `DS: Handle Adapter#findMany of '${modelName}'`;
      if (promise === undefined) {
        throw new Error('adapter.findMany returned undefined, this was very likely a mistake');
      }
      promise = guardDestroyedStore(promise, store, label);
      return promise.then(adapterPayload => {
        let serializer = store.serializerFor(modelName);
        let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findMany');
        return payload;
      }, null, `DS: Extract payload of ${modelName}`);
    }
    _processCoalescedGroup(seeking, group, adapter, optionsMap, modelName) {
      //TODO check what happened with identifiers here
      let totalInGroup = group.length;
      let ids = new Array(totalInGroup);
      let groupedSnapshots = new Array(totalInGroup);
      for (let j = 0; j < totalInGroup; j++) {
        groupedSnapshots[j] = group[j];
        ids[j] = groupedSnapshots[j].id;
      }
      let store = this._store;
      if (totalInGroup > 1) {
        this._findMany(adapter, store, modelName, group, groupedSnapshots, optionsMap).then(payloads => {
          this.handleFoundRecords(seeking, payloads, groupedSnapshots);
        }).catch(error => {
          this.rejectFetchedItems(seeking, groupedSnapshots, error);
        });
      } else if (ids.length === 1) {
        let pair = seeking[groupedSnapshots[0].id];
        this._fetchRecord(pair);
      } else ;
    }
    _flushPendingFetchForType(pendingFetchItems, modelName) {
      let adapter = this._store.adapterFor(modelName);
      let shouldCoalesce = !!adapter.findMany && adapter.coalesceFindRequests;
      let totalItems = pendingFetchItems.length;
      let identifiers = new Array(totalItems);
      let seeking = Object.create(null);
      let optionsMap = new WeakCache$1('');
      for (let i = 0; i < totalItems; i++) {
        let pendingItem = pendingFetchItems[i];
        let identifier = pendingItem.identifier;
        identifiers[i] = identifier;
        optionsMap.set(identifier, pendingItem.options);
        seeking[identifier.id] = pendingItem;
      }
      if (shouldCoalesce) {
        // TODO: Improve records => snapshots => records => snapshots
        //
        // We want to provide records to all store methods and snapshots to all
        // adapter methods. To make sure we're doing that we're providing an array
        // of snapshots to adapter.groupRecordsForFindMany(), which in turn will
        // return grouped snapshots instead of grouped records.
        //
        // But since the _findMany() finder is a store method we need to get the
        // records from the grouped snapshots even though the _findMany() finder
        // will once again convert the records to snapshots for adapter.findMany()
        let snapshots = new Array(totalItems);
        for (let i = 0; i < totalItems; i++) {
          // we know options is in the map due to having just set it above
          // but TS doesn't know so we cast it
          let options = optionsMap.get(identifiers[i]);
          snapshots[i] = new Snapshot(options, identifiers[i], this._store);
        }
        let groups;
        if (adapter.groupRecordsForFindMany) {
          groups = adapter.groupRecordsForFindMany(this, snapshots);
        } else {
          groups = [snapshots];
        }
        for (let i = 0, l = groups.length; i < l; i++) {
          this._processCoalescedGroup(seeking, groups[i], adapter, optionsMap, modelName);
        }
      } else {
        for (let i = 0; i < totalItems; i++) {
          this._fetchRecord(pendingFetchItems[i]);
        }
      }
    }
    getPendingFetch(identifier, options) {
      let pendingRequests = this.requestCache.getPendingRequestsForRecord(identifier).filter(req => {
        return req.type === 'query' && isSameRequest(options, req.request.data[0].options);
      });
      if (pendingRequests.length > 0) {
        return pendingRequests[0][RequestPromise];
      }
    }
    flushAllPendingFetches() {
      if (this.isDestroyed) {
        return;
      }
      this._pendingFetch.forEach(this._flushPendingFetchForType, this);
      this._pendingFetch.clear();
    }
    destroy() {
      this.isDestroyed = true;
    }
  }
  function assertIsString(id) {
  }

  // this function helps resolve whether we have a pending request that we should use instead
  function isSameRequest(options, reqOptions) {
    if (options === void 0) {
      options = {};
    }
    if (reqOptions === void 0) {
      reqOptions = {};
    }
    return options.include === reqOptions.include;
  }

  const Cache = new WeakCache$1('');
  Cache._generator = () => new Map();
  const Tokens = new WeakCache$1('');

  // 'property' is an internal EmberData only transition period concept.

  function unsubscribe(token) {
    let identifier = Tokens.get(token);
    if (!identifier) {
      throw new Error('Passed unknown unsubscribe token to unsubscribe');
    }
    Tokens.delete(token);
    const map = Cache.get(identifier);
    map?.delete(token);
  }
  /*
    Currently only support a single callback per identifier
  */
  class NotificationManager {
    constructor(store) {
      this.store = store;
    }
    subscribe(identifier, callback) {
      let stableIdentifier = this.store.identifierCache.getOrCreateRecordIdentifier(identifier);
      let map = Cache.lookup(stableIdentifier);
      let unsubToken = {};
      map.set(unsubToken, callback);
      Tokens.set(unsubToken, stableIdentifier);
      return unsubToken;
    }
    notify(identifier, value, key) {
      let stableIdentifier = this.store.identifierCache.getOrCreateRecordIdentifier(identifier);
      let callbackMap = Cache.get(stableIdentifier);
      if (!callbackMap || !callbackMap.size) {
        return false;
      }
      callbackMap.forEach(cb => {
        cb(stableIdentifier, value, key);
      });
      return true;
    }
  }

  /**
    @module @ember-data/store
  */

  /**
   `InternalModelMap` is a custom storage map for internalModels of a given modelName
   used by `IdentityMap`.

   It was extracted from an implicit pojo based "internalModel map" and preserves
   that interface while we work towards a more official API.

   @class InternalModelMap
   @internal
   */
  class InternalModelMap {
    constructor(modelName) {
      this._idToModel = Object.create(null);
      this._models = [];
      this.modelName = modelName;
    }
    get(id) {
      return this._idToModel[id] || null;
    }
    has(id) {
      return !!this._idToModel[id];
    }
    get length() {
      return this._models.length;
    }
    get recordIdentifiers() {
      return this._models.map(m => m.identifier);
    }
    set(id, internalModel) {
      this._idToModel[id] = internalModel;
    }
    add(internalModel, id) {
      if (id) {
        this._idToModel[id] = internalModel;
      }
      this._models.push(internalModel);
    }
    remove(internalModel, id) {
      delete this._idToModel[id];
      let loc = this._models.indexOf(internalModel);
      if (loc !== -1) {
        this._models.splice(loc, 1);
      }
    }
    contains(internalModel) {
      return this._models.indexOf(internalModel) !== -1;
    }

    /**
     An array of all models of this modelName
     @property models
     @internal
     @type Array
     */
    get models() {
      return this._models;
    }

    /**
     Destroy all models in the map
      @internal
     */
    clear() {
      let internalModels = this._models;
      this._models = [];
      for (let i = 0; i < internalModels.length; i++) {
        let internalModel = internalModels[i];
        internalModel.unloadRecord();
      }
    }
  }

  /**
    @module @ember-data/store
  */

  /**
   `IdentityMap` is a custom storage map for records by modelName
   used by `Store`.

   @class IdentityMap
   @internal
   */
  class IdentityMap {
    constructor() {
      this._map = Object.create(null);
    }
    /**
     Retrieves the `InternalModelMap` for a given modelName,
     creating one if one did not already exist. This is
     similar to `getWithDefault` or `get` on a `MapWithDefault`
      @method retrieve
     @internal
     @param modelName a previously normalized modelName
     @return {InternalModelMap} the InternalModelMap for the given modelName
     */
    retrieve(modelName) {
      let map = this._map[modelName];
      if (map === undefined) {
        map = this._map[modelName] = new InternalModelMap(modelName);
      }
      return map;
    }

    /**
     Clears the contents of all known `RecordMaps`, but does
     not remove the InternalModelMap instances.
      @internal
     */
    clear() {
      let map = this._map;
      let keys = Object.keys(map);
      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        map[key].clear();
      }
    }
  }

  /**
    @module @ember-data/store
  */

  const FactoryCache = new WeakCache$1('');
  FactoryCache._generator = store => {
    return new InternalModelFactory(store);
  };
  const RecordCache = new WeakCache$1('');
  function peekRecordIdentifier(record) {
    return RecordCache.get(record);
  }

  /**
    Retrieves the unique referentially-stable [RecordIdentifier](/ember-data/release/classes/StableRecordIdentifier)
    assigned to the given record instance.

    ```js
    import { recordIdentifierFor } from "@ember-data/store";

    // ... gain access to a record, for instance with peekRecord or findRecord
    const record = store.peekRecord("user", "1");

    // get the identifier for the record (see docs for StableRecordIdentifier)
    const identifier = recordIdentifierFor(record);

    // access the identifier's properties.
    const { id, type, lid } = identifier;
    ```

    @method recordIdentifierFor
    @public
    @static
    @for @ember-data/store
    @param {Object} record a record instance previously obstained from the store.
    @returns {StableRecordIdentifier}
   */
  function recordIdentifierFor(record) {
    return RecordCache.getWithError(record);
  }
  function setRecordIdentifier(record, identifier) {

    /*
    It would be nice to do a reverse check here that an identifier has not
    previously been assigned a record; however, unload + rematerialization
    prevents us from having a great way of doing so when CustomRecordClasses
    don't necessarily give us access to a `isDestroyed` for dematerialized
    instance.
    */

    RecordCache.set(record, identifier);
  }
  function internalModelFactoryFor(store) {
    return FactoryCache.lookup(store);
  }

  /**
   * The InternalModelFactory handles the lifecyle of
   * instantiating, caching, and destroying InternalModel
   * instances.
   *
   * @class InternalModelFactory
   * @internal
   */
  class InternalModelFactory {
    constructor(store) {
      this.store = store;
      this.identifierCache = store.identifierCache;
      this.identifierCache.__configureMerge((identifier, matchedIdentifier, resourceData) => {
        let intendedIdentifier = identifier;
        if (identifier.id !== matchedIdentifier.id) {
          intendedIdentifier = 'id' in resourceData && identifier.id === resourceData.id ? identifier : matchedIdentifier;
        } else if (identifier.type !== matchedIdentifier.type) {
          intendedIdentifier = 'type' in resourceData && identifier.type === resourceData.type ? identifier : matchedIdentifier;
        }
        let altIdentifier = identifier === intendedIdentifier ? matchedIdentifier : identifier;

        // check for duplicate InternalModel's
        const map = this.modelMapFor(identifier.type);
        let im = map.get(intendedIdentifier.lid);
        let otherIm = map.get(altIdentifier.lid);

        // we cannot merge internalModels when both have records
        // (this may not be strictly true, we could probably swap the internalModel the record points at)
        if (im && otherIm && im.hasRecord && otherIm.hasRecord) {
          if ('id' in resourceData) {
            throw new Error(`Failed to update the 'id' for the RecordIdentifier '${identifier.type}:${identifier.id} (${identifier.lid})' to '${resourceData.id}', because that id is already in use by '${matchedIdentifier.type}:${matchedIdentifier.id} (${matchedIdentifier.lid})'`);
          }
        }

        // remove otherIm from cache
        if (otherIm) {
          map.remove(otherIm, altIdentifier.lid);
        }
        if (im === null && otherIm === null) {
          // nothing more to do
          return intendedIdentifier;

          // only the other has an InternalModel
          // OR only the other has a Record
        } else if (im === null && otherIm !== null || im && !im.hasRecord && otherIm && otherIm.hasRecord) {
          if (im) {
            // TODO check if we are retained in any async relationships
            map.remove(im, intendedIdentifier.lid);
            // im.destroy();
          }
          im = otherIm;
          // TODO do we need to notify the id change?
          im._id = intendedIdentifier.id;
          map.add(im, intendedIdentifier.lid);

          // just use im
        } else ;

        /*
        TODO @runspired consider adding this to make polymorphism even nicer
        if (HAS_RECORD_DATA_PACKAGE) {
          if (identifier.type !== matchedIdentifier.type) {
            const graphFor = require('@ember-data/record-data/-private').graphFor;
            graphFor(this).registerPolymorphicType(identifier.type, matchedIdentifier.type);
          }
        }
        */

        return intendedIdentifier;
      });
      this._identityMap = new IdentityMap();
    }

    /**
     * Retrieve the InternalModel for a given { type, id, lid }.
     *
     * If an InternalModel does not exist, it instantiates one.
     *
     * If an InternalModel does exist bus has a scheduled destroy,
     *   the scheduled destroy will be cancelled.
     *
     * @method lookup
     * @private
     */
    lookup(resource, data) {
      if (data !== undefined) {
        // if we've been given data associated with this lookup
        // we must first give secondary-caches for LIDs the
        // opportunity to populate based on it
        this.identifierCache.getOrCreateRecordIdentifier(data);
      }
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      const internalModel = this.peek(identifier);
      if (internalModel) {
        // unloadRecord is async, if one attempts to unload + then sync push,
        //   we must ensure the unload is canceled before continuing
        //   The createRecord path will take _existingInternalModelForId()
        //   which will call `destroySync` instead for this unload + then
        //   sync createRecord scenario. Once we have true client-side
        //   delete signaling, we should never call destroySync
        if (internalModel.hasScheduledDestroy()) {
          internalModel.cancelDestroy();
        }
        return internalModel;
      }
      return this._build(identifier, false);
    }

    /**
     * Peek the InternalModel for a given { type, id, lid }.
     *
     * If an InternalModel does not exist, return `null`.
     *
     * @method peek
     * @private
     */
    peek(identifier) {
      return this.modelMapFor(identifier.type).get(identifier.lid);
    }
    getByResource(resource) {
      const normalizedResource = constructResource(resource);
      return this.lookup(normalizedResource);
    }
    setRecordId(type, id, lid) {
      const resource = {
        type,
        id: null,
        lid
      };
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      const internalModel = this.peek(identifier);
      if (internalModel === null) {
        throw new Error(`Cannot set the id ${id} on the record ${type}:${lid} as there is no such record in the cache.`);
      }
      let oldId = internalModel.id;
      let modelName = internalModel.modelName;
      // however, this is more than likely a developer error.
      if (oldId !== null && id === null) {
        return;
      }
      this.peekById(modelName, id);
      if (identifier.id === null) {
        this.identifierCache.updateRecordIdentifier(identifier, {
          type,
          id
        });
      }
      internalModel.setId(id, true);
    }
    peekById(type, id) {
      const identifier = this.identifierCache.peekRecordIdentifier({
        type,
        id
      });
      let internalModel = identifier ? this.modelMapFor(type).get(identifier.lid) : null;
      if (internalModel && internalModel.hasScheduledDestroy()) {
        // unloadRecord is async, if one attempts to unload + then sync create,
        //   we must ensure the unload is complete before starting the create
        //   The push path will take this.lookup()
        //   which will call `cancelDestroy` instead for this unload + then
        //   sync push scenario. Once we have true client-side
        //   delete signaling, we should never call destroySync
        internalModel.destroySync();
        internalModel = null;
      }
      return internalModel;
    }
    build(newResourceInfo) {
      return this._build(newResourceInfo, true);
    }
    _build(resource, isCreate) {
      if (isCreate === void 0) {
        isCreate = false;
      }
      if (isCreate === true && resource.id) {
        this.peekById(resource.type, resource.id);
      }
      const {
        identifierCache
      } = this;
      let identifier;
      if (isCreate === true) {
        identifier = identifierCache.createIdentifierForNewRecord(resource);
      } else {
        identifier = resource;
      }

      // lookupFactory should really return an object that creates
      // instances with the injections applied
      let internalModel = new InternalModel(this.store, identifier);
      this.modelMapFor(resource.type).add(internalModel, identifier.lid);
      return internalModel;
    }
    remove(internalModel) {
      let recordMap = this.modelMapFor(internalModel.modelName);
      let clientId = internalModel.identifier.lid;
      recordMap.remove(internalModel, clientId);
      const {
        identifier
      } = internalModel;
      this.identifierCache.forgetRecordIdentifier(identifier);
    }
    modelMapFor(type) {
      return this._identityMap.retrieve(type);
    }
    clear(type) {
      if (type === undefined) {
        this._identityMap.clear();
      } else {
        this.modelMapFor(type).clear();
      }
    }
  }

  function _classPrivateFieldLooseBase$3(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
  var id$3 = 0;
  function _classPrivateFieldLooseKey$3(e) { return "__private_" + id$3++ + "_" + e; }
  /**
    @module @ember-data/store
  */

  function isResourceIdentiferWithRelatedLinks(value) {
    return value && value.links && value.links.related;
  }

  /**
    This is the baseClass for the different References
    like RecordReference/HasManyReference/BelongsToReference

   @class Reference
   @public
   */
  var _identifier$2 = /*#__PURE__*/_classPrivateFieldLooseKey$3("identifier");
  class Reference {
    constructor(store, identifier) {
      Object.defineProperty(this, _identifier$2, {
        writable: true,
        value: void 0
      });
      this.store = store;
      _classPrivateFieldLooseBase$3(this, _identifier$2)[_identifier$2] = identifier;
    }
    get recordData() {
      return this.store.recordDataFor(_classPrivateFieldLooseBase$3(this, _identifier$2)[_identifier$2], false);
    }
    _resource() {}

    /**
     This returns a string that represents how the reference will be
     looked up when it is loaded. If the relationship has a link it will
     use the "link" otherwise it defaults to "id".
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default Model.extend({
       comments: hasMany({ async: true })
     });
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      // get the identifier of the reference
     if (commentsRef.remoteType() === "ids") {
       let ids = commentsRef.ids();
     } else if (commentsRef.remoteType() === "link") {
       let link = commentsRef.link();
     }
     ```
      @method remoteType
     @public
     @return {String} The name of the remote type. This should either be "link" or "ids"
     */
    remoteType() {
      let value = this._resource();
      if (isResourceIdentiferWithRelatedLinks(value)) {
        return 'link';
      }
      return 'id';
    }

    /**
     The link Ember Data will use to fetch or reload this belongs-to
     relationship. By default it uses only the "related" resource linkage.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
     export default Model.extend({
        user: belongsTo({ async: true })
      });
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              links: {
                related: '/articles/1/author'
              }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      // get the identifier of the reference
     if (userRef.remoteType() === "link") {
        let link = userRef.link();
      }
     ```
      @method link
     @public
     @return {String} The link Ember Data will use to fetch or reload this belongs-to relationship.
     */
    link() {
      let link;
      let resource = this._resource();
      if (isResourceIdentiferWithRelatedLinks(resource)) {
        if (resource.links) {
          link = resource.links.related;
          link = !link || typeof link === 'string' ? link : link.href;
        }
      }
      return link || null;
    }
    links() {
      let resource = this._resource();
      return resource && resource.links ? resource.links : null;
    }

    /**
     The meta data for the belongs-to relationship.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
     export default Model.extend({
        user: belongsTo({ async: true })
      });
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              links: {
                related: {
                  href: '/articles/1/author'
                },
              },
              meta: {
                lastUpdated: 1458014400000
              }
            }
          }
        }
      });
      let userRef = blog.belongsTo('user');
      userRef.meta() // { lastUpdated: 1458014400000 }
     ```
      @method meta
      @public
     @return {Object} The meta information for the belongs-to relationship.
     */
    meta() {
      let meta = null;
      let resource = this._resource();
      if (resource && resource.meta && typeof resource.meta === 'object') {
        meta = resource.meta;
      }
      return meta;
    }
  }

  var _class$2, _descriptor$2, _token$2, _relatedToken;
  function _initializerDefineProperty$2(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _classPrivateFieldLooseBase$2(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
  var id$2 = 0;
  function _classPrivateFieldLooseKey$2(e) { return "__private_" + id$2++ + "_" + e; }
  function _applyDecoratedDescriptor$2(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }

  /**
    @module @ember-data/store
  */

  /**
   A `BelongsToReference` is a low-level API that allows users and
   addon authors to perform meta-operations on a belongs-to
   relationship.

   @class BelongsToReference
   @public
   @extends Reference
   */
  let BelongsToReference = (_class$2 = (_token$2 = /*#__PURE__*/_classPrivateFieldLooseKey$2("token"), _relatedToken = /*#__PURE__*/_classPrivateFieldLooseKey$2("relatedToken"), class BelongsToReference extends Reference {
    constructor(store, parentIdentifier, belongsToRelationship, key) {
      super(store, parentIdentifier);
      // unsubscribe tokens given to us by the notification manager
      Object.defineProperty(this, _token$2, {
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, _relatedToken, {
        writable: true,
        value: null
      });
      _initializerDefineProperty$2(this, "_ref", _descriptor$2, this);
      this.key = key;
      this.belongsToRelationship = belongsToRelationship;
      this.type = belongsToRelationship.definition.type;
      const parent = internalModelFactoryFor(store).peek(parentIdentifier);
      this.parent = parent.recordReference;
      this.parentIdentifier = parentIdentifier;
      _classPrivateFieldLooseBase$2(this, _token$2)[_token$2] = store._notificationManager.subscribe(parentIdentifier, (_, bucket, notifiedKey) => {
        if ((bucket === 'relationships' || bucket === 'property') && notifiedKey === key) {
          this._ref++;
        }
      });

      // TODO inverse
    }
    destroy() {
      unsubscribe(_classPrivateFieldLooseBase$2(this, _token$2)[_token$2]);
      if (_classPrivateFieldLooseBase$2(this, _relatedToken)[_relatedToken]) {
        unsubscribe(_classPrivateFieldLooseBase$2(this, _relatedToken)[_relatedToken]);
      }
    }
    get _relatedIdentifier() {
      this._ref; // consume the tracked prop
      if (_classPrivateFieldLooseBase$2(this, _relatedToken)[_relatedToken]) {
        unsubscribe(_classPrivateFieldLooseBase$2(this, _relatedToken)[_relatedToken]);
      }
      let resource = this._resource();
      if (resource && resource.data) {
        const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resource.data);
        _classPrivateFieldLooseBase$2(this, _relatedToken)[_relatedToken] = this.store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
          if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
            this._ref++;
          }
        });
        return identifier;
      }
      return null;
    }

    /**
     The `id` of the record that this reference refers to. Together, the
     `type()` and `id()` methods form a composite key for the identity
     map. This can be used to access the id of an async relationship
     without triggering a fetch that would normally happen if you
     attempted to use `record.get('relationship.id')`.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
      export default class BlogModel extends Model {
      @belongsTo({ async: true }) user;
     }
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      // get the identifier of the reference
     if (userRef.remoteType() === "id") {
        let id = userRef.id();
      }
     ```
      @method id
      @public
     @return {String} The id of the record in this belongsTo relationship.
     */
    id() {
      return this._relatedIdentifier?.id || null;
    }
    _resource() {
      return this.recordData.getBelongsTo(this.key);
    }

    /**
     `push` can be used to update the data in the relationship and Ember
     Data will treat the new data as the canonical value of this
     relationship on the backend.
      Example
      ```app/models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
      export default class BlogModel extends Model {
        @belongsTo({ async: true }) user;
      }
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      // provide data for reference
     userRef.push({
        data: {
          type: 'user',
          id: 1,
          attributes: {
            username: "@user"
          }
        }
      }).then(function(user) {
        userRef.value() === user;
      });
     ```
      @method push
      @public
     @param {Object|Promise} objectOrPromise a promise that resolves to a JSONAPI document object describing the new value of this relationship.
     @return {Promise<record>} A promise that resolves with the new value in this belongs-to relationship.
     */
    async push(data) {
      const jsonApiDoc = await RSVP.resolve(data);
      let record = this.store.push(jsonApiDoc);
      const {
        graph,
        identifier
      } = this.belongsToRelationship;
      this.store._backburner.join(() => {
        graph.push({
          op: 'replaceRelatedRecord',
          record: identifier,
          field: this.key,
          value: recordIdentifierFor(record)
        });
      });
      return record;
    }

    /**
     `value()` synchronously returns the current value of the belongs-to
     relationship. Unlike `record.get('relationshipName')`, calling
     `value()` on a reference does not trigger a fetch if the async
     relationship is not yet loaded. If the relationship is not loaded
     it will always return `null`.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
      export default class BlogModel extends Model {
       @belongsTo({ async: true }) user;
     }
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      userRef.value(); // null
      // provide data for reference
     userRef.push({
        data: {
          type: 'user',
          id: 1,
          attributes: {
            username: "@user"
          }
        }
      }).then(function(user) {
        userRef.value(); // user
      });
     ```
      @method value
      @public
     @return {Model} the record in this relationship
     */
    value() {
      let resource = this._resource();
      if (resource && resource.data) {
        let inverseInternalModel = this.store._internalModelForResource(resource.data);
        if (inverseInternalModel && inverseInternalModel.currentState.isLoaded) {
          return inverseInternalModel.getRecord();
        }
      }
      return null;
    }

    /**
     Loads a record in a belongs-to relationship if it is not already
     loaded. If the relationship is already loaded this method does not
     trigger a new load.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
      export default class BlogModel extends Model {
       @belongsTo({ async: true }) user;
     }
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      userRef.value(); // null
      userRef.load().then(function(user) {
        userRef.value() === user
      });
     ```
      You may also pass in an options object whose properties will be
     fed forward. This enables you to pass `adapterOptions` into the
     request given to the adapter via the reference.
      Example
      ```javascript
     userRef.load({ adapterOptions: { isPrivate: true } }).then(function(user) {
       userRef.value() === user;
     });
     ```
     ```app/adapters/user.js
     import Adapter from '@ember-data/adapter';
      export default class UserAdapter extends Adapter {
       findRecord(store, type, id, snapshot) {
         // In the adapter you will have access to adapterOptions.
         let adapterOptions = snapshot.adapterOptions;
       }
     });
     ```
      @method load
      @public
     @param {Object} options the options to pass in.
     @return {Promise} a promise that resolves with the record in this belongs-to relationship.
     */
    load(options) {
      let parentInternalModel = internalModelFactoryFor(this.store).peek(this.parentIdentifier);
      return parentInternalModel.getBelongsTo(this.key, options);
    }

    /**
     Triggers a reload of the value in this relationship. If the
     remoteType is `"link"` Ember Data will use the relationship link to
     reload the relationship. Otherwise it will reload the record by its
     id.
      Example
      ```javascript
     // models/blog.js
     import Model, { belongsTo } from '@ember-data/model';
      export default class BlogModel extends Model {
       @belongsTo({ async: true }) user;
     }
      let blog = store.push({
        data: {
          type: 'blog',
          id: 1,
          relationships: {
            user: {
              data: { type: 'user', id: 1 }
            }
          }
        }
      });
     let userRef = blog.belongsTo('user');
      userRef.reload().then(function(user) {
        userRef.value() === user
      });
     ```
      You may also pass in an options object whose properties will be
     fed forward. This enables you to pass `adapterOptions` into the
     request given to the adapter via the reference. A full example
     can be found in the `load` method.
      Example
      ```javascript
     userRef.reload({ adapterOptions: { isPrivate: true } })
     ```
      @method reload
      @public
     @param {Object} options the options to pass in.
     @return {Promise} a promise that resolves with the record in this belongs-to relationship after the reload has completed.
     */
    reload(options) {
      let parentInternalModel = internalModelFactoryFor(this.store).peek(this.parentIdentifier);
      return parentInternalModel.reloadBelongsTo(this.key, options).then(internalModel => {
        return this.value();
      });
    }
  }), (_descriptor$2 = _applyDecoratedDescriptor$2(_class$2.prototype, "_ref", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return 0;
    }
  }), _applyDecoratedDescriptor$2(_class$2.prototype, "_relatedIdentifier", [emberCachedDecoratorPolyfill.cached, compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$2.prototype, "_relatedIdentifier"), _class$2.prototype)), _class$2);

  var _class$1, _descriptor$1, _token$1, _identifier$1, _relatedTokenMap;
  function _initializerDefineProperty$1(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _classPrivateFieldLooseBase$1(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
  var id$1 = 0;
  function _classPrivateFieldLooseKey$1(e) { return "__private_" + id$1++ + "_" + e; }
  function _applyDecoratedDescriptor$1(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
  /**
    @module @ember-data/store
  */

  /**
   A `HasManyReference` is a low-level API that allows users and addon
   authors to perform meta-operations on a has-many relationship.

   @class HasManyReference
   @public
   @extends Reference
   */
  let HasManyReference = (_class$1 = (_token$1 = /*#__PURE__*/_classPrivateFieldLooseKey$1("token"), _identifier$1 = /*#__PURE__*/_classPrivateFieldLooseKey$1("identifier"), _relatedTokenMap = /*#__PURE__*/_classPrivateFieldLooseKey$1("relatedTokenMap"), class HasManyReference extends Reference {
    constructor(store, parentIdentifier, hasManyRelationship, key) {
      super(store, parentIdentifier);
      // unsubscribe tokens given to us by the notification manager
      Object.defineProperty(this, _token$1, {
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, _identifier$1, {
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, _relatedTokenMap, {
        writable: true,
        value: void 0
      });
      _initializerDefineProperty$1(this, "_ref", _descriptor$1, this);
      this.key = key;
      this.hasManyRelationship = hasManyRelationship;
      this.type = hasManyRelationship.definition.type;
      this.parent = internalModelFactoryFor(store).peek(parentIdentifier).recordReference;
      _classPrivateFieldLooseBase$1(this, _identifier$1)[_identifier$1] = parentIdentifier;
      _classPrivateFieldLooseBase$1(this, _token$1)[_token$1] = store._notificationManager.subscribe(parentIdentifier, (_, bucket, notifiedKey) => {
        if ((bucket === 'relationships' || bucket === 'property') && notifiedKey === key) {
          this._ref++;
        }
      });
      _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap] = new Map();
      // TODO inverse
    }
    destroy() {
      unsubscribe(_classPrivateFieldLooseBase$1(this, _token$1)[_token$1]);
      _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap].forEach(token => {
        unsubscribe(token);
      });
      _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap].clear();
    }
    get _relatedIdentifiers() {
      this._ref; // consume the tracked prop

      let resource = this._resource();
      _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap].forEach(token => {
        unsubscribe(token);
      });
      _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap].clear();
      if (resource && resource.data) {
        return resource.data.map(resourceIdentifier => {
          const identifier = this.store.identifierCache.getOrCreateRecordIdentifier(resourceIdentifier);
          const token = this.store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
            if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
              this._ref++;
            }
          });
          _classPrivateFieldLooseBase$1(this, _relatedTokenMap)[_relatedTokenMap].set(identifier, token);
          return identifier;
        });
      }
      return [];
    }
    _resource() {
      return this.recordData.getHasMany(this.key);
    }

    /**
     This returns a string that represents how the reference will be
     looked up when it is loaded. If the relationship has a link it will
     use the "link" otherwise it defaults to "id".
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      // get the identifier of the reference
     if (commentsRef.remoteType() === "ids") {
       let ids = commentsRef.ids();
     } else if (commentsRef.remoteType() === "link") {
       let link = commentsRef.link();
     }
     ```
      @method remoteType
     @public
     @return {String} The name of the remote type. This should either be `link` or `ids`
     */
    remoteType() {
      let value = this._resource();
      if (value && value.links && value.links.related) {
        return 'link';
      }
      return 'ids';
    }

    /**
     `ids()` returns an array of the record IDs in this relationship.
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      commentsRef.ids(); // ['1']
     ```
      @method ids
      @public
     @return {Array} The ids in this has-many relationship
     */
    ids() {
      return this._relatedIdentifiers.map(identifier => identifier.id);
    }

    /**
     `push` can be used to update the data in the relationship and Ember
     Data will treat the new data as the canonical value of this
     relationship on the backend.
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      commentsRef.ids(); // ['1']
      commentsRef.push([
     [{ type: 'comment', id: 2 }],
     [{ type: 'comment', id: 3 }],
     ])
      commentsRef.ids(); // ['2', '3']
     ```
      @method push
      @public
     @param {Array|Promise} objectOrPromise a promise that resolves to a JSONAPI document object describing the new value of this relationship.
     @return {ManyArray}
     */
    async push(objectOrPromise) {
      const payload = await RSVP.resolve(objectOrPromise);
      let array;
      if (!Array.isArray(payload) && typeof payload === 'object' && Array.isArray(payload.data)) {
        array = payload.data;
      } else {
        array = payload;
      }
      const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase$1(this, _identifier$1)[_identifier$1]);
      const {
        store
      } = this;
      let identifiers = array.map(obj => {
        let record;
        if ('data' in obj) {
          // TODO deprecate pushing non-valid JSON:API here
          record = store.push(obj);
        } else {
          record = store.push({
            data: obj
          });
        }
        return recordIdentifierFor(record);
      });
      const {
        graph,
        identifier
      } = this.hasManyRelationship;
      store._backburner.join(() => {
        graph.push({
          op: 'replaceRelatedRecords',
          record: identifier,
          field: this.key,
          value: identifiers
        });
      });

      // TODO IGOR it seems wrong that we were returning the many array here
      return internalModel.getHasMany(this.key);
    }
    _isLoaded() {
      let hasRelationshipDataProperty = this.hasManyRelationship.state.hasReceivedData;
      if (!hasRelationshipDataProperty) {
        return false;
      }
      let members = this.hasManyRelationship.currentState;

      //TODO Igor cleanup
      return members.every(identifier => {
        let internalModel = this.store._internalModelForResource(identifier);
        return internalModel.currentState.isLoaded === true;
      });
    }

    /**
     `value()` synchronously returns the current value of the has-many
     relationship. Unlike `record.get('relationshipName')`, calling
     `value()` on a reference does not trigger a fetch if the async
     relationship is not yet loaded. If the relationship is not loaded
     it will always return `null`.
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      post.get('comments').then(function(comments) {
       commentsRef.value() === comments
     })
     ```
      @method value
      @public
     @return {ManyArray}
     */
    value() {
      const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase$1(this, _identifier$1)[_identifier$1]);
      if (this._isLoaded()) {
        return internalModel.getManyArray(this.key);
      }
      return null;
    }

    /**
     Loads the relationship if it is not already loaded.  If the
     relationship is already loaded this method does not trigger a new
     load. This causes a request to the specified
     relationship link or reloads all items currently in the relationship.
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      commentsRef.load().then(function(comments) {
       //...
     });
     ```
      You may also pass in an options object whose properties will be
     fed forward. This enables you to pass `adapterOptions` into the
     request given to the adapter via the reference.
      Example
      ```javascript
     commentsRef.load({ adapterOptions: { isPrivate: true } })
       .then(function(comments) {
         //...
       });
     ```
      ```app/adapters/comment.js
     export default ApplicationAdapter.extend({
       findMany(store, type, id, snapshots) {
         // In the adapter you will have access to adapterOptions.
         let adapterOptions = snapshots[0].adapterOptions;
       }
     });
     ```
      @method load
      @public
     @param {Object} options the options to pass in.
     @return {Promise} a promise that resolves with the ManyArray in
     this has-many relationship.
     */
    load(options) {
      const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase$1(this, _identifier$1)[_identifier$1]);
      return internalModel.getHasMany(this.key, options);
    }

    /**
     Reloads this has-many relationship. This causes a request to the specified
     relationship link or reloads all items currently in the relationship.
      Example
      ```app/models/post.js
     import Model, { hasMany } from '@ember-data/model';
      export default class PostModel extends Model {
       @hasMany({ async: true }) comments;
     }
     ```
      ```javascript
     let post = store.push({
       data: {
         type: 'post',
         id: 1,
         relationships: {
           comments: {
             data: [{ type: 'comment', id: 1 }]
           }
         }
       }
     });
      let commentsRef = post.hasMany('comments');
      commentsRef.reload().then(function(comments) {
       //...
     });
     ```
      You may also pass in an options object whose properties will be
     fed forward. This enables you to pass `adapterOptions` into the
     request given to the adapter via the reference. A full example
     can be found in the `load` method.
      Example
      ```javascript
     commentsRef.reload({ adapterOptions: { isPrivate: true } })
     ```
      @method reload
      @public
     @param {Object} options the options to pass in.
     @return {Promise} a promise that resolves with the ManyArray in this has-many relationship.
     */
    reload(options) {
      const internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase$1(this, _identifier$1)[_identifier$1]);
      return internalModel.reloadHasMany(this.key, options);
    }
  }), (_descriptor$1 = _applyDecoratedDescriptor$1(_class$1.prototype, "_ref", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return 0;
    }
  }), _applyDecoratedDescriptor$1(_class$1.prototype, "_relatedIdentifiers", [emberCachedDecoratorPolyfill.cached, compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class$1.prototype, "_relatedIdentifiers"), _class$1.prototype)), _class$1);

  var _class, _descriptor, _token, _identifier;
  function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
  function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
  var id = 0;
  function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
  function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
  /**
    @module @ember-data/store
  */

  /**
     A `RecordReference` is a low-level API that allows users and
     addon authors to perform meta-operations on a record.

     @class RecordReference
     @public
     @extends Reference
  */
  let RecordReference = (_class = (_token = /*#__PURE__*/_classPrivateFieldLooseKey("token"), _identifier = /*#__PURE__*/_classPrivateFieldLooseKey("identifier"), class RecordReference extends Reference {
    constructor(store, identifier) {
      super(store, identifier);
      // unsubscribe token given to us by the notification manager
      Object.defineProperty(this, _token, {
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, _identifier, {
        writable: true,
        value: void 0
      });
      _initializerDefineProperty(this, "_ref", _descriptor, this);
      this.store = store;
      _classPrivateFieldLooseBase(this, _identifier)[_identifier] = identifier;
      _classPrivateFieldLooseBase(this, _token)[_token] = store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
        if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
          this._ref++;
        }
      });
    }
    destroy() {
      unsubscribe(_classPrivateFieldLooseBase(this, _token)[_token]);
    }
    get type() {
      return this.identifier().type;
    }
    get _id() {
      this._ref; // consume the tracked prop
      let identifier = this.identifier();
      if (identifier) {
        return identifier.id;
      }
      return null;
    }

    /**
       The `id` of the record that this reference refers to.
        Together, the `type` and `id` properties form a composite key for
       the identity map.
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        userRef.id(); // '1'
       ```
        @method id
      @public
       @return {String} The id of the record.
    */
    id() {
      return this._id;
    }

    /**
       The `identifier` of the record that this reference refers to.
        Together, the `type` and `id` properties form a composite key for
       the identity map.
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        userRef.identifier(); // '1'
       ```
        @method identifier
      @public
       @return {String} The identifier of the record.
    */
    identifier() {
      return _classPrivateFieldLooseBase(this, _identifier)[_identifier];
    }

    /**
       How the reference will be looked up when it is loaded. Currently
       this always returns `identity` to signify that a record will be
       loaded by its `type` and `id`.
        Example
        ```javascript
       const userRef = store.getReference('user', 1);
        userRef.remoteType(); // 'identity'
       ```
        @method remoteType
       @public
       @return {String} 'identity'
    */
    remoteType() {
      return 'identity';
    }

    /**
      This API allows you to provide a reference with new data. The
      simplest usage of this API is similar to `store.push`: you provide a
      normalized hash of data and the object represented by the reference
      will update.
       If you pass a promise to `push`, Ember Data will not ask the adapter
      for the data if another attempt to fetch it is made in the
      interim. When the promise resolves, the underlying object is updated
      with the new data, and the promise returned by *this function* is resolved
      with that object.
       For example, `recordReference.push(promise)` will be resolved with a
      record.
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        // provide data for reference
       userRef.push({
         data: {
           id: "1",
           type: "user",
           attributes: {
             username: "@user"
           }
         }
       }).then(function(user) {
         userRef.value() === user;
       });
       ```
       @method push
      @public
      @param objectOrPromise a JSON:API ResourceDocument or a promise resolving to one
      @return a promise for the value (record or relationship)
    */
    push(objectOrPromise) {
      return RSVP.resolve(objectOrPromise).then(data => {
        return this.store.push(data);
      });
    }

    /**
      If the entity referred to by the reference is already loaded, it is
      present as `reference.value`. Otherwise the value returned by this function
      is `null`.
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        userRef.value(); // user
       ```
        @method value
      @public
       @return {Model} the record for this RecordReference
    */
    value() {
      if (this.id() !== null) {
        let internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
        if (internalModel && internalModel.currentState.isLoaded) {
          return internalModel.getRecord();
        }
      }
      return null;
    }

    /**
       Triggers a fetch for the backing entity based on its `remoteType`
       (see `remoteType` definitions per reference type).
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        // load user (via store.find)
       userRef.load().then(...)
       ```
        @method load
      @public
       @return {Promise<record>} the record for this RecordReference
    */
    load() {
      const id = this.id();
      if (id !== null) {
        return this.store.findRecord(this.type, id);
      }
      throw new Error(`Unable to fetch record of type ${this.type} without an id`);
    }

    /**
       Reloads the record if it is already loaded. If the record is not
       loaded it will load the record via `store.findRecord`
        Example
        ```javascript
       let userRef = store.getReference('user', 1);
        // or trigger a reload
       userRef.reload().then(...)
       ```
        @method reload
      @public
       @return {Promise<record>} the record for this RecordReference
    */
    reload() {
      const id = this.id();
      if (id !== null) {
        return this.store.findRecord(this.type, id, {
          reload: true
        });
      }
      throw new Error(`Unable to fetch record of type ${this.type} without an id`);
    }
  }), (_descriptor = _applyDecoratedDescriptor(_class.prototype, "_ref", [tracking.tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: function () {
      return 0;
    }
  }), _applyDecoratedDescriptor(_class.prototype, "_id", [emberCachedDecoratorPolyfill.cached, compat.dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "_id"), _class.prototype)), _class);

  /*
    This file encapsulates the various states that a record can transition
    through during its lifecycle.
  */
  /**
    ### State

    Each record has a `currentState` property that explicitly tracks what
    state a record is in at any given time. For instance, if a record is
    newly created and has not yet been sent to the adapter to be saved,
    it would be in the `root.loaded.created.uncommitted` state.  If a
    record has had local modifications made to it that are in the
    process of being saved, the record would be in the
    `root.loaded.updated.inFlight` state. (This state path will be
    explained in more detail below.)

    Events are sent by the record or its store to the record's
    `currentState` property. How the state reacts to these events is
    dependent on which state it is in. In some states, certain events
    will be invalid and will cause an exception to be raised.

    States are hierarchical and every state is a sub-state of the
    `RootState`. For example, a record can be in the
    `root.deleted.uncommitted` state then transitions into the
    `root.deleted.inFlight` state. If a child state does not implement
    an event handler, the state manager will attempt to invoke the event
    on all parent states until the root state is reached. The state
    hierarchy of a record is described in terms of a path string. You
    can determine a record's current state by getting the state's
    `stateName` property:

    ```javascript
    record.get('currentState.stateName');
    //=> "root.created.uncommitted"
     ```

    The hierarchy of valid states that ship with ember data looks like
    this:

    ```text
    * root
      * deleted
        * saved
        * uncommitted
        * inFlight
      * empty
      * loaded
        * created
          * uncommitted
          * inFlight
        * saved
        * updated
          * uncommitted
          * inFlight
      * loading
    ```

    The `Model` states are themselves stateless. What that means is
    that, the hierarchical states that each of *those* points to is a
    shared data structure. For performance reasons, instead of each
    record getting its own copy of the hierarchy of states, each record
    points to this global, immutable shared instance. How does a state
    know which record it should be acting on? We pass the record
    instance into the state's event handlers as the first argument.

    The record passed as the first parameter is where you should stash
    state about the record if needed; you should never store data on the state
    object itself.

    ### Events and Flags

    A state may implement zero or more events and flags.

    #### Events

    Events are named functions that are invoked when sent to a record. The
    record will first look for a method with the given name on the
    current state. If no method is found, it will search the current
    state's parent, and then its grandparent, and so on until reaching
    the top of the hierarchy. If the root is reached without an event
    handler being found, an exception will be raised. This can be very
    helpful when debugging new features.

    Here's an example implementation of a state with a `myEvent` event handler:

    ```javascript
    aState: State.create({
      myEvent: function(manager, param) {
        console.log("Received myEvent with", param);
      }
    })
    ```

    To trigger this event:

    ```javascript
    record.send('myEvent', 'foo');
    //=> "Received myEvent with foo"
    ```

    Note that an optional parameter can be sent to a record's `send()` method,
    which will be passed as the second parameter to the event handler.

    Events should transition to a different state if appropriate. This can be
    done by calling the record's `transitionTo()` method with a path to the
    desired state. The state manager will attempt to resolve the state path
    relative to the current state. If no state is found at that path, it will
    attempt to resolve it relative to the current state's parent, and then its
    parent, and so on until the root is reached. For example, imagine a hierarchy
    like this:

        * created
          * uncommitted <-- currentState
          * inFlight
        * updated
          * inFlight

    If we are currently in the `uncommitted` state, calling
    `transitionTo('inFlight')` would transition to the `created.inFlight` state,
    while calling `transitionTo('updated.inFlight')` would transition to
    the `updated.inFlight` state.

    Remember that *only events* should ever cause a state transition. You should
    never call `transitionTo()` from outside a state's event handler. If you are
    tempted to do so, create a new event and send that to the state manager.

    #### Flags

    Flags are Boolean values that can be used to introspect a record's current
    state in a more user-friendly way than examining its state path. For example,
    instead of doing this:

    ```javascript
    var statePath = record.get('stateManager.currentPath');
    if (statePath === 'created.inFlight') {
      doSomething();
    }
    ```

    You can say:

    ```javascript
    if (record.get('isNew') && record.get('isSaving')) {
      doSomething();
    }
    ```

    If your state does not set a value for a given flag, the value will
    be inherited from its parent (or the first place in the state hierarchy
    where it is defined).

    The current set of flags are defined below. If you want to add a new flag,
    in addition to the area below, you will also need to declare it in the
    `Model` class.


     * [isEmpty](Model/properties/isEmpty?anchor=isEmpty)
     * [isLoading](Model/properties/isLoading?anchor=isLoading)
     * [isLoaded](Model/properties/isLoaded?anchor=isLoaded)
     * [hasDirtyAttributes](Model/properties/hasDirtyAttributes?anchor=hasDirtyAttributes)
     * [isSaving](Model/properties/isSaving?anchor=isSaving)
     * [isDeleted](Model/properties/isDeleted?anchor=isDeleted)
     * [isNew](Model/properties/isNew?anchor=isNew)
     * [isValid](Model/properties/isValid?anchor=isValid)

    @class RootState
    @internal
  */

  function didSetProperty(internalModel, context) {
    if (context.isDirty) {
      internalModel.send('becomeDirty');
    } else {
      internalModel.send('propertyWasReset');
    }
  }

  // Implementation notes:
  //
  // Each state has a boolean value for all of the following flags:
  //
  // * isLoaded: The record has a populated `data` property. When a
  //   record is loaded via `store.find`, `isLoaded` is false
  //   until the adapter sets it. When a record is created locally,
  //   its `isLoaded` property is always true.
  // * isDirty: The record has local changes that have not yet been
  //   saved by the adapter. This includes records that have been
  //   created (but not yet saved) or deleted.
  // * isSaving: The record has been committed, but
  //   the adapter has not yet acknowledged that the changes have
  //   been persisted to the backend.
  // * isDeleted: The record was marked for deletion. When `isDeleted`
  //   is true and `isDirty` is true, the record is deleted locally
  //   but the deletion was not yet persisted. When `isSaving` is
  //   true, the change is in-flight. When both `isDirty` and
  //   `isSaving` are false, the change has persisted.
  // * isNew: The record was created on the client and the adapter
  //   did not yet report that it was successfully saved.
  // * isValid: The adapter did not report any server-side validation
  //   failures.

  // The dirty state is a abstract state whose functionality is
  // shared between the `created` and `updated` states.
  //
  // The deleted state shares the `isDirty` flag with the
  // subclasses of `DirtyState`, but with a very different
  // implementation.
  //
  // Dirty states have three child states:
  //
  // `uncommitted`: the store has not yet handed off the record
  //   to be saved.
  // `inFlight`: the store has handed off the record to be saved,
  //   but the adapter has not yet acknowledged success.
  // `invalid`: the record has invalid information and cannot be
  //   sent to the adapter yet.
  const DirtyState = {
    initialState: 'uncommitted',
    // FLAGS
    isDirty: true,
    // SUBSTATES

    // When a record first becomes dirty, it is `uncommitted`.
    // This means that there are local pending changes, but they
    // have not yet begun to be saved, and are not invalid.
    uncommitted: {
      // EVENTS
      didSetProperty,
      //TODO(Igor) reloading now triggers a
      //loadingData event, though it seems fine?
      loadingData() {},
      propertyWasReset(internalModel, name) {
        if (!internalModel.hasChangedAttributes()) {
          internalModel.send('rolledBack');
        }
      },
      pushedData(internalModel) {
        if (!internalModel.hasChangedAttributes()) {
          internalModel.transitionTo('loaded.saved');
        }
      },
      becomeDirty() {},
      willCommit(internalModel) {
        internalModel.transitionTo('inFlight');
      },
      reloadRecord(internalModel, _ref) {
        let {
          resolve,
          options
        } = _ref;
        resolve(internalModel.store._reloadRecord(internalModel, options));
      },
      rolledBack(internalModel) {
        internalModel.transitionTo('loaded.saved');
        internalModel.triggerLater('rolledBack');
      },
      becameInvalid(internalModel) {
        internalModel.transitionTo('invalid');
      },
      rollback(internalModel) {
        internalModel.rollbackAttributes();
        internalModel.triggerLater('ready');
      }
    },
    // Once a record has been handed off to the adapter to be
    // saved, it is in the 'in flight' state. Changes to the
    // record cannot be made during this window.
    inFlight: {
      // FLAGS
      isSaving: true,
      // EVENTS
      didSetProperty,
      becomeDirty() {},
      pushedData() {},
      unloadRecord: assertAgainstUnloadRecord,
      // TODO: More robust semantics around save-while-in-flight
      willCommit() {},
      didCommit(internalModel) {
        internalModel.transitionTo('saved');
        internalModel.send('invokeLifecycleCallbacks', this.dirtyType);
      },
      rolledBack(internalModel) {
        internalModel.triggerLater('rolledBack');
      },
      becameInvalid(internalModel) {
        internalModel.transitionTo('invalid');
        internalModel.send('invokeLifecycleCallbacks');
      },
      becameError(internalModel) {
        internalModel.transitionTo('uncommitted');
        internalModel.triggerLater('becameError', internalModel);
      }
    },
    // A record is in the `invalid` if the adapter has indicated
    // the the record failed server-side invalidations.
    invalid: {
      // FLAGS
      isValid: false,
      // EVENTS
      deleteRecord(internalModel) {
        internalModel.transitionTo('deleted.uncommitted');
      },
      didSetProperty(internalModel, context) {
        internalModel.getRecord().errors._remove(context.name);
        didSetProperty(internalModel, context);
        if (!internalModel.hasErrors()) {
          this.becameValid(internalModel);
        }
      },
      becameInvalid() {},
      becomeDirty() {},
      pushedData() {},
      willCommit(internalModel) {
        clearErrorMessages(internalModel);
        internalModel.transitionTo('inFlight');
      },
      rolledBack(internalModel) {
        clearErrorMessages(internalModel);
        internalModel.transitionTo('loaded.saved');
        internalModel.triggerLater('ready');
      },
      becameValid(internalModel) {
        internalModel.transitionTo('uncommitted');
      },
      invokeLifecycleCallbacks(internalModel) {
        internalModel.triggerLater('becameInvalid', internalModel);
      }
    }
  };

  // The created and updated states are created outside the state
  // chart so we can reopen their substates and add mixins as
  // necessary.

  function deepClone(object) {
    const clone = {};
    let value;
    for (let prop in object) {
      value = object[prop];
      if (value && typeof value === 'object') {
        clone[prop] = deepClone(value);
      } else {
        clone[prop] = value;
      }
    }
    return clone;
  }
  function mixin(original, hash) {
    for (let prop in hash) {
      original[prop] = hash[prop];
    }
    return original;
  }
  function dirtyState(options) {
    var newState = deepClone(DirtyState);
    return mixin(newState, options);
  }
  const createdState = dirtyState({
    dirtyType: 'created',
    // FLAGS
    isNew: true,
    setup(internalModel) {
      internalModel.store.recordArrayManager.recordDidChange(internalModel.identifier);
    }
  });
  createdState.invalid.rolledBack = function (internalModel) {
    internalModel.transitionTo('deleted.saved');
    internalModel.triggerLater('rolledBack');
  };
  createdState.uncommitted.rolledBack = function (internalModel) {
    internalModel.transitionTo('deleted.saved');
    internalModel.triggerLater('rolledBack');
  };
  const updatedState = dirtyState({
    dirtyType: 'updated'
  });
  function createdStateDeleteRecord(internalModel) {
    internalModel.transitionTo('deleted.saved');
    internalModel.send('invokeLifecycleCallbacks');
  }
  createdState.uncommitted.deleteRecord = createdStateDeleteRecord;
  createdState.invalid.deleteRecord = createdStateDeleteRecord;
  createdState.uncommitted.rollback = function (internalModel) {
    DirtyState.uncommitted.rollback.apply(this, arguments);
    internalModel.transitionTo('deleted.saved');
  };
  createdState.uncommitted.pushedData = function (internalModel) {
    // TODO @runspired consider where to do this once we kill off state machine
    internalModel.store._notificationManager.notify(internalModel.identifier, 'identity');
    internalModel.transitionTo('loaded.updated.uncommitted');
    internalModel.triggerLater('didLoad');
  };
  createdState.uncommitted.propertyWasReset = function () {};
  function assertAgainstUnloadRecord(internalModel) {
  }
  updatedState.invalid.becameValid = function (internalModel) {
    // we're eagerly transition into the loaded.saved state, even though we could
    // be still dirty; but the setup hook of the loaded.saved state checks for
    // dirty attributes and transitions into the corresponding dirty state
    internalModel.transitionTo('loaded.saved');
  };
  updatedState.inFlight.unloadRecord = assertAgainstUnloadRecord;
  updatedState.uncommitted.deleteRecord = function (internalModel) {
    internalModel.transitionTo('deleted.uncommitted');
  };
  updatedState.invalid.rolledBack = function (internalModel) {
    clearErrorMessages(internalModel);
    internalModel.transitionTo('loaded.saved');
    internalModel.triggerLater('rolledBack');
  };
  const RootState = {
    // FLAGS
    isEmpty: false,
    isLoading: false,
    isLoaded: false,
    isDirty: false,
    isSaving: false,
    isDeleted: false,
    isNew: false,
    isValid: true,
    // DEFAULT EVENTS

    // Trying to roll back if you're not in the dirty state
    // doesn't change your state. For example, if you're in the
    // in-flight state, rolling back the record doesn't move
    // you out of the in-flight state.
    rolledBack() {},
    unloadRecord(internalModel) {},
    propertyWasReset() {},
    // SUBSTATES

    // A record begins its lifecycle in the `empty` state.
    // If its data will come from the adapter, it will
    // transition into the `loading` state. Otherwise, if
    // the record is being created on the client, it will
    // transition into the `created` state.
    empty: {
      isEmpty: true,
      // EVENTS
      loadingData(internalModel, promise) {
        internalModel.transitionTo('loading');
      },
      loadedData(internalModel) {
        internalModel.transitionTo('loaded.created.uncommitted');
        internalModel.triggerLater('ready');
      },
      pushedData(internalModel) {
        internalModel.transitionTo('loaded.saved');
        internalModel.triggerLater('didLoad');
        internalModel.triggerLater('ready');
      },
      // Record is already in an empty state, triggering transition to empty here
      // produce an error.
      notFound() {}
    },
    // A record enters this state when the store asks
    // the adapter for its data. It remains in this state
    // until the adapter provides the requested data.
    //
    // Usually, this process is asynchronous, using an
    // XHR to retrieve the data.
    loading: {
      // FLAGS
      isLoading: true,
      exit(internalModel) {
        internalModel._promiseProxy = null;
      },
      loadingData() {},
      // EVENTS
      pushedData(internalModel) {
        internalModel.transitionTo('loaded.saved');
        internalModel.triggerLater('didLoad');
        internalModel.triggerLater('ready');
        //TODO this seems out of place here
        internalModel.didCleanError();
      },
      becameError(internalModel) {
        internalModel.triggerLater('becameError', internalModel);
      },
      notFound(internalModel) {
        internalModel.transitionTo('empty');
      }
    },
    // A record enters this state when its data is populated.
    // Most of a record's lifecycle is spent inside substates
    // of the `loaded` state.
    loaded: {
      initialState: 'saved',
      // FLAGS
      isLoaded: true,
      //TODO(Igor) Reloading now triggers a loadingData event,
      //but it should be ok?
      loadingData() {},
      // SUBSTATES

      // If there are no local changes to a record, it remains
      // in the `saved` state.
      saved: {
        setup(internalModel) {
          if (internalModel.hasChangedAttributes()) {
            internalModel.adapterDidDirty();
          }
        },
        // EVENTS
        didSetProperty,
        pushedData() {},
        becomeDirty(internalModel) {
          internalModel.transitionTo('updated.uncommitted');
        },
        willCommit(internalModel) {
          internalModel.transitionTo('updated.inFlight');
        },
        reloadRecord() {},
        deleteRecord(internalModel) {
          internalModel.transitionTo('deleted.uncommitted');
        },
        unloadRecord(internalModel) {},
        didCommit() {},
        // loaded.saved.notFound would be triggered by a failed
        // `reload()` on an unchanged record
        notFound() {}
      },
      // A record is in this state after it has been locally
      // created but before the adapter has indicated that
      // it has been saved.
      created: createdState,
      // A record is in this state if it has already been
      // saved to the server, but there are new local changes
      // that have not yet been saved.
      updated: updatedState
    },
    // A record is in this state if it was deleted from the store.
    deleted: {
      initialState: 'uncommitted',
      dirtyType: 'deleted',
      // FLAGS
      isDeleted: true,
      isLoaded: true,
      isDirty: true,
      // TRANSITIONS
      setup(internalModel) {
        internalModel.store.recordArrayManager.recordDidChange(internalModel.identifier);
      },
      // SUBSTATES

      // When a record is deleted, it enters the `start`
      // state. It will exit this state when the record
      // starts to commit.
      uncommitted: {
        // EVENTS

        willCommit(internalModel) {
          internalModel.transitionTo('inFlight');
        },
        rollback(internalModel) {
          internalModel.rollbackAttributes();
          internalModel.triggerLater('ready');
        },
        pushedData() {},
        becomeDirty() {},
        deleteRecord() {},
        rolledBack(internalModel) {
          internalModel.transitionTo('loaded.saved');
          internalModel.triggerLater('ready');
          internalModel.triggerLater('rolledBack');
        }
      },
      // After a record starts committing, but
      // before the adapter indicates that the deletion
      // has saved to the server, a record is in the
      // `inFlight` substate of `deleted`.
      inFlight: {
        // FLAGS
        isSaving: true,
        // EVENTS

        unloadRecord: assertAgainstUnloadRecord,
        // TODO: More robust semantics around save-while-in-flight
        willCommit() {},
        didCommit(internalModel) {
          internalModel.transitionTo('saved');
          internalModel.send('invokeLifecycleCallbacks');
        },
        becameError(internalModel) {
          internalModel.transitionTo('uncommitted');
          internalModel.triggerLater('becameError', internalModel);
        },
        becameInvalid(internalModel) {
          internalModel.transitionTo('invalid');
          internalModel.triggerLater('becameInvalid', internalModel);
        }
      },
      // Once the adapter indicates that the deletion has
      // been saved, the record enters the `saved` substate
      // of `deleted`.
      saved: {
        // FLAGS
        isDirty: false,
        setup(internalModel) {
          internalModel.removeFromInverseRelationships();
        },
        invokeLifecycleCallbacks(internalModel) {
          internalModel.triggerLater('didDelete', internalModel);
          internalModel.triggerLater('didCommit', internalModel);
        },
        willCommit() {},
        didCommit() {},
        pushedData() {}
      },
      invalid: {
        isValid: false,
        didSetProperty(internalModel, context) {
          internalModel.getRecord().errors._remove(context.name);
          didSetProperty(internalModel, context);
          if (!internalModel.hasErrors()) {
            this.becameValid(internalModel);
          }
        },
        becameInvalid() {},
        becomeDirty() {},
        deleteRecord() {},
        willCommit() {},
        rolledBack(internalModel) {
          clearErrorMessages(internalModel);
          internalModel.transitionTo('loaded.saved');
          internalModel.triggerLater('ready');
        },
        becameValid(internalModel) {
          internalModel.transitionTo('uncommitted');
        }
      }
    },
    invokeLifecycleCallbacks(internalModel, dirtyType) {
      if (dirtyType === 'created') {
        internalModel.triggerLater('didCreate', internalModel);
      } else {
        internalModel.triggerLater('didUpdate', internalModel);
      }
      internalModel.triggerLater('didCommit', internalModel);
    }
  };
  function wireState(object, parent, name) {
    // TODO: Use Object.create and copy instead
    object = mixin(parent ? Object.create(parent) : {}, object);
    object.parentState = parent;
    object.stateName = name;
    for (let prop in object) {
      if (!Object.prototype.hasOwnProperty.call(object, prop) || prop === 'parentState' || prop === 'stateName') {
        continue;
      }
      if (typeof object[prop] === 'object') {
        object[prop] = wireState(object[prop], object, name + '.' + prop);
      }
    }
    return object;
  }
  function clearErrorMessages(internalModel) {
    internalModel.getRecord().errors._clear();
  }
  var RootState$1 = wireState(RootState, null, 'root');

  // move to TS hacks module that we can delete when this is no longer a necessary recast

  /**
    @module @ember-data/store
  */

  const {
    hasOwnProperty
  } = Object.prototype;
  let ManyArray;
  let PromiseBelongsTo;
  let _PromiseManyArray; // TODO find a way to get the klass type here

  let _found = false;
  let _getModelPackage;
  {
    _getModelPackage = function () {
      if (!_found) {
        let modelPackage = require('@ember-data/model/-private');
        ({
          ManyArray,
          PromiseBelongsTo,
          PromiseManyArray: _PromiseManyArray
        } = modelPackage);
        if (ManyArray && PromiseBelongsTo && _PromiseManyArray) {
          _found = true;
        }
      }
      return _found;
    };
  }
  /*
    The TransitionChainMap caches the `state.enters`, `state.setups`, and final state reached
    when transitioning from one state to another, so that future transitions can replay the
    transition without needing to walk the state tree, collect these hook calls and determine
     the state to transition into.

     A future optimization would be to build a single chained method out of the collected enters
     and setups. It may also be faster to do a two level cache (from: { to }) instead of caching based
     on a key that adds the two together.
   */
  const TransitionChainMap = Object.create(null);
  const _extractPivotNameCache = Object.create(null);
  const _splitOnDotCache = Object.create(null);
  function splitOnDot(name) {
    return _splitOnDotCache[name] || (_splitOnDotCache[name] = name.split('.'));
  }
  function extractPivotName(name) {
    return _extractPivotNameCache[name] || (_extractPivotNameCache[name] = splitOnDot(name)[0]);
  }
  class InternalModel {
    // Not typed yet

    constructor(store, identifier) {
      this.store = store;
      this.identifier = identifier;
      {
        _getModelPackage();
      }
      this._id = identifier.id;
      this._isUpdatingId = false;
      this.modelName = identifier.type;
      this.clientId = identifier.lid;
      this.__recordData = null;
      this._promiseProxy = null;
      this._isDestroyed = false;
      this._doNotDestroy = false;
      this.isError = false;
      this._pendingRecordArrayManagerFlush = false; // used by the recordArrayManager

      // During dematerialization we don't want to rematerialize the record.  The
      // reason this might happen is that dematerialization removes records from
      // record arrays,  and Ember arrays will always `objectAt(0)` and
      // `objectAt(len - 1)` to test whether or not `firstObject` or `lastObject`
      // have changed.
      this._isDematerializing = false;
      this._scheduledDestroy = null;
      this._record = null;
      this.error = null;

      // caches for lazy getters
      this._modelClass = null;
      this.__recordArrays = null;
      this._recordReference = null;
      this.__recordData = null;
      this.error = null;

      // other caches
      // class fields have [[DEFINE]] semantics which are significantly slower than [[SET]] semantics here
      this._manyArrayCache = Object.create(null);
      this._relationshipPromisesCache = Object.create(null);
      this._relationshipProxyCache = Object.create(null);
      this.references = Object.create(null);
      this._deferredTriggers = [];
      this.currentState = RootState$1.empty;
    }
    get id() {
      return this.identifier.id;
    }
    set id(value) {
      if (value !== this._id) {
        let newIdentifier = {
          type: this.identifier.type,
          lid: this.identifier.lid,
          id: value
        };
        this.store.identifierCache.updateRecordIdentifier(this.identifier, newIdentifier);
        this.notifyPropertyChange('id');
      }
    }
    get modelClass() {
      if (this.store.modelFor) {
        return this._modelClass || (this._modelClass = this.store.modelFor(this.modelName));
      }
    }
    get recordReference() {
      if (this._recordReference === null) {
        this._recordReference = new RecordReference(this.store, this.identifier);
      }
      return this._recordReference;
    }
    get _recordData() {
      if (this.__recordData === null) {
        let recordData = this.store._createRecordData(this.identifier);
        this.__recordData = recordData;
        return recordData;
      }
      return this.__recordData;
    }
    set _recordData(newValue) {
      this.__recordData = newValue;
    }
    isHiddenFromRecordArrays() {
      // During dematerialization we don't want to rematerialize the record.
      // recordWasDeleted can cause other records to rematerialize because it
      // removes the internal model from the array and Ember arrays will always
      // `objectAt(0)` and `objectAt(len -1)` to check whether `firstObject` or
      // `lastObject` have changed.  When this happens we don't want those
      // models to rematerialize their records.

      // eager checks to avoid instantiating record data if we are empty or loading
      if (this.currentState.isEmpty) {
        return true;
      }
      if (this.currentState.isLoading) {
        return false;
      }
      let isRecordFullyDeleted = this._isRecordFullyDeleted();
      return this._isDematerializing || this.hasScheduledDestroy() || this.isDestroyed || isRecordFullyDeleted;
    }
    _isRecordFullyDeleted() {
      if (this._recordData.isDeletionCommitted && this._recordData.isDeletionCommitted()) {
        return true;
      } else if (this._recordData.isNew && this._recordData.isDeleted && this._recordData.isNew() && this._recordData.isDeleted()) {
        return true;
      } else {
        return this.currentState.stateName === 'root.deleted.saved';
      }
    }
    isDeleted() {
      if (this._recordData.isDeleted) {
        return this._recordData.isDeleted();
      } else {
        return this.currentState.isDeleted;
      }
    }
    isNew() {
      if (this._recordData.isNew) {
        return this._recordData.isNew();
      } else {
        return this.currentState.isNew;
      }
    }
    getRecord(properties) {
      if (!this._record && !this._isDematerializing) {
        let {
          store
        } = this;
        this._record = store._instantiateRecord(this, this.modelName, this._recordData, this.identifier, properties);
        this._triggerDeferredTriggers();
      }
      return this._record;
    }
    dematerializeRecord() {
      this._isDematerializing = true;

      // TODO IGOR add a test that fails when this is missing, something that involves canceling a destroy
      // and the destroy not happening, and then later on trying to destroy
      this._doNotDestroy = false;
      // this has to occur before the internal model is removed
      // for legacy compat.
      if (this._record) {
        this.store.teardownRecord(this._record);
      }

      // move to an empty never-loaded state
      // ensure any record notifications happen prior to us
      // unseting the record but after we've triggered
      // destroy
      this.store._backburner.join(() => {
        this._recordData.unloadRecord();
      });
      if (this._record) {
        Object.keys(this._relationshipProxyCache).forEach(key => {
          if (this._relationshipProxyCache[key].destroy) {
            this._relationshipProxyCache[key].destroy();
          }
          delete this._relationshipProxyCache[key];
        });
      }
      this._record = null;
      this.error = null;
      this._previousState = this.currentState;
      this.currentState = RootState$1.empty;
      this.store.recordArrayManager.recordDidChange(this.identifier);
    }
    deleteRecord() {
      runloop.run(() => {
        const backburner = this.store._backburner;
        backburner.run(() => {
          if (this._recordData.setIsDeleted) {
            this._recordData.setIsDeleted(true);
          }
          if (this.isNew()) {
            // destroyRecord follows up deleteRecord with save(). This prevents an unecessary save for a new record
            this._deletedRecordWasNew = true;
            this.send('deleteRecord');
            this._triggerDeferredTriggers();
            this.unloadRecord();
          } else {
            this.send('deleteRecord');
          }
        });
      });
    }
    save(options) {
      if (this._deletedRecordWasNew) {
        return RSVP.Promise.resolve();
      }
      let promiseLabel = 'DS: Model#save ' + this;
      let resolver = RSVP__default["default"].defer(promiseLabel);

      // Casting to promise to narrow due to the feature flag paths inside scheduleSave
      return this.store.scheduleSave(this, resolver, options);
    }
    reload(options) {
      if (!options) {
        options = {};
      }
      let internalModel = this;
      return internalModel.store._reloadRecord(internalModel, options).then(function () {
        //TODO NOW seems like we shouldn't need to do this
        return internalModel;
      }, function (error) {
        throw error;
      }, 'DS: Model#reload complete, update flags');
    }

    /*
      Unload the record for this internal model. This will cause the record to be
      destroyed and freed up for garbage collection. It will also do a check
      for cleaning up internal models.
       This check is performed by first computing the set of related internal
      models. If all records in this set are unloaded, then the entire set is
      destroyed. Otherwise, nothing in the set is destroyed.
       This means that this internal model will be freed up for garbage collection
      once all models that refer to it via some relationship are also unloaded.
    */
    unloadRecord() {
      if (this.isDestroyed) {
        return;
      }
      this.send('unloadRecord');
      this.dematerializeRecord();
      if (this._scheduledDestroy === null) {
        this._scheduledDestroy = runloop._backburner.schedule('destroy', this, '_checkForOrphanedInternalModels');
      }
    }
    hasScheduledDestroy() {
      return !!this._scheduledDestroy;
    }
    cancelDestroy() {
      this._doNotDestroy = true;
      this._isDematerializing = false;
      runloop.cancel(this._scheduledDestroy);
      this._scheduledDestroy = null;
    }

    // typically, we prefer to async destroy this lets us batch cleanup work.
    // Unfortunately, some scenarios where that is not possible. Such as:
    //
    // ```js
    // const record = store.findRecord(‘record’, 1);
    // record.unloadRecord();
    // store.createRecord(‘record’, 1);
    // ```
    //
    // In those scenarios, we make that model's cleanup work, sync.
    //
    destroySync() {
      if (this._isDematerializing) {
        this.cancelDestroy();
      }
      this._checkForOrphanedInternalModels();
      if (this.isDestroyed || this.isDestroying) {
        return;
      }

      // just in-case we are not one of the orphaned, we should still
      // still destroy ourselves
      this.destroy();
    }
    _checkForOrphanedInternalModels() {
      this._isDematerializing = false;
      this._scheduledDestroy = null;
      if (this.isDestroyed) {
        return;
      }
    }
    _findBelongsTo(key, resource, relationshipMeta, options) {
      // TODO @runspired follow up if parent isNew then we should not be attempting load here
      return this.store._findBelongsToByJsonApiResource(resource, this, relationshipMeta, options).then(internalModel => handleCompletedRelationshipRequest(this, key, resource._relationship, internalModel, null), e => handleCompletedRelationshipRequest(this, key, resource._relationship, null, e));
    }
    getBelongsTo(key, options) {
      let resource = this._recordData.getBelongsTo(key);
      let identifier = resource && resource.data ? this.store.identifierCache.getOrCreateRecordIdentifier(resource.data) : null;
      let relationshipMeta = this.store._relationshipMetaFor(this.modelName, null, key);
      if (!relationshipMeta) return;
      let store = this.store;
      let async = relationshipMeta.options.async;
      let isAsync = typeof async === 'undefined' ? true : async;
      let _belongsToState = {
        key,
        store,
        originatingInternalModel: this,
        modelName: relationshipMeta.type
      };
      if (isAsync) {
        let internalModel = identifier !== null ? store._internalModelForResource(identifier) : null;
        if (resource._relationship.state.hasFailedLoadAttempt) {
          return this._relationshipProxyCache[key];
        }
        let promise = this._findBelongsTo(key, resource, relationshipMeta, options);
        return this._updatePromiseProxyFor('belongsTo', key, {
          promise,
          content: internalModel ? internalModel.getRecord() : null,
          _belongsToState
        });
      } else {
        if (identifier === null) {
          return null;
        } else {
          let internalModel = store._internalModelForResource(identifier);
          let toReturn = internalModel.getRecord();
          return toReturn;
        }
      }
    }
    getManyArray(key, definition) {
      {
        let manyArray = this._manyArrayCache[key];
        if (!definition) {
          const graphFor = require('@ember-data/record-data/-private').graphFor;
          definition = graphFor(this.store).get(this.identifier, key).definition;
        }
        if (!manyArray) {
          manyArray = ManyArray.create({
            store: this.store,
            type: this.store.modelFor(definition.type),
            recordData: this._recordData,
            key,
            isPolymorphic: definition.isPolymorphic,
            isAsync: definition.isAsync,
            _inverseIsAsync: definition.inverseIsAsync,
            internalModel: this,
            isLoaded: !definition.isAsync
          });
          this._manyArrayCache[key] = manyArray;
        }
        return manyArray;
      }
    }
    fetchAsyncHasMany(key, relationship, manyArray, options) {
      {
        let loadingPromise = this._relationshipPromisesCache[key];
        if (loadingPromise) {
          return loadingPromise;
        }
        const jsonApi = this._recordData.getHasMany(key);
        loadingPromise = this.store._findHasManyByJsonApiResource(jsonApi, this, relationship, options).then(() => handleCompletedRelationshipRequest(this, key, relationship, manyArray, null), e => handleCompletedRelationshipRequest(this, key, relationship, manyArray, e));
        this._relationshipPromisesCache[key] = loadingPromise;
        return loadingPromise;
      }
    }
    getHasMany(key, options) {
      {
        const graphFor = require('@ember-data/record-data/-private').graphFor;
        const relationship = graphFor(this.store).get(this.identifier, key);
        const {
          definition,
          state
        } = relationship;
        let manyArray = this.getManyArray(key, definition);
        if (definition.isAsync) {
          if (state.hasFailedLoadAttempt) {
            return this._relationshipProxyCache[key];
          }
          let promise = this.fetchAsyncHasMany(key, relationship, manyArray, options);
          return this._updatePromiseProxyFor('hasMany', key, {
            promise,
            content: manyArray
          });
        } else {
          return manyArray;
        }
      }
    }
    _updatePromiseProxyFor(kind, key, args) {
      let promiseProxy = this._relationshipProxyCache[key];
      if (kind === 'hasMany') {
        if (promiseProxy) {
          promiseProxy._update(args.promise, args.content);
        } else {
          promiseProxy = this._relationshipProxyCache[key] = new _PromiseManyArray(args.promise, args.content);
        }
        return promiseProxy;
      }
      if (promiseProxy) {
        if (args.content !== undefined) {
          // this usage of `any` can be removed when `@types/ember_object` proxy allows `null` for content
          promiseProxy.set('content', args.content);
        }
        promiseProxy.set('promise', args.promise);
      } else {
        const klass = PromiseBelongsTo;
        // this usage of `any` can be removed when `@types/ember_object` proxy allows `null` for content
        this._relationshipProxyCache[key] = klass.create(args);
      }
      return this._relationshipProxyCache[key];
    }
    reloadHasMany(key, options) {
      {
        let loadingPromise = this._relationshipPromisesCache[key];
        if (loadingPromise) {
          return loadingPromise;
        }
        const graphFor = require('@ember-data/record-data/-private').graphFor;
        const relationship = graphFor(this.store).get(this.identifier, key);
        const {
          definition,
          state
        } = relationship;
        state.hasFailedLoadAttempt = false;
        state.shouldForceReload = true;
        let manyArray = this.getManyArray(key, definition);
        let promise = this.fetchAsyncHasMany(key, relationship, manyArray, options);
        if (this._relationshipProxyCache[key]) {
          return this._updatePromiseProxyFor('hasMany', key, {
            promise
          });
        }
        return promise;
      }
    }
    reloadBelongsTo(key, options) {
      let loadingPromise = this._relationshipPromisesCache[key];
      if (loadingPromise) {
        return loadingPromise;
      }
      let resource = this._recordData.getBelongsTo(key);
      // TODO move this to a public api
      if (resource._relationship) {
        resource._relationship.state.hasFailedLoadAttempt = false;
        resource._relationship.state.shouldForceReload = true;
      }
      let relationshipMeta = this.store._relationshipMetaFor(this.modelName, null, key);
      let promise = this._findBelongsTo(key, resource, relationshipMeta, options);
      if (this._relationshipProxyCache[key]) {
        return this._updatePromiseProxyFor('belongsTo', key, {
          promise
        });
      }
      return promise;
    }
    destroyFromRecordData() {
      if (this._doNotDestroy) {
        this._doNotDestroy = false;
        return;
      }
      this.destroy();
    }
    destroy() {
      this.isDestroying = true;
      if (this._recordReference) {
        this._recordReference.destroy();
      }
      this._recordReference = null;
      let cache = this._manyArrayCache;
      Object.keys(cache).forEach(key => {
        cache[key].destroy();
        delete cache[key];
      });
      if (this.references) {
        cache = this.references;
        Object.keys(cache).forEach(key => {
          cache[key].destroy();
          delete cache[key];
        });
      }
      internalModelFactoryFor(this.store).remove(this);
      this._isDestroyed = true;
    }
    setupData(data) {
      let changedKeys = this._recordData.pushData(data, this.hasRecord);
      if (this.hasRecord) {
        // TODO @runspired should this be going through the notification manager?
        this._record._notifyProperties(changedKeys);
      }
      this.send('pushedData');
    }
    setDirtyHasMany(key, records) {
      return this._recordData.setDirtyHasMany(key, extractRecordDatasFromRecords(records));
    }
    setDirtyBelongsTo(key, value) {
      return this._recordData.setDirtyBelongsTo(key, extractRecordDataFromRecord(value));
    }
    setDirtyAttribute(key, value) {
      if (this.isDeleted()) {
        {
          throw new EmberError__default["default"](`Attempted to set '${key}' on the deleted record ${this}`);
        }
      }
      let currentValue = this._recordData.getAttr(key);
      if (currentValue !== value) {
        this._recordData.setDirtyAttribute(key, value);
        let isDirty = this._recordData.isAttrDirty(key);
        this.send('didSetProperty', {
          name: key,
          isDirty: isDirty
        });
      }
      return value;
    }
    get isDestroyed() {
      return this._isDestroyed;
    }
    get hasRecord() {
      return !!this._record;
    }
    createSnapshot(options) {
      if (options === void 0) {
        options = {};
      }
      return new Snapshot(options, this.identifier, this.store);
    }
    hasChangedAttributes() {
      if (!this.__recordData) {
        // no need to calculate changed attributes when calling `findRecord`
        return false;
      }
      return this._recordData.hasChangedAttributes();
    }
    changedAttributes() {
      if (!this.__recordData) {
        // no need to calculate changed attributes when calling `findRecord`
        return {};
      }
      return this._recordData.changedAttributes();
    }
    adapterWillCommit() {
      this._recordData.willCommit();
      this.send('willCommit');
    }
    adapterDidDirty() {
      this.send('becomeDirty');
    }
    send(name, context) {
      let currentState = this.currentState;
      if (!currentState[name]) {
        this._unhandledEvent(currentState, name, context);
      }
      return currentState[name](this, context);
    }
    notifyHasManyChange(key) {
      if (this.hasRecord) {
        let manyArray = this._manyArrayCache[key];
        let hasPromise = !!this._relationshipPromisesCache[key];
        if (manyArray && hasPromise) {
          // do nothing, we will notify the ManyArray directly
          // once the fetch has completed.
          return;
        }
        this.store._notificationManager.notify(this.identifier, 'relationships', key);
      }
    }
    notifyBelongsToChange(key) {
      if (this.hasRecord) {
        this.store._notificationManager.notify(this.identifier, 'relationships', key);
      }
    }
    notifyPropertyChange(key) {
      if (this.hasRecord) {
        // TODO this should likely *mostly* be the `attributes` bucket
        // but it seems for local mutations we rely on computed updating
        // iteself when set. As we design our own thing we may need to change
        // that.
        this.store._notificationManager.notify(this.identifier, 'property', key);
      }
    }
    notifyStateChange(key) {
      if (this.hasRecord) {
        this.store._notificationManager.notify(this.identifier, 'state');
      }
      if (!key || key === 'isDeletionCommitted') {
        this.store.recordArrayManager.recordDidChange(this.identifier);
      }
    }
    didCreateRecord() {
      this._recordData.clientDidCreate();
    }
    rollbackAttributes() {
      this.store._backburner.join(() => {
        let dirtyKeys = this._recordData.rollbackAttributes();
        if (object.get(this, 'isError')) {
          this.didCleanError();
        }
        this.send('rolledBack');
        if (this._record && dirtyKeys && dirtyKeys.length > 0) {
          this._record._notifyProperties(dirtyKeys);
        }
      });
    }
    transitionTo(name) {
      // POSSIBLE TODO: Remove this code and replace with
      // always having direct reference to state objects

      let pivotName = extractPivotName(name);
      let state = this.currentState;
      let transitionMapId = `${state.stateName}->${name}`;
      do {
        if (state.exit) {
          state.exit(this);
        }
        state = state.parentState;
      } while (!state[pivotName]);
      let setups;
      let enters;
      let i;
      let l;
      let map = TransitionChainMap[transitionMapId];
      if (map) {
        setups = map.setups;
        enters = map.enters;
        state = map.state;
      } else {
        setups = [];
        enters = [];
        let path = splitOnDot(name);
        for (i = 0, l = path.length; i < l; i++) {
          state = state[path[i]];
          if (state.enter) {
            enters.push(state);
          }
          if (state.setup) {
            setups.push(state);
          }
        }
        TransitionChainMap[transitionMapId] = {
          setups,
          enters,
          state
        };
      }
      for (i = 0, l = enters.length; i < l; i++) {
        enters[i].enter(this);
      }
      this.currentState = state;
      if (this.hasRecord && typeof this._record.notifyPropertyChange === 'function') {
        // TODO refactor Model to have all flags pull from the notification manager
        // and for currentState.stateName to be constructed from flag state.
        // Probably just port this work from ember-m3
        // After that we can eliminate this.
        this.notifyStateChange('currentState');
        // this._record.notifyPropertyChange('currentState');
      }
      for (i = 0, l = setups.length; i < l; i++) {
        setups[i].setup(this);
      }
    }
    _unhandledEvent(state, name, context) {
      let errorMessage = 'Attempted to handle event `' + name + '` ';
      errorMessage += 'on ' + String(this) + ' while in state ';
      errorMessage += state.stateName + '. ';
      if (context !== undefined) {
        errorMessage += 'Called with ' + debug.inspect(context) + '.';
      }
      throw new EmberError__default["default"](errorMessage);
    }
    triggerLater() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      if (this._deferredTriggers.push(args) !== 1) {
        return;
      }
      this.store._updateInternalModel(this);
    }
    _triggerDeferredTriggers() {
      //TODO: Before 1.0 we want to remove all the events that happen on the pre materialized record,
      //but for now, we queue up all the events triggered before the record was materialized, and flush
      //them once we have the record
      if (!this.hasRecord) {
        return;
      }
      let triggers = this._deferredTriggers;
      let record = this._record;
      let trigger = record.trigger;
      // TODO Igor make nicer check
      if (trigger && typeof trigger === 'function') {
        for (let i = 0, l = triggers.length; i < l; i++) {
          let eventName = triggers[i];
          trigger.apply(record, eventName);
        }
      }
      triggers.length = 0;
    }
    removeFromInverseRelationships() {
      if (this.__recordData) {
        this.store._backburner.join(() => {
          this._recordData.removeFromInverseRelationships();
        });
      }
    }

    /*
      When a find request is triggered on the store, the user can optionally pass in
      attributes and relationships to be preloaded. These are meant to behave as if they
      came back from the server, except the user obtained them out of band and is informing
      the store of their existence. The most common use case is for supporting client side
      nested URLs, such as `/posts/1/comments/2` so the user can do
      `store.findRecord('comment', 2, { preload: { post: 1 } })` without having to fetch the post.
       Preloaded data can be attributes and relationships passed in either as IDs or as actual
      models.
    */
    preloadData(preload) {
      let jsonPayload = {};
      //TODO(Igor) consider the polymorphic case
      Object.keys(preload).forEach(key => {
        let preloadValue = object.get(preload, key);
        let relationshipMeta = this.modelClass.metaForProperty(key);
        if (relationshipMeta.isRelationship) {
          if (!jsonPayload.relationships) {
            jsonPayload.relationships = {};
          }
          jsonPayload.relationships[key] = this._preloadRelationship(key, preloadValue);
        } else {
          if (!jsonPayload.attributes) {
            jsonPayload.attributes = {};
          }
          jsonPayload.attributes[key] = preloadValue;
        }
      });
      this._recordData.pushData(jsonPayload);
    }
    _preloadRelationship(key, preloadValue) {
      let relationshipMeta = this.modelClass.metaForProperty(key);
      let modelClass = relationshipMeta.type;
      let data;
      if (relationshipMeta.kind === 'hasMany') {
        data = preloadValue.map(value => this._convertPreloadRelationshipToJSON(value, modelClass));
      } else {
        data = this._convertPreloadRelationshipToJSON(preloadValue, modelClass);
      }
      return {
        data
      };
    }
    _convertPreloadRelationshipToJSON(value, modelClass) {
      if (typeof value === 'string' || typeof value === 'number') {
        return {
          type: modelClass,
          id: value
        };
      }
      let internalModel;
      if (value._internalModel) {
        internalModel = value._internalModel;
      } else {
        internalModel = value;
      }
      // TODO IGOR DAVID assert if no id is present
      return {
        type: internalModel.modelName,
        id: internalModel.id
      };
    }

    /*
     * calling `store.setRecordId` is necessary to update
     * the cache index for this record if we have changed.
     *
     * However, since the store is not aware of whether the update
     * is from us (via user set) or from a push of new data
     * it will also call us so that we can notify and update state.
     *
     * When it does so it calls with `fromCache` so that we can
     * short-circuit instead of cycling back.
     *
     * This differs from the short-circuit in the `_isUpdatingId`
     * case in that the the cache can originate the call to setId,
     * so on first entry we will still need to do our own update.
     */
    setId(id, fromCache) {
      if (fromCache === void 0) {
        fromCache = false;
      }
      if (this._isUpdatingId === true) {
        return;
      }
      this._isUpdatingId = true;
      let didChange = id !== this._id;
      this._id = id;
      if (didChange && id !== null) {
        if (!fromCache) {
          this.store.setRecordId(this.modelName, id, this.clientId);
        }
        // internal set of ID to get it to RecordData from DS.Model
        // if we are within create we may not have a recordData yet.
        if (this.__recordData && this._recordData.__setId) {
          this._recordData.__setId(id);
        }
      }
      if (didChange && this.hasRecord) {
        this.store._notificationManager.notify(this.identifier, 'identity');
      }
      this._isUpdatingId = false;
    }
    didError() {}
    didCleanError() {}

    /*
      If the adapter did not return a hash in response to a commit,
      merge the changed attributes and relationships into the existing
      saved data.
    */
    adapterDidCommit(data) {
      this.didCleanError();
      this._recordData.didCommit(data);
      this.send('didCommit');
      this.store.recordArrayManager.recordDidChange(this.identifier);
      if (!data) {
        return;
      }
      this.store._notificationManager.notify(this.identifier, 'attributes');
    }
    hasErrors() {
      if (this._recordData.getErrors) {
        return this._recordData.getErrors(this.identifier).length > 0;
      } else {
        let errors = this.getRecord().errors;
        return errors.length > 0;
      }
    }

    // FOR USE DURING COMMIT PROCESS
    adapterDidInvalidate(parsedErrors, error) {
      // TODO @runspired this should be handled by RecordState
      // and errors should be dirtied but lazily fetch if at
      // all possible. We should only notify errors here.
      let attribute;
      if (error && parsedErrors) {
        if (!this._recordData.getErrors) {
          for (attribute in parsedErrors) {
            if (hasOwnProperty.call(parsedErrors, attribute)) {
              this.getRecord().errors._add(attribute, parsedErrors[attribute]);
            }
          }
        }
        let jsonApiErrors = errorsHashToArray(parsedErrors);
        this.send('becameInvalid');
        if (jsonApiErrors.length === 0) {
          jsonApiErrors = [{
            title: 'Invalid Error',
            detail: '',
            source: {
              pointer: '/data'
            }
          }];
        }
        this._recordData.commitWasRejected(this.identifier, jsonApiErrors);
      } else {
        this.send('becameError');
        this._recordData.commitWasRejected(this.identifier);
      }
    }
    notifyErrorsChange() {
      this.store._notificationManager.notify(this.identifier, 'errors');
    }
    adapterDidError() {
      this.send('becameError');
      this._recordData.commitWasRejected();
    }
    toString() {
      return `<${this.modelName}:${this.id}>`;
    }
    referenceFor(kind, name) {
      let reference = this.references[name];
      if (!reference) {
        const graphFor = require('@ember-data/record-data/-private').graphFor;
        const relationship = graphFor(this.store._storeWrapper).get(this.identifier, name);
        let relationshipKind = relationship.definition.kind;
        let identifierOrInternalModel = this.identifier;
        if (relationshipKind === 'belongsTo') {
          reference = new BelongsToReference(this.store, identifierOrInternalModel, relationship, name);
        } else if (relationshipKind === 'hasMany') {
          reference = new HasManyReference(this.store, identifierOrInternalModel, relationship, name);
        }
        this.references[name] = reference;
      }
      return reference;
    }
  }
  function handleCompletedRelationshipRequest(internalModel, key, relationship, value, error) {
    delete internalModel._relationshipPromisesCache[key];
    relationship.state.shouldForceReload = false;
    const isHasMany = relationship.definition.kind === 'hasMany';
    if (isHasMany) {
      // we don't notify the record property here to avoid refetch
      // only the many array
      value.notify();
    }
    if (error) {
      relationship.state.hasFailedLoadAttempt = true;
      let proxy = internalModel._relationshipProxyCache[key];
      // belongsTo relationships are sometimes unloaded
      // when a load fails, in this case we need
      // to make sure that we aren't proxying
      // to destroyed content
      // for the sync belongsTo reload case there will be no proxy
      // for the async reload case there will be no proxy if the ui
      // has never been accessed
      if (proxy && !isHasMany) {
        if (proxy.content && proxy.content.isDestroying) {
          proxy.set('content', null);
        }
      }
      throw error;
    }
    if (isHasMany) {
      value.set('isLoaded', true);
    }
    relationship.state.hasFailedLoadAttempt = false;
    // only set to not stale if no error is thrown
    relationship.state.isStale = false;
    return value;
  }
  function extractRecordDatasFromRecords(records) {
    return records.map(extractRecordDataFromRecord);
  }
  function extractRecordDataFromRecord(recordOrPromiseRecord) {
    if (!recordOrPromiseRecord) {
      return null;
    }
    if (recordOrPromiseRecord.then) {
      let content = recordOrPromiseRecord.get && recordOrPromiseRecord.get('content');
      return content ? recordDataFor(content) : null;
    }
    return recordDataFor(recordOrPromiseRecord);
  }

  const AvailableShims = new WeakCache$1('');
  AvailableShims._generator = () => {
    return Object.create(null);
  };
  function getShimClass(store, modelName) {
    let shims = AvailableShims.lookup(store);
    let shim = shims[modelName];
    if (shim === undefined) {
      shim = shims[modelName] = new ShimModelClass(store, modelName);
    }
    return shim;
  }
  function mapFromHash(hash) {
    let map = new Map();
    for (let i in hash) {
      if (Object.prototype.hasOwnProperty.call(hash, i)) {
        map.set(i, hash[i]);
      }
    }
    return map;
  }

  // Mimics the static apis of DSModel
  class ShimModelClass {
    // TODO Maybe expose the class here?
    constructor(__store, modelName) {
      this.__store = __store;
      this.modelName = modelName;
    }
    get fields() {
      let attrs = this.__store._attributesDefinitionFor({
        type: this.modelName
      });
      let relationships = this.__store._relationshipsDefinitionFor({
        type: this.modelName
      });
      let fields = new Map();
      Object.keys(attrs).forEach(key => fields.set(key, 'attribute'));
      Object.keys(relationships).forEach(key => fields.set(key, relationships[key].kind));
      return fields;
    }
    get attributes() {
      let attrs = this.__store._attributesDefinitionFor({
        type: this.modelName
      });
      return mapFromHash(attrs);
    }
    get relationshipsByName() {
      let relationships = this.__store._relationshipsDefinitionFor({
        type: this.modelName
      });
      return mapFromHash(relationships);
    }
    eachAttribute(callback, binding) {
      let attrDefs = this.__store._attributesDefinitionFor({
        type: this.modelName
      });
      Object.keys(attrDefs).forEach(key => {
        callback.call(binding, key, attrDefs[key]);
      });
    }
    eachRelationship(callback, binding) {
      let relationshipDefs = this.__store._relationshipsDefinitionFor({
        type: this.modelName
      });
      Object.keys(relationshipDefs).forEach(key => {
        callback.call(binding, key, relationshipDefs[key]);
      });
    }
    eachTransformedAttribute(callback, binding) {
      let relationshipDefs = this.__store._relationshipsDefinitionFor({
        type: this.modelName
      });
      Object.keys(relationshipDefs).forEach(key => {
        if (relationshipDefs[key].type) {
          callback.call(binding, key, relationshipDefs[key]);
        }
      });
    }
  }

  /**
    @module @ember-data/store
  */

  /**
    SnapshotRecordArray is not directly instantiable.
    Instances are provided to consuming application's
    adapters for certain requests.  

    @class SnapshotRecordArray
    @public
  */
  class SnapshotRecordArray {
    /**
      SnapshotRecordArray is not directly instantiable.
      Instances are provided to consuming application's
      adapters and serializers for certain requests.  
       @method constructor
      @private
      @constructor
      @param {RecordArray} recordArray
      @param {Object} meta
      @param options 
     */
    constructor(recordArray, meta, options) {
      if (options === void 0) {
        options = {};
      }
      this._snapshots = void 0;
      this._recordArray = void 0;
      this._type = void 0;
      this.length = void 0;
      this.meta = void 0;
      this.adapterOptions = void 0;
      this.include = void 0;
      /**
        An array of snapshots
        @private
        @property _snapshots
        @type {Array}
      */
      this._snapshots = null;

      /**
        An array of records
        @private
        @property _recordArray
        @type {Array}
      */
      this._recordArray = recordArray;

      /**
        Number of records in the array
         Example
         ```app/adapters/post.js
        import JSONAPIAdapter from '@ember-data/adapter/json-api';
         export default class PostAdapter extends JSONAPIAdapter {
          shouldReloadAll(store, snapshotRecordArray) {
            return !snapshotRecordArray.length;
          }
        });
        ```
         @property length
        @public
        @type {Number}
      */
      this.length = recordArray.get('length');
      this._type = null;

      /**
        Meta objects for the record array.
         Example
         ```app/adapters/post.js
        import JSONAPIAdapter from '@ember-data/adapter/json-api';
         export default class PostAdapter extends JSONAPIAdapter {
          shouldReloadAll(store, snapshotRecordArray) {
            let lastRequestTime = snapshotRecordArray.meta.lastRequestTime;
            let twentyMinutes = 20 * 60 * 1000;
            return Date.now() > lastRequestTime + twentyMinutes;
          }
        });
        ```
         @property meta
        @public
        @type {Object}
      */
      this.meta = meta;

      /**
        A hash of adapter options passed into the store method for this request.
         Example
         ```app/adapters/post.js
        import MyCustomAdapter from './custom-adapter';
         export default class PostAdapter extends MyCustomAdapter {
          findAll(store, type, sinceToken, snapshotRecordArray) {
            if (snapshotRecordArray.adapterOptions.subscribe) {
              // ...
            }
            // ...
          }
        }
        ```
         @property adapterOptions
        @public
        @type {Object}
      */
      this.adapterOptions = options.adapterOptions;

      /**
        The relationships to include for this request.
         Example
         ```app/adapters/application.js
        import Adapter from '@ember-data/adapter';
         export default class ApplicationAdapter extends Adapter {
          findAll(store, type, snapshotRecordArray) {
            let url = `/${type.modelName}?include=${encodeURIComponent(snapshotRecordArray.include)}`;
             return fetch(url).then((response) => response.json())
          }
        }
        ```
         @property include
        @public
        @type {String|Array}
      */
      this.include = options.include;
    }

    /**
      The type of the underlying records for the snapshots in the array, as a Model
      @property type
      @public
      @type {Model}
    */
    get type() {
      return this._type || (this._type = this._recordArray.get('type'));
    }
    /**
      The modelName of the underlying records for the snapshots in the array, as a Model
      @property modelName
      @public
      @type {Model}
    */
    get modelName() {
      return this._recordArray.modelName;
    }

    /**
      Get snapshots of the underlying record array
       Example
       ```app/adapters/post.js
      import JSONAPIAdapter from '@ember-data/adapter/json-api';
       export default class PostAdapter extends JSONAPIAdapter {
        shouldReloadAll(store, snapshotArray) {
          let snapshots = snapshotArray.snapshots();
           return snapshots.any(function(ticketSnapshot) {
            let timeDiff = moment().diff(ticketSnapshot.attr('lastAccessedAt'), 'minutes');
            if (timeDiff > 20) {
              return true;
            } else {
              return false;
            }
          });
        }
      }
      ```
       @method snapshots
      @public
      @return {Array} Array of snapshots
    */
    snapshots() {
      if (this._snapshots !== null) {
        return this._snapshots;
      }
      this._snapshots = this._recordArray._takeSnapshot();
      return this._snapshots;
    }
  }

  function recordForIdentifier(store, identifier) {
    return internalModelFactoryFor(store).lookup(identifier).getRecord();
  }

  /**
    A record array is an array that contains records of a certain modelName. The record
    array materializes records as needed when they are retrieved for the first
    time. You should not create record arrays yourself. Instead, an instance of
    `RecordArray` or its subclasses will be returned by your application's store
    in response to queries.

    This class should not be imported and instantiated by consuming applications.

    @class RecordArray
    @public
    @extends Ember.ArrayProxy
  */

  let RecordArray = ArrayProxy__default["default"].extend({
    init(args) {
      this._super(args);

      /**
        The array of client ids backing the record array. When a
        record is requested from the record array, the record
        for the client id at the same index is materialized, if
        necessary, by the store.
         @property content
        @private
        @type Ember.Array
        */
      this.set('content', this.content || null);

      /**
      The flag to signal a `RecordArray` is finished loading data.
       Example
       ```javascript
      let people = store.peekAll('person');
      people.get('isLoaded'); // true
      ```
       @property isLoaded
      @public
      @type Boolean
      */
      this.isLoaded = this.isLoaded || false;
      /**
      The flag to signal a `RecordArray` is currently loading data.
       Example
       ```javascript
      let people = store.peekAll('person');
      people.get('isUpdating'); // false
      people.update();
      people.get('isUpdating'); // true
      ```
       @property isUpdating
      @public
      @type Boolean
      */
      this.isUpdating = false;

      /**
      The store that created this record array.
       @property store
      @private
      @type Store
      */
      this.store = this.store || null;
      this._updatingPromise = null;
    },
    replace() {
      throw new Error(`The result of a server query (for all ${this.modelName} types) is immutable. To modify contents, use toArray()`);
    },
    /**
     The modelClass represented by this record array.
      @property type
      @public
     @type {subclass of Model}
     */
    type: object.computed('modelName', function () {
      if (!this.modelName) {
        return null;
      }
      return this.store.modelFor(this.modelName);
    }).readOnly(),
    /**
      Retrieves an object from the content by index.
       @method objectAtContent
      @private
      @param {Number} index
      @return {Model} record
    */
    objectAtContent(index) {
      let identifier = object.get(this, 'content').objectAt(index);
      return identifier ? recordForIdentifier(this.store, identifier) : undefined;
    },
    /**
      Used to get the latest version of all of the records in this array
      from the adapter.
       Example
       ```javascript
      let people = store.peekAll('person');
      people.get('isUpdating'); // false
       people.update().then(function() {
        people.get('isUpdating'); // false
      });
       people.get('isUpdating'); // true
      ```
       @method update
      @public
    */
    update() {
      if (object.get(this, 'isUpdating')) {
        return this._updatingPromise;
      }
      this.set('isUpdating', true);
      let updatingPromise = this._update().finally(() => {
        this._updatingPromise = null;
        if (this.get('isDestroying') || this.get('isDestroyed')) {
          return;
        }
        this.set('isUpdating', false);
      });
      this._updatingPromise = updatingPromise;
      return updatingPromise;
    },
    /*
      Update this RecordArray and return a promise which resolves once the update
      is finished.
     */
    _update() {
      return this.store.findAll(this.modelName, {
        reload: true
      });
    },
    /**
      Saves all of the records in the `RecordArray`.
       Example
       ```javascript
      let messages = store.peekAll('message');
      messages.forEach(function(message) {
        message.set('hasBeenSeen', true);
      });
      messages.save();
      ```
       @method save
      @public
      @return {PromiseArray} promise
    */
    save() {
      let promiseLabel = `DS: RecordArray#save ${this.modelName}`;
      let promise = RSVP.Promise.all(this.invoke('save'), promiseLabel).then(() => this, null, 'DS: RecordArray#save return RecordArray');
      return PromiseArray.create({
        promise
      });
    },
    /**
      @method _unregisterFromManager
      @internal
    */
    _unregisterFromManager() {
      this.manager.unregisterRecordArray(this);
    },
    willDestroy() {
      this._unregisterFromManager();
      this._dissociateFromOwnRecords();
      // TODO: we should not do work during destroy:
      //   * when objects are destroyed, they should simply be left to do
      //   * if logic errors do to this, that logic needs to be more careful during
      //    teardown (ember provides isDestroying/isDestroyed) for this reason
      //   * the exception being: if an dominator has a reference to this object,
      //     and must be informed to release e.g. e.g. removing itself from th
      //     recordArrayMananger
      object.set(this, 'content', null);
      object.set(this, 'length', 0);
      this._super(...arguments);
    },
    /**
      @method _createSnapshot
      @private
    */
    _createSnapshot(options) {
      // this is private for users, but public for ember-data internals
      return new SnapshotRecordArray(this, this.get('meta'), options);
    },
    /**
      @method _dissociateFromOwnRecords
      @internal
    */
    _dissociateFromOwnRecords() {
      this.get('content').forEach(identifier => {
        let recordArrays = this.manager.getRecordArraysForIdentifier(identifier);
        if (recordArrays) {
          recordArrays.delete(this);
        }
      });
    },
    /**
      Adds identifiers to the `RecordArray` without duplicates
       @method _pushIdentifiers
      @internal
      @param {StableRecordIdentifier[]} identifiers
    */
    _pushIdentifiers(identifiers) {
      object.get(this, 'content').pushObjects(identifiers);
    },
    /**
      Removes identifiers from the `RecordArray`.
       @method _removeIdentifiers
      @internal
      @param {StableRecordIdentifier[]} identifiers
    */
    _removeIdentifiers(identifiers) {
      object.get(this, 'content').removeObjects(identifiers);
    },
    /**
      @method _takeSnapshot
      @internal
    */
    _takeSnapshot() {
      return object.get(this, 'content').map(identifier => internalModelFactoryFor(this.store).lookup(identifier).createSnapshot());
    }
  });

  /**
    @module @ember-data/store
  */

  /**
    Represents an ordered list of records whose order and membership is
    determined by the adapter. For example, a query sent to the adapter
    may trigger a search on the server, whose results would be loaded
    into an instance of the `AdapterPopulatedRecordArray`.

    This class should not be imported and instantiated by consuming applications.

    ---

    If you want to update the array and get the latest records from the
    adapter, you can invoke [`update()`](AdapterPopulatedRecordArray/methods/update?anchor=update):

    Example

    ```javascript
    // GET /users?isAdmin=true
    store.query('user', { isAdmin: true }).then(function(admins) {

      admins.then(function() {
        console.log(admins.get("length")); // 42
      });

      // somewhere later in the app code, when new admins have been created
      // in the meantime
      //
      // GET /users?isAdmin=true
      admins.update().then(function() {
        admins.get('isUpdating'); // false
        console.log(admins.get("length")); // 123
      });

      admins.get('isUpdating'); // true
    }
    ```

    @class AdapterPopulatedRecordArray
    @public
    @extends RecordArray
  */
  var AdapterPopulatedRecordArray = RecordArray.extend({
    init() {
      this.set('content', this.get('content') || EmberArray.A());
      this._super(...arguments);
      this.query = this.query || null;
      this.links = this.links || null;
    },
    replace() {
      throw new Error(`The result of a server query (on ${this.modelName}) is immutable.`);
    },
    _update() {
      let store = object.get(this, 'store');
      let query = object.get(this, 'query');
      return store._query(this.modelName, query, this);
    },
    _setObjects(identifiersOrInternalModels, payload) {
      // TODO: initial load should not cause change events at all, only
      // subsequent. This requires changing the public api of adapter.query, but
      // hopefully we can do that soon.
      this.get('content').setObjects(identifiersOrInternalModels);
      this.setProperties({
        isLoaded: true,
        isUpdating: false,
        meta: {
          ...payload.meta
        },
        links: {
          ...payload.links
        }
      });
      this.manager._associateWithRecordArray(identifiersOrInternalModels, this);
    },
    /**
      @method _setIdentifiers
      @param {StableRecordIdentifier[]} identifiers
      @param {Object} payload normalized payload
      @private
    */
    _setIdentifiers(identifiers, payload) {
      this._setObjects(identifiers, payload);
    }
  });

  const RecordArraysCache = new WeakCache$1('');
  RecordArraysCache._generator = () => new Set();
  function recordArraysForIdentifier(identifierOrInternalModel) {
    return RecordArraysCache.lookup(identifierOrInternalModel);
  }
  const pendingForIdentifier = new Set([]);
  function getIdentifier(identifierOrInternalModel) {
    let i = identifierOrInternalModel;
    if (!isStableIdentifier(identifierOrInternalModel)) {
      // identifier may actually be an internalModel
      // but during materialization we will get an identifier that
      // has already been removed from the identifiers cache yet
      // so it will not behave as if stable. This is a bug we should fix.
      i = identifierOrInternalModel.identifier || i;
    }
    return i;
  }
  function shouldIncludeInRecordArrays(store, identifier) {
    const cache = internalModelFactoryFor(store);
    const internalModel = cache.peek(identifier);
    if (internalModel === null) {
      return false;
    }
    return !internalModel.isHiddenFromRecordArrays();
  }

  /**
    @class RecordArrayManager
    @internal
  */
  class RecordArrayManager {
    constructor(options) {
      this.store = options.store;
      this.isDestroying = false;
      this.isDestroyed = false;
      this._liveRecordArrays = Object.create(null);
      this._pendingIdentifiers = Object.create(null);
      this._adapterPopulatedRecordArrays = [];
    }

    /**
     * @method getRecordArraysForIdentifier
     * @internal
     * @param {StableIdentifier} param
     * @return {RecordArray} array
     */
    getRecordArraysForIdentifier(identifier) {
      return recordArraysForIdentifier(identifier);
    }
    _flushPendingIdentifiersForModelName(modelName, identifiers) {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let modelsToRemove = [];
      for (let j = 0; j < identifiers.length; j++) {
        let i = identifiers[j];
        // mark identifiers, so they can once again be processed by the
        // recordArrayManager
        pendingForIdentifier.delete(i);
        // build up a set of models to ensure we have purged correctly;
        let isIncluded = shouldIncludeInRecordArrays(this.store, i);
        if (!isIncluded) {
          modelsToRemove.push(i);
        }
      }
      let array = this._liveRecordArrays[modelName];
      if (array) {
        // TODO: skip if it only changed
        // process liveRecordArrays
        updateLiveRecordArray(this.store, array, identifiers);
      }

      // process adapterPopulatedRecordArrays
      if (modelsToRemove.length > 0) {
        removeFromAdapterPopulatedRecordArrays(this.store, modelsToRemove);
      }
    }
    _flush() {
      let pending = this._pendingIdentifiers;
      this._pendingIdentifiers = Object.create(null);
      for (let modelName in pending) {
        this._flushPendingIdentifiersForModelName(modelName, pending[modelName]);
      }
    }
    _syncLiveRecordArray(array, modelName) {
      let pending = this._pendingIdentifiers[modelName];
      let hasPendingChanges = Array.isArray(pending);
      let hasNoPotentialDeletions = !hasPendingChanges || pending.length === 0;
      let map = internalModelFactoryFor(this.store).modelMapFor(modelName);
      let hasNoInsertionsOrRemovals = object.get(map, 'length') === object.get(array, 'length');

      /*
        Ideally the recordArrayManager has knowledge of the changes to be applied to
        liveRecordArrays, and is capable of strategically flushing those changes and applying
        small diffs if desired.  However, until we've refactored recordArrayManager, this dirty
        check prevents us from unnecessarily wiping out live record arrays returned by peekAll.
        */
      if (hasNoPotentialDeletions && hasNoInsertionsOrRemovals) {
        return;
      }
      if (hasPendingChanges) {
        this._flushPendingIdentifiersForModelName(modelName, pending);
        delete this._pendingIdentifiers[modelName];
      }
      let identifiers = this._visibleIdentifiersByType(modelName);
      let modelsToAdd = [];
      for (let i = 0; i < identifiers.length; i++) {
        let identifier = identifiers[i];
        let recordArrays = recordArraysForIdentifier(identifier);
        if (recordArrays.has(array) === false) {
          recordArrays.add(array);
          modelsToAdd.push(identifier);
        }
      }
      if (modelsToAdd.length) {
        array._pushIdentifiers(modelsToAdd);
      }
    }
    _didUpdateAll(modelName) {
      let recordArray = this._liveRecordArrays[modelName];
      if (recordArray) {
        object.set(recordArray, 'isUpdating', false);
      }
    }

    /**
      Get the `RecordArray` for a modelName, which contains all loaded records of
      given modelName.
       @method liveRecordArrayFor
      @internal
      @param {String} modelName
      @return {RecordArray}
    */
    liveRecordArrayFor(modelName) {
      let array = this._liveRecordArrays[modelName];
      if (array) {
        // if the array already exists, synchronize
        this._syncLiveRecordArray(array, modelName);
      } else {
        // if the array is being newly created merely create it with its initial
        // content already set. This prevents unneeded change events.
        let identifiers = this._visibleIdentifiersByType(modelName);
        array = this.createRecordArray(modelName, identifiers);
        this._liveRecordArrays[modelName] = array;
      }
      return array;
    }
    _visibleIdentifiersByType(modelName) {
      let all = internalModelFactoryFor(this.store).modelMapFor(modelName).recordIdentifiers;
      let visible = [];
      for (let i = 0; i < all.length; i++) {
        let identifier = all[i];
        let shouldInclude = shouldIncludeInRecordArrays(this.store, identifier);
        if (shouldInclude) {
          visible.push(identifier);
        }
      }
      return visible;
    }

    /**
      Create a `RecordArray` for a modelName.
       @method createRecordArray
      @internal
      @param {String} modelName
      @param {Array} [identifiers]
      @return {RecordArray}
    */
    createRecordArray(modelName, identifiers) {
      let array = RecordArray.create({
        modelName,
        content: EmberArray.A(identifiers || []),
        store: this.store,
        isLoaded: true,
        manager: this
      });
      if (Array.isArray(identifiers)) {
        this._associateWithRecordArray(identifiers, array);
      }
      return array;
    }

    /**
      Create a `AdapterPopulatedRecordArray` for a modelName with given query.
       @method createAdapterPopulatedRecordArray
      @internal
      @param {String} modelName
      @param {Object} query
      @return {AdapterPopulatedRecordArray}
    */
    createAdapterPopulatedRecordArray(modelName, query, identifiers, payload) {
      let array;
      if (Array.isArray(identifiers)) {
        array = AdapterPopulatedRecordArray.create({
          modelName,
          query: query,
          content: EmberArray.A(identifiers),
          store: this.store,
          manager: this,
          isLoaded: true,
          isUpdating: false,
          meta: {
            ...payload.meta
          },
          links: {
            ...payload.links
          }
        });
        this._associateWithRecordArray(identifiers, array);
      } else {
        array = AdapterPopulatedRecordArray.create({
          modelName,
          query: query,
          content: EmberArray.A(),
          store: this.store,
          manager: this
        });
      }
      this._adapterPopulatedRecordArrays.push(array);
      return array;
    }

    /**
      Unregister a RecordArray.
      So manager will not update this array.
       @method unregisterRecordArray
      @internal
      @param {RecordArray} array
    */
    unregisterRecordArray(array) {
      let modelName = array.modelName;

      // remove from adapter populated record array
      let removedFromAdapterPopulated = removeFromArray(this._adapterPopulatedRecordArrays, array);
      if (!removedFromAdapterPopulated) {
        let liveRecordArrayForType = this._liveRecordArrays[modelName];
        // unregister live record array
        if (liveRecordArrayForType) {
          if (array === liveRecordArrayForType) {
            delete this._liveRecordArrays[modelName];
          }
        }
      }
    }

    /**
     * @method _associateWithRecordArray
     * @internal
     * @param {StableIdentifier} identifiers
     * @param {RecordArray} array
     */
    _associateWithRecordArray(identifiers, array) {
      for (let i = 0, l = identifiers.length; i < l; i++) {
        let identifier = identifiers[i];
        identifier = getIdentifier(identifier);
        let recordArrays = this.getRecordArraysForIdentifier(identifier);
        recordArrays.add(array);
      }
    }

    /**
      @method recordDidChange
      @internal
    */
    recordDidChange(identifier) {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let modelName = identifier.type;
      identifier = getIdentifier(identifier);
      if (pendingForIdentifier.has(identifier)) {
        return;
      }
      pendingForIdentifier.add(identifier);
      let pending = this._pendingIdentifiers;
      let models = pending[modelName] = pending[modelName] || [];
      if (models.push(identifier) !== 1) {
        return;
      }
      runloop._backburner.schedule('actions', this, this._flush);
    }
    willDestroy() {
      Object.keys(this._liveRecordArrays).forEach(modelName => this._liveRecordArrays[modelName].destroy());
      this._adapterPopulatedRecordArrays.forEach(entry => entry.destroy());
      this.isDestroyed = true;
    }
    destroy() {
      this.isDestroying = true;
      runloop._backburner.schedule('actions', this, this.willDestroy);
    }
  }
  function removeFromArray(array, item) {
    let index = array.indexOf(item);
    if (index !== -1) {
      array.splice(index, 1);
      return true;
    }
    return false;
  }
  function updateLiveRecordArray(store, recordArray, identifiers) {
    let identifiersToAdd = [];
    let identifiersToRemove = [];
    for (let i = 0; i < identifiers.length; i++) {
      let identifier = identifiers[i];
      let shouldInclude = shouldIncludeInRecordArrays(store, identifier);
      let recordArrays = recordArraysForIdentifier(identifier);
      if (shouldInclude) {
        if (!recordArrays.has(recordArray)) {
          identifiersToAdd.push(identifier);
          recordArrays.add(recordArray);
        }
      }
      if (!shouldInclude) {
        identifiersToRemove.push(identifier);
        recordArrays.delete(recordArray);
      }
    }
    if (identifiersToAdd.length > 0) {
      recordArray._pushIdentifiers(identifiersToAdd);
    }
    if (identifiersToRemove.length > 0) {
      recordArray._removeIdentifiers(identifiersToRemove);
    }
  }
  function removeFromAdapterPopulatedRecordArrays(store, identifiers) {
    for (let i = 0; i < identifiers.length; i++) {
      removeFromAll(store, identifiers[i]);
    }
  }
  function removeFromAll(store, identifier) {
    identifier = getIdentifier(identifier);
    const recordArrays = recordArraysForIdentifier(identifier);
    recordArrays.forEach(function (recordArray) {
      recordArray._removeIdentifiers([identifier]);
    });
    recordArrays.clear();
  }

  function iterateData(data, fn) {
    if (Array.isArray(data)) {
      return data.map(fn);
    } else {
      return fn(data);
    }
  }

  // sync
  // iterate over records in payload.data
  // for each record
  //   assert that record.relationships[inverse] is either undefined (so we can fix it)
  //     or provide a data: {id, type} that matches the record that requested it
  //   return the relationship data for the parent
  function syncRelationshipDataFromLink(store, payload, parentInternalModel, relationship) {
    // ensure the right hand side (incoming payload) points to the parent record that
    // requested this relationship
    let relationshipData = payload.data ? iterateData(payload.data, (data, index) => {
      const {
        id,
        type
      } = data;
      ensureRelationshipIsSetToParent(data, parentInternalModel, store, relationship);
      return {
        id,
        type
      };
    }) : null;
    const relatedDataHash = {};
    if ('meta' in payload) {
      relatedDataHash.meta = payload.meta;
    }
    if ('links' in payload) {
      relatedDataHash.links = payload.links;
    }
    if ('data' in payload) {
      relatedDataHash.data = relationshipData;
    }

    // now, push the left hand side (the parent record) to ensure things are in sync, since
    // the payload will be pushed with store._push
    const parentPayload = {
      id: parentInternalModel.id,
      type: parentInternalModel.modelName,
      relationships: {
        [relationship.key]: relatedDataHash
      }
    };
    if (!Array.isArray(payload.included)) {
      payload.included = [];
    }
    payload.included.push(parentPayload);
    return payload;
  }
  function ensureRelationshipIsSetToParent(payload, parentInternalModel, store, parentRelationship, index) {
    let {
      id,
      type
    } = payload;
    if (!payload.relationships) {
      payload.relationships = {};
    }
    let {
      relationships
    } = payload;
    let inverse = getInverse(store, parentInternalModel, parentRelationship, type);
    if (inverse) {
      let {
        inverseKey,
        kind
      } = inverse;
      let relationshipData = relationships[inverseKey] && relationships[inverseKey].data;
      if (kind !== 'hasMany' || typeof relationshipData !== 'undefined') {
        relationships[inverseKey] = relationships[inverseKey] || {};
        relationships[inverseKey].data = fixRelationshipData(relationshipData, kind, parentInternalModel);
      }
    }
  }
  function getInverse(store, parentInternalModel, parentRelationship, type) {
    return recordDataFindInverseRelationshipInfo(store, parentInternalModel, parentRelationship, type);
  }
  function recordDataFindInverseRelationshipInfo(_ref, parentInternalModel, parentRelationship, type) {
    let {
      _storeWrapper
    } = _ref;
    let {
      name: lhs_relationshipName
    } = parentRelationship;
    let {
      modelName
    } = parentInternalModel;
    let inverseKey = _storeWrapper.inverseForRelationship(modelName, lhs_relationshipName);
    if (inverseKey) {
      let {
        meta: {
          kind
        }
      } = _storeWrapper.relationshipsDefinitionFor(type)[inverseKey];
      return {
        inverseKey,
        kind
      };
    }
  }
  function fixRelationshipData(relationshipData, relationshipKind, _ref2) {
    let {
      id,
      modelName
    } = _ref2;
    let parentRelationshipData = {
      id,
      type: modelName
    };
    let payload;
    if (relationshipKind === 'hasMany') {
      payload = relationshipData || [];
      if (relationshipData) {
        // these arrays could be massive so this is better than filter
        // Note: this is potentially problematic if type/id are not in the
        // same state of normalization.
        let found = relationshipData.find(v => {
          return v.type === parentRelationshipData.type && v.id === parentRelationshipData.id;
        });
        if (!found) {
          payload.push(parentRelationshipData);
        }
      } else {
        payload.push(parentRelationshipData);
      }
    } else {
      payload = relationshipData || {};
      Object.assign(payload, parentRelationshipData);
    }
    return payload;
  }
  function _findHasMany(adapter, store, internalModel, link, relationship, options) {
    let snapshot = internalModel.createSnapshot(options);
    let modelClass = store.modelFor(relationship.type);
    let useLink = !link || typeof link === 'string';
    let relatedLink = useLink ? link : link.href;
    let promise = adapter.findHasMany(store, snapshot, relatedLink, relationship);
    let label = `DS: Handle Adapter#findHasMany of '${internalModel.modelName}' : '${relationship.type}'`;
    promise = guardDestroyedStore(promise, store, label);
    promise = _guard(promise, _bind(_objectIsAlive, internalModel));
    return promise.then(adapterPayload => {
      let serializer = store.serializerFor(relationship.type);
      let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findHasMany');
      payload = syncRelationshipDataFromLink(store, payload, internalModel, relationship);
      let internalModelArray = store._push(payload);
      return internalModelArray;
    }, null, `DS: Extract payload of '${internalModel.modelName}' : hasMany '${relationship.type}'`);
  }
  function _findBelongsTo(adapter, store, internalModel, link, relationship, options) {
    let snapshot = internalModel.createSnapshot(options);
    let modelClass = store.modelFor(relationship.type);
    let useLink = !link || typeof link === 'string';
    let relatedLink = useLink ? link : link.href;
    let promise = adapter.findBelongsTo(store, snapshot, relatedLink, relationship);
    let label = `DS: Handle Adapter#findBelongsTo of ${internalModel.modelName} : ${relationship.type}`;
    promise = guardDestroyedStore(promise, store, label);
    promise = _guard(promise, _bind(_objectIsAlive, internalModel));
    return promise.then(adapterPayload => {
      let serializer = store.serializerFor(relationship.type);
      let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findBelongsTo');
      if (!payload.data && !payload.links && !payload.meta) {
        return null;
      }
      payload = syncRelationshipDataFromLink(store, payload, internalModel, relationship);
      return store._push(payload);
    }, null, `DS: Extract payload of ${internalModel.modelName} : ${relationship.type}`);
  }
  function _findAll(adapter, store, modelName, options) {
    let modelClass = store.modelFor(modelName); // adapter.findAll depends on the class
    let recordArray = store.peekAll(modelName);
    let snapshotArray = recordArray._createSnapshot(options);
    let promise = RSVP.Promise.resolve().then(() => adapter.findAll(store, modelClass, null, snapshotArray));
    let label = 'DS: Handle Adapter#findAll of ' + modelClass;
    promise = guardDestroyedStore(promise, store, label);
    return promise.then(adapterPayload => {
      let serializer = store.serializerFor(modelName);
      let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findAll');
      store._push(payload);
      store._didUpdateAll(modelName);
      return recordArray;
    }, null, 'DS: Extract payload of findAll ${modelName}');
  }
  function _query(adapter, store, modelName, query, recordArray, options) {
    let modelClass = store.modelFor(modelName); // adapter.query needs the class

    recordArray = recordArray || store.recordArrayManager.createAdapterPopulatedRecordArray(modelName, query);
    let promise = RSVP.Promise.resolve().then(() => adapter.query(store, modelClass, query, recordArray, options));
    let label = `DS: Handle Adapter#query of ${modelName}`;
    promise = guardDestroyedStore(promise, store, label);
    return promise.then(adapterPayload => {
      let serializer = store.serializerFor(modelName);
      let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'query');
      let internalModels = store._push(payload);
      let identifiers = internalModels.map(im => im.identifier);
      if (recordArray) {
        recordArray._setIdentifiers(identifiers, payload);
      } else {
        recordArray = store.recordArrayManager.createAdapterPopulatedRecordArray(modelName, query, identifiers, payload);
      }
      return recordArray;
    }, null, `DS: Extract payload of query ${modelName}`);
  }
  function _queryRecord(adapter, store, modelName, query, options) {
    let modelClass = store.modelFor(modelName); // adapter.queryRecord needs the class
    let promise = RSVP.Promise.resolve().then(() => adapter.queryRecord(store, modelClass, query, options));
    let label = `DS: Handle Adapter#queryRecord of ${modelName}`;
    promise = guardDestroyedStore(promise, store, label);
    return promise.then(adapterPayload => {
      let serializer = store.serializerFor(modelName);
      let payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'queryRecord');
      return store._push(payload);
    }, null, `DS: Extract payload of queryRecord ${modelName}`);
  }

  /**
    @module @ember-data/store
  */

  function metaIsRelationshipDefinition(meta) {
    return typeof meta._inverseKey === 'function';
  }
  let peekGraph;
  {
    let _peekGraph;
    peekGraph = wrapper => {
      _peekGraph = _peekGraph || require('@ember-data/record-data/-private').peekGraph;
      return _peekGraph(wrapper);
    };
  }
  class RecordDataStoreWrapper {
    constructor(_store) {
      this._store = _store;
      this._willNotify = false;
      this._pendingNotifies = new Map();
    }
    get identifierCache() {
      return this._store.identifierCache;
    }
    _scheduleNotification(identifier, key, kind) {
      let pending = this._pendingNotifies.get(identifier);
      if (!pending) {
        pending = new Map();
        this._pendingNotifies.set(identifier, pending);
      }
      pending.set(key, kind);
      if (this._willNotify === true) {
        return;
      }
      this._willNotify = true;
      let backburner = this._store._backburner;
      backburner.schedule('notify', this, this._flushNotifications);
    }
    notifyErrorsChange(type, id, lid) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      let internalModel = internalModelFactoryFor(this._store).peek(identifier);
      if (internalModel) {
        internalModel.notifyErrorsChange();
      }
    }
    _flushNotifications() {
      if (this._willNotify === false) {
        return;
      }
      let pending = this._pendingNotifies;
      this._pendingNotifies = new Map();
      this._willNotify = false;
      const factory = internalModelFactoryFor(this._store);
      pending.forEach((map, identifier) => {
        const internalModel = factory.peek(identifier);
        if (internalModel) {
          map.forEach((kind, key) => {
            if (kind === 'belongsTo') {
              internalModel.notifyBelongsToChange(key);
            } else {
              internalModel.notifyHasManyChange(key);
            }
          });
        }
      });
    }
    attributesDefinitionFor(type) {
      return this._store._attributesDefinitionFor({
        type
      });
    }
    relationshipsDefinitionFor(type) {
      return this._store._relationshipsDefinitionFor({
        type
      });
    }
    inverseForRelationship(type, key) {
      const modelClass = this._store.modelFor(type);
      const definition = this.relationshipsDefinitionFor(type)[key];
      if (!definition) {
        return null;
      }
      if (metaIsRelationshipDefinition(definition)) {
        return definition._inverseKey(this._store, modelClass);
      } else if (definition.options && definition.options.inverse !== undefined) {
        return definition.options.inverse;
      } else {
        return null;
      }
    }
    inverseIsAsyncForRelationship(type, key) {
      const modelClass = this._store.modelFor(type);
      const definition = this.relationshipsDefinitionFor(type)[key];
      if (!definition) {
        return false;
      }
      if (definition.options && definition.options.inverse === null) {
        return false;
      }
      if (definition.inverseIsAsync !== undefined) {
        // TODO do we need to amend the RFC for this prop?
        // else we should add it to the TS interface and document.
        return !!definition.inverseIsAsync;
      } else if (metaIsRelationshipDefinition(definition)) {
        return definition._inverseIsAsync(this._store, modelClass);
      } else {
        return false;
      }
    }
    notifyPropertyChange(type, id, lid, key) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      let internalModel = internalModelFactoryFor(this._store).peek(identifier);
      if (internalModel) {
        internalModel.notifyPropertyChange(key);
      }
    }
    notifyHasManyChange(type, id, lid, key) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      this._scheduleNotification(identifier, key, 'hasMany');
    }
    notifyBelongsToChange(type, id, lid, key) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      this._scheduleNotification(identifier, key, 'belongsTo');
    }
    notifyStateChange(type, id, lid, key) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      let internalModel = internalModelFactoryFor(this._store).peek(identifier);
      if (internalModel) {
        internalModel.notifyStateChange(key);
      }
    }
    recordDataFor(type, id, lid) {
      let identifier;
      let isCreate = false;
      if (!id && !lid) {
        isCreate = true;
        identifier = {
          type
        };
      } else {
        const resource = constructResource(type, id, lid);
        identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      }
      return this._store.recordDataFor(identifier, isCreate);
    }
    setRecordId(type, id, lid) {
      this._store.setRecordId(type, id, lid);
    }
    isRecordInUse(type, id, lid) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      const internalModel = internalModelFactoryFor(this._store).peek(identifier);
      if (!internalModel) {
        return false;
      }
      const record = internalModel._record;
      return record && !(record.isDestroyed || record.isDestroying);
    }
    disconnectRecord(type, id, lid) {
      const resource = constructResource(type, id, lid);
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      {
        let graph = peekGraph(this);
        if (graph) {
          graph.remove(identifier);
        }
      }
      let internalModel = internalModelFactoryFor(this._store).peek(identifier);
      if (internalModel) {
        internalModel.destroyFromRecordData();
      }
    }
  }

  let _RecordData;
  const RECORD_REFERENCES = new WeakCache$1('');

  /**
    The store contains all of the data for records loaded from the server.
    It is also responsible for creating instances of `Model` that wrap
    the individual data for a record, so that they can be bound to in your
    Handlebars templates.

    Define your application's store like this:

    ```app/services/store.js
    import Store from '@ember-data/store';

    export default class MyStore extends Store {}
    ```

    Most Ember.js applications will only have a single `Store` that is
    automatically created by their `Ember.Application`.

    You can retrieve models from the store in several ways. To retrieve a record
    for a specific id, use `Store`'s `findRecord()` method:

    ```javascript
    store.findRecord('person', 123).then(function (person) {
    });
    ```

    By default, the store will talk to your backend using a standard
    REST mechanism. You can customize how the store talks to your
    backend by specifying a custom adapter:

    ```app/adapters/application.js
    import Adapter from '@ember-data/adapter';

    export default class ApplicationAdapter extends Adapter {
    }
    ```

    You can learn more about writing a custom adapter by reading the `Adapter`
    documentation.

    ### Store createRecord() vs. push() vs. pushPayload()

    The store provides multiple ways to create new record objects. They have
    some subtle differences in their use which are detailed below:

    [createRecord](../methods/createRecord?anchor=createRecord) is used for creating new
    records on the client side. This will return a new record in the
    `created.uncommitted` state. In order to persist this record to the
    backend, you will need to call `record.save()`.

    [push](../methods/push?anchor=push) is used to notify Ember Data's store of new or
    updated records that exist in the backend. This will return a record
    in the `loaded.saved` state. The primary use-case for `store#push` is
    to notify Ember Data about record updates (full or partial) that happen
    outside of the normal adapter methods (for example
    [SSE](http://dev.w3.org/html5/eventsource/) or [Web
    Sockets](http://www.w3.org/TR/2009/WD-websockets-20091222/)).

    [pushPayload](../methods/pushPayload?anchor=pushPayload) is a convenience wrapper for
    `store#push` that will deserialize payloads if the
    Serializer implements a `pushPayload` method.

    Note: When creating a new record using any of the above methods
    Ember Data will update `RecordArray`s such as those returned by
    `store#peekAll()` or `store#findAll()`. This means any
    data bindings or computed properties that depend on the RecordArray
    will automatically be synced to include the new or updated record
    values.

    @main @ember-data/store
    @class Store
    @public
    @extends Ember.Service
  */

  class CoreStore extends Service__default["default"] {
    /**
      The default adapter to use to communicate to a backend server or
      other persistence layer. This will be overridden by an application
      adapter if present.
       If you want to specify `app/adapters/custom.js` as a string, do:
       ```js
      import Store from '@ember-data/store';
       export default Store.extend({
        constructor() {
          super(...arguments);
          this.adapter = 'custom';
        }
      }
      ```
       @property adapter
      @public
      @default '-json-api'
      @type {String}
    */

    /**
    This property returns the adapter, after resolving a possible
    string key.
     If the supplied `adapter` was a class, or a String property
    path resolved to a class, this property will instantiate the
    class.
     This property is cacheable, so the same instance of a specified
    adapter class should be used for the lifetime of the store.
     @property defaultAdapter
    @private
    @return Adapter
    */
    /**
      @method init
      @private
    */
    constructor() {
      super(...arguments);
      /**
       * EmberData specific backburner instance
       * @property _backburner
       * @private
       */
      this._backburner = backburner;
      this.recordArrayManager = new RecordArrayManager({
        store: this
      });
      this._adapterCache = Object.create(null);
      this._serializerCache = Object.create(null);
      this._storeWrapper = new RecordDataStoreWrapper(this);
      /*
        Ember Data uses several specialized micro-queues for organizing
        and coalescing similar async work.
         These queues are currently controlled by a flush scheduled into
        ember-data's custom backburner instance.
        */
      // used for coalescing internal model updates
      this._updatedInternalModels = [];
      this.shouldTrackAsyncRequests = false;
      this.generateStackTracesForTrackedRequests = false;
      RECORD_REFERENCES._generator = identifier => {
        return new RecordReference(this, identifier);
      };
      this._fetchManager = new FetchManager(this);
      this._notificationManager = new NotificationManager(this);
      this.__recordDataFor = this.__recordDataFor.bind(this);

      /**
       * Provides access to the IdentifierCache instance
       * for this store.
       *
       * The IdentifierCache can be used to generate or
       * retrieve a stable unique identifier for any resource.
       *
       * @property {IdentifierCache} identifierCache
       * @public
       */
      this.identifierCache = new IdentifierCache();
    }
    getRequestStateService() {
      return this._fetchManager.requestCache;
    }
    _instantiateRecord(internalModel, modelName, recordData, identifier, properties) {
      // assert here
      if (properties !== undefined) {
        if ('id' in properties) {
          internalModel.setId(properties.id);
        }

        // convert relationship Records to RecordDatas before passing to RecordData
        let defs = this._relationshipsDefinitionFor({
          type: modelName
        });
        if (defs !== null) {
          let keys = Object.keys(properties);
          let relationshipValue;
          for (let i = 0; i < keys.length; i++) {
            let prop = keys[i];
            let def = defs[prop];
            if (def !== undefined) {
              if (def.kind === 'hasMany') {
                relationshipValue = extractRecordDatasFromRecords(properties[prop]);
              } else {
                relationshipValue = extractRecordDataFromRecord(properties[prop]);
              }
              properties[prop] = relationshipValue;
            }
          }
        }
      }

      // TODO guard against initRecordOptions no being there
      let createOptions = recordData._initRecordCreateOptions(properties);
      //TODO Igor pass a wrapper instead of RD
      let record = this.instantiateRecord(identifier, createOptions, this.__recordDataFor, this._notificationManager);
      setRecordIdentifier(record, identifier);
      //recordToInternalModelMap.set(record, internalModel);
      return record;
    }
    _internalDeleteRecord(internalModel) {
      internalModel.deleteRecord();
    }

    // FeatureFlagged in the DSModelStore claas
    _attributesDefinitionFor(identifier) {
      return this.getSchemaDefinitionService().attributesDefinitionFor(identifier);
    }
    _relationshipsDefinitionFor(identifier) {
      return this.getSchemaDefinitionService().relationshipsDefinitionFor(identifier);
    }
    registerSchemaDefinitionService(schema) {
      this._schemaDefinitionService = schema;
    }
    getSchemaDefinitionService() {
      return this._schemaDefinitionService;
    }

    // TODO Double check this return value is correct
    _relationshipMetaFor(modelName, id, key) {
      return this._relationshipsDefinitionFor({
        type: modelName
      })[key];
    }

    /**
      Returns the schema for a particular `modelName`.
       When used with Model from @ember-data/model the return is the model class,
      but this is not guaranteed.
       The class of a model might be useful if you want to get a list of all the
      relationship names of the model, see
      [`relationshipNames`](/ember-data/release/classes/Model?anchor=relationshipNames)
      for example.
       @method modelFor
      @public
      @param {String} modelName
      @return {subclass of Model | ShimModelClass}
      */
    modelFor(modelName) {
      return getShimClass(this, modelName);
    }

    // Feature Flagged in DSModelStore
    /**
      Returns whether a ModelClass exists for a given modelName
      This exists for legacy support for the RESTSerializer,
      which due to how it must guess whether a key is a model
      must query for whether a match exists.
       We should investigate an RFC to make this public or removing
      this requirement.
       @method _hasModelFor
      @private
    */
    _hasModelFor(modelName) {
      return this.getSchemaDefinitionService().doesTypeExist(modelName);
    }

    // .....................
    // . CREATE NEW RECORD .
    // .....................

    /**
      Create a new record in the current store. The properties passed
      to this method are set on the newly created record.
       To create a new instance of a `Post`:
       ```js
      store.createRecord('post', {
        title: 'Ember is awesome!'
      });
      ```
       To create a new instance of a `Post` that has a relationship with a `User` record:
       ```js
      let user = this.store.peekRecord('user', 1);
      store.createRecord('post', {
        title: 'Ember is awesome!',
        user: user
      });
      ```
       @method createRecord
      @public
      @param {String} modelName
      @param {Object} inputProperties a hash of properties to set on the
        newly created record.
      @return {Model} record
    */
    createRecord(modelName, inputProperties) {
      //   calls to `createRecord`. The run loop usage here is because we batch the joining and updating
      //   of record-arrays via ember's run loop, not our own.
      //
      //   to remove this, we would need to move to a new `async` API.
      return runloop._backburner.join(() => {
        return this._backburner.join(() => {
          let normalizedModelName = normalizeModelName(modelName);
          let properties = {
            ...inputProperties
          };

          // If the passed properties do not include a primary key,
          // give the adapter an opportunity to generate one. Typically,
          // client-side ID generators will use something like uuid.js
          // to avoid conflicts.

          if (utils.isNone(properties.id)) {
            properties.id = this._generateId(normalizedModelName, properties);
          }

          // Coerce ID to a string
          properties.id = coerceId(properties.id);
          const factory = internalModelFactoryFor(this);
          const internalModel = factory.build({
            type: normalizedModelName,
            id: properties.id
          });
          internalModel.send('loadedData');
          // TODO this exists just to proxy `isNew` to RecordData which is weird
          internalModel.didCreateRecord();
          return internalModel.getRecord(properties);
        });
      });
    }

    /**
      If possible, this method asks the adapter to generate an ID for
      a newly created record.
       @method _generateId
      @private
      @param {String} modelName
      @param {Object} properties from the new record
      @return {String} if the adapter can generate one, an ID
    */
    _generateId(modelName, properties) {
      let adapter = this.adapterFor(modelName);
      if (adapter && adapter.generateIdForRecord) {
        return adapter.generateIdForRecord(this, modelName, properties);
      }
      return null;
    }

    // .................
    // . DELETE RECORD .
    // .................

    /**
      For symmetry, a record can be deleted via the store.
       Example
       ```javascript
      let post = store.createRecord('post', {
        title: 'Ember is awesome!'
      });
       store.deleteRecord(post);
      ```
       @method deleteRecord
      @public
      @param {Model} record
    */
    deleteRecord(record) {
      this._backburner.join(() => {
        let identifier = peekRecordIdentifier(record);
        if (identifier) {
          let internalModel = internalModelFactoryFor(this).peek(identifier);
          if (internalModel) {
            internalModel.deleteRecord();
          }
        }
      });
    }

    /**
      For symmetry, a record can be unloaded via the store.
      This will cause the record to be destroyed and freed up for garbage collection.
       Example
       ```javascript
      store.findRecord('post', 1).then(function(post) {
        store.unloadRecord(post);
      });
      ```
       @method unloadRecord
      @public
      @param {Model} record
    */
    unloadRecord(record) {
      let identifier = peekRecordIdentifier(record);
      if (identifier) {
        let internalModel = internalModelFactoryFor(this).peek(identifier);
        if (internalModel) {
          internalModel.unloadRecord();
        }
      }
    }

    // ................
    // . FIND RECORDS .
    // ................

    /**
      @method find
      @param {String} modelName
      @param {String|Integer} id
      @param {Object} options
      @return {Promise} promise
      @private
    */
    find(modelName, id, options) {
      return this.findRecord(modelName, id);
    }

    /**
      This method returns a record for a given identifier or type and id combination.
       The `findRecord` method will always resolve its promise with the same
      object for a given identifier or type and `id`.
       The `findRecord` method will always return a **promise** that will be
      resolved with the record.
       **Example 1**
       ```app/routes/post.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model({ post_id }) {
          return this.store.findRecord('post', post_id);
        }
      }
      ```
       **Example 2**
       `findRecord` can be called with a single identifier argument instead of the combination
      of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
      the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
       ```app/routes/post.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model({ post_id: id }) {
          return this.store.findRecord({ type: 'post', id });
        }
      }
      ```
       **Example 3**
       If you have previously received an lid via an Identifier for this record, and the record
      has already been assigned an id, you can find the record again using just the lid.
       ```app/routes/post.js
      store.findRecord({ lid });
      ```
       If the record is not yet available, the store will ask the adapter's `findRecord`
      method to retrieve and supply the necessary data. If the record is already present
      in the store, it depends on the reload behavior _when_ the returned promise
      resolves.
       ### Preloading
       You can optionally `preload` specific attributes and relationships that you know of
      by passing them via the passed `options`.
       For example, if your Ember route looks like `/posts/1/comments/2` and your API route
      for the comment also looks like `/posts/1/comments/2` if you want to fetch the comment
      without also fetching the post you can pass in the post to the `findRecord` call:
       ```app/routes/post-comments.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model({ post_id, comment_id: id }) {
          return this.store.findRecord({ type: 'comment', id, { preload: { post: post_id }} });
        }
      }
      ```
       In your adapter you can then access this id without triggering a network request via the
      snapshot:
       ```app/adapters/application.js
      import EmberObject from '@ember/object';
       export default class Adapter extends EmberObject {
         findRecord(store, schema, id, snapshot) {
          let type = schema.modelName;
           if (type === 'comment')
            let postId = snapshot.belongsTo('post', { id: true });
             return fetch(`./posts/${postId}/comments/${id}`)
              .then(response => response.json())
          }
        }
      }
      ```
       This could also be achieved by supplying the post id to the adapter via the adapterOptions
      property on the options hash.
       ```app/routes/post-comments.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model({ post_id, comment_id: id }) {
          return this.store.findRecord({ type: 'comment', id, { adapterOptions: { post: post_id }} });
        }
      }
      ```
       ```app/adapters/application.js
      import EmberObject from '@ember/object';
       export default class Adapter extends EmberObject {
         findRecord(store, schema, id, snapshot) {
          let type = schema.modelName;
           if (type === 'comment')
            let postId = snapshot.adapterOptions.post;
             return fetch(`./posts/${postId}/comments/${id}`)
              .then(response => response.json())
          }
        }
      }
      ```
       If you have access to the post model you can also pass the model itself to preload:
       ```javascript
      let post = await store.findRecord('post', 1);
      let comment = await store.findRecord('comment', 2, { post: myPostModel });
      ```
       ### Reloading
       The reload behavior is configured either via the passed `options` hash or
      the result of the adapter's `shouldReloadRecord`.
       If `{ reload: true }` is passed or `adapter.shouldReloadRecord` evaluates
      to `true`, then the returned promise resolves once the adapter returns
      data, regardless if the requested record is already in the store:
       ```js
      store.push({
        data: {
          id: 1,
          type: 'post',
          revision: 1
        }
      });
       // adapter#findRecord resolves with
      // [
      //   {
      //     id: 1,
      //     type: 'post',
      //     revision: 2
      //   }
      // ]
      store.findRecord('post', 1, { reload: true }).then(function(post) {
        post.get('revision'); // 2
      });
      ```
       If no reload is indicated via the above mentioned ways, then the promise
      immediately resolves with the cached version in the store.
       ### Background Reloading
       Optionally, if `adapter.shouldBackgroundReloadRecord` evaluates to `true`,
      then a background reload is started, which updates the records' data, once
      it is available:
       ```js
      // app/adapters/post.js
      import ApplicationAdapter from "./application";
       export default class PostAdapter extends ApplicationAdapter {
        shouldReloadRecord(store, snapshot) {
          return false;
        },
         shouldBackgroundReloadRecord(store, snapshot) {
          return true;
        }
      });
       // ...
       store.push({
        data: {
          id: 1,
          type: 'post',
          revision: 1
        }
      });
       let blogPost = store.findRecord('post', 1).then(function(post) {
        post.get('revision'); // 1
      });
       // later, once adapter#findRecord resolved with
      // [
      //   {
      //     id: 1,
      //     type: 'post',
      //     revision: 2
      //   }
      // ]
       blogPost.get('revision'); // 2
      ```
       If you would like to force or prevent background reloading, you can set a
      boolean value for `backgroundReload` in the options object for
      `findRecord`.
       ```app/routes/post/edit.js
      import Route from '@ember/routing/route';
       export default class PostEditRoute extends Route {
        model(params) {
          return this.store.findRecord('post', params.post_id, { backgroundReload: false });
        }
      }
      ```
       If you pass an object on the `adapterOptions` property of the options
      argument it will be passed to your adapter via the snapshot
       ```app/routes/post/edit.js
      import Route from '@ember/routing/route';
       export default class PostEditRoute extends Route {
        model(params) {
          return this.store.findRecord('post', params.post_id, {
            adapterOptions: { subscribe: false }
          });
        }
      }
      ```
       ```app/adapters/post.js
      import MyCustomAdapter from './custom-adapter';
       export default class PostAdapter extends MyCustomAdapter {
        findRecord(store, type, id, snapshot) {
          if (snapshot.adapterOptions.subscribe) {
            // ...
          }
          // ...
        }
      }
      ```
       See [peekRecord](../methods/peekRecord?anchor=peekRecord) to get the cached version of a record.
       ### Retrieving Related Model Records
       If you use an adapter such as Ember's default
      [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
      that supports the [JSON API specification](http://jsonapi.org/) and if your server
      endpoint supports the use of an
      ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
      you can use `findRecord()` or `findAll()` to automatically retrieve additional records related to
      the one you request by supplying an `include` parameter in the `options` object.
       For example, given a `post` model that has a `hasMany` relationship with a `comment`
      model, when we retrieve a specific post we can have the server also return that post's
      comments in the same request:
       ```app/routes/post.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model(params) {
          return this.store.findRecord('post', params.post_id, { include: 'comments' });
        }
      }
      ```
       ```app/adapters/application.js
      import EmberObject from '@ember/object';
       export default class Adapter extends EmberObject {
         findRecord(store, schema, id, snapshot) {
          let type = schema.modelName;
           if (type === 'post')
            let includes = snapshot.adapterOptions.include;
             return fetch(`./posts/${postId}?include=${includes}`)
              .then(response => response.json())
          }
        }
      }
      ```
       In this case, the post's comments would then be available in your template as
      `model.comments`.
       Multiple relationships can be requested using an `include` parameter consisting of a
      comma-separated list (without white-space) while nested relationships can be specified
      using a dot-separated sequence of relationship names. So to request both the post's
      comments and the authors of those comments the request would look like this:
       ```app/routes/post.js
      import Route from '@ember/routing/route';
       export default class PostRoute extends Route {
        model(params) {
          return this.store.findRecord('post', params.post_id, { include: 'comments,comments.author' });
        }
      }
      ```
       ### Retrieving Specific Fields by Type
       If your server endpoint supports the use of a ['fields' query parameter](https://jsonapi.org/format/#fetching-sparse-fieldsets),
      you can use pass those fields through to your server.  At this point in time, this requires a few manual steps on your part.
       1. Implement `buildQuery` in your adapter.
       ```app/adapters/application.js
      buildQuery(snapshot) {
        let query = super.buildQuery(...arguments);
         let { fields } = snapshot.adapterOptions;
         if (fields) {
          query.fields = fields;
        }
         return query;
      }
      ```
       2. Then pass through the applicable fields to your `findRecord` request.
       Given a `post` model with attributes body, title, publishDate and meta, you can retrieve a filtered list of attributes.
       ```app/routes/post.js
      import Route from '@ember/routing/route';
      export default Route.extend({
        model(params) {
          return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title' } });
        }
      });
      ```
       Moreover, you can filter attributes on related models as well. If a `post` has a `belongsTo` relationship to a user,
      just include the relationship key and attributes.
       ```app/routes/post.js
      import Route from '@ember/routing/route';
      export default Route.extend({
        model(params) {
          return this.store.findRecord('post', params.post_id, { adapterOptions: { fields: { post: 'body,title', user: 'name,email' } });
        }
      });
      ```
       @since 1.13.0
      @method findRecord
      @public
      @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
      @param {(String|Integer|Object)} id - optional object with options for the request only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved
      @param {Object} [options] - if the first param is a string this will be the optional options for the request. See examples for available options.
      @return {Promise} promise
    */

    findRecord(resource, id, options) {
      if (isMaybeIdentifier(resource)) {
        options = id;
      } else {
        const type = normalizeModelName(resource);
        const normalizedId = ensureStringId(id);
        resource = constructResource(type, normalizedId);
      }
      const internalModel = internalModelFactoryFor(this).lookup(resource);
      options = options || {};
      if (!internalModel.currentState.isLoaded) {
        return this._findByInternalModel(internalModel, options);
      }
      let fetchedInternalModel = this._findRecord(internalModel, options);
      return promiseRecord(fetchedInternalModel, `DS: Store#findRecord ${internalModel.identifier}`);
    }
    _findRecord(internalModel, options) {
      // Refetch if the reload option is passed
      if (options.reload) {
        return this._scheduleFetch(internalModel, options);
      }
      let snapshot = internalModel.createSnapshot(options);
      let adapter = this.adapterFor(internalModel.modelName);

      // Refetch the record if the adapter thinks the record is stale
      if (typeof options.reload === 'undefined' && adapter.shouldReloadRecord && adapter.shouldReloadRecord(this, snapshot)) {
        return this._scheduleFetch(internalModel, options);
      }
      if (options.backgroundReload === false) {
        return RSVP.Promise.resolve(internalModel);
      }

      // Trigger the background refetch if backgroundReload option is passed
      if (options.backgroundReload || !adapter.shouldBackgroundReloadRecord || adapter.shouldBackgroundReloadRecord(this, snapshot)) {
        this._scheduleFetch(internalModel, options);
      }

      // Return the cached record
      return RSVP.Promise.resolve(internalModel);
    }
    _findByInternalModel(internalModel, options) {
      if (options === void 0) {
        options = {};
      }
      if (options.preload) {
        this._backburner.join(() => {
          internalModel.preloadData(options.preload);
        });
      }
      let fetchedInternalModel = this._findEmptyInternalModel(internalModel, options);
      return promiseRecord(fetchedInternalModel, `DS: Store#findRecord ${internalModel.modelName} with id: ${internalModel.id}`);
    }
    _findEmptyInternalModel(internalModel, options) {
      if (internalModel.currentState.isEmpty) {
        return this._scheduleFetch(internalModel, options);
      }
      if (internalModel.currentState.isLoading) {
        let pendingRequest = this._fetchManager.getPendingFetch(internalModel.identifier, options);
        if (pendingRequest) {
          return pendingRequest.then(() => RSVP.Promise.resolve(internalModel));
        }
        return this._scheduleFetch(internalModel, options);
      }
      return RSVP.Promise.resolve(internalModel);
    }

    /**
      This method makes a series of requests to the adapter's `find` method
      and returns a promise that resolves once they are all loaded.
       @private
      @method findByIds
      @param {String} modelName
      @param {Array} ids
      @return {Promise} promise
    */
    findByIds(modelName, ids) {
      let promises = new Array(ids.length);
      let normalizedModelName = normalizeModelName(modelName);
      for (let i = 0; i < ids.length; i++) {
        promises[i] = this.findRecord(normalizedModelName, ids[i]);
      }
      return promiseArray(RSVP.all(promises).then(EmberArray.A, null, `DS: Store#findByIds of ${normalizedModelName} complete`));
    }
    _scheduleFetchMany(internalModels, options) {
      let fetches = new Array(internalModels.length);
      for (let i = 0; i < internalModels.length; i++) {
        fetches[i] = this._scheduleFetch(internalModels[i], options);
      }
      return RSVP.Promise.all(fetches);
    }
    _scheduleFetch(internalModel, options) {
      if (options === void 0) {
        options = {};
      }
      let generateStackTrace = this.generateStackTracesForTrackedRequests;
      // TODO  remove this once we don't rely on state machine
      internalModel.send('loadingData');
      let identifier = internalModel.identifier;
      let promise = this._fetchManager.scheduleFetch(identifier, options, generateStackTrace);
      return promise.then(payload => {
        // ensure that regardless of id returned we assign to the correct record
        if (payload.data && !Array.isArray(payload.data)) {
          payload.data.lid = identifier.lid;
        }

        // Returning this._push here, breaks typing but not any tests, investigate potential missing tests
        let potentiallyNewIm = this._push(payload);
        if (potentiallyNewIm && !Array.isArray(potentiallyNewIm)) {
          return potentiallyNewIm;
        } else {
          return internalModel;
        }
      }, error => {
        // TODO  remove this once we don't rely on state machine
        internalModel.send('notFound');
        if (internalModel.currentState.isEmpty) {
          internalModel.unloadRecord();
        }
        throw error;
      });
    }

    /**
      Get the reference for the specified record.
       Example
       ```javascript
      let userRef = store.getReference('user', 1);
       // check if the user is loaded
      let isLoaded = userRef.value() !== null;
       // get the record of the reference (null if not yet available)
      let user = userRef.value();
       // get the identifier of the reference
      if (userRef.remoteType() === 'id') {
      let id = userRef.id();
      }
       // load user (via store.find)
      userRef.load().then(...)
       // or trigger a reload
      userRef.reload().then(...)
       // provide data for reference
      userRef.push({ id: 1, username: '@user' }).then(function(user) {
        userRef.value() === user;
      });
      ```
       @method getReference
      @public
      @param {String|object} resource - modelName (string) or Identifier (object)
      @param {String|Integer} id
      @since 2.5.0
      @return {RecordReference}
    */
    getReference(resource, id) {
      let resourceIdentifier;
      if (arguments.length === 1 && isMaybeIdentifier(resource)) {
        resourceIdentifier = resource;
      } else {
        const type = normalizeModelName(resource);
        const normalizedId = ensureStringId(id);
        resourceIdentifier = constructResource(type, normalizedId);
      }
      let identifier = this.identifierCache.getOrCreateRecordIdentifier(resourceIdentifier);
      if (identifier) {
        return RECORD_REFERENCES.lookup(identifier);
      }
    }

    /**
      Get a record by a given type and ID without triggering a fetch.
       This method will synchronously return the record if it is available in the store,
      otherwise it will return `null`. A record is available if it has been fetched earlier, or
      pushed manually into the store.
       See [findRecord](../methods/findRecord?anchor=findRecord) if you would like to request this record from the backend.
       _Note: This is a synchronous method and does not return a promise._
       **Example 1**
       ```js
      let post = store.peekRecord('post', 1);
       post.id; // 1
      ```
       `peekRecord` can be called with a single identifier argument instead of the combination
      of `type` (modelName) and `id` as separate arguments. You may recognize this combo as
      the typical pairing from [JSON:API](https://jsonapi.org/format/#document-resource-object-identification)
       **Example 2**
       ```js
      let post = store.peekRecord({ type: 'post', id });
      post.id; // 1
      ```
       If you have previously received an lid from an Identifier for this record, you can lookup the record again using
      just the lid.
       **Example 3**
       ```js
      let post = store.peekRecord({ lid });
      post.id; // 1
      ```
        @since 1.13.0
      @method peekRecord
      @public
      @param {String|object} modelName - either a string representing the modelName or a ResourceIdentifier object containing both the type (a string) and the id (a string) for the record or an lid (a string) of an existing record
      @param {String|Integer} id - optional only if the first param is a ResourceIdentifier, else the string id of the record to be retrieved.
      @return {Model|null} record
    */

    peekRecord(identifier, id) {
      if (arguments.length === 1 && isMaybeIdentifier(identifier)) {
        let stableIdentifier = this.identifierCache.peekRecordIdentifier(identifier);
        if (stableIdentifier) {
          return internalModelFactoryFor(this).peek(stableIdentifier)?.getRecord() || null;
        }
        return null;
      }
      const type = normalizeModelName(identifier);
      const normalizedId = ensureStringId(id);
      if (this.hasRecordForId(type, normalizedId)) {
        const resource = constructResource(type, normalizedId);
        return internalModelFactoryFor(this).lookup(resource).getRecord();
      } else {
        return null;
      }
    }

    /**
      This method is called by the record's `reload` method.
       This method calls the adapter's `find` method, which returns a promise. When
      **that** promise resolves, `_reloadRecord` will resolve the promise returned
      by the record's `reload`.
       @method _reloadRecord
      @private
      @param {Model} internalModel
      @param options optional to include adapterOptions
      @return {Promise} promise
    */
    _reloadRecord(internalModel, options) {
      options.isReloading = true;
      let {
        id,
        modelName
      } = internalModel;
      this.adapterFor(modelName);
      return this._scheduleFetch(internalModel, options);
    }

    /**
     This method returns true if a record for a given modelName and id is already
     loaded in the store. Use this function to know beforehand if a findRecord()
     will result in a request or that it will be a cache hit.
      Example
      ```javascript
     store.hasRecordForId('post', 1); // false
     store.findRecord('post', 1).then(function() {
       store.hasRecordForId('post', 1); // true
     });
     ```
       @method hasRecordForId
      @public
      @param {String} modelName
      @param {(String|Integer)} id
      @return {Boolean}
    */
    hasRecordForId(modelName, id) {
      const type = normalizeModelName(modelName);
      const trueId = ensureStringId(id);
      const resource = {
        type,
        id: trueId
      };
      const identifier = this.identifierCache.peekRecordIdentifier(resource);
      const internalModel = identifier && internalModelFactoryFor(this).peek(identifier);
      return !!internalModel && internalModel.currentState.isLoaded;
    }

    /**
      Returns id record for a given type and ID. If one isn't already loaded,
      it builds a new record and leaves it in the `empty` state.
       @method recordForId
      @private
      @param {String} modelName
      @param {(String|Integer)} id
      @return {Model} record
    */
    recordForId(modelName, id) {
      const resource = constructResource(modelName, ensureStringId(id));
      return internalModelFactoryFor(this).lookup(resource).getRecord();
    }

    /**
      @method findMany
      @private
      @param {Array} internalModels
      @return {Promise} promise
    */
    findMany(internalModels, options) {
      let finds = new Array(internalModels.length);
      for (let i = 0; i < internalModels.length; i++) {
        finds[i] = this._findEmptyInternalModel(internalModels[i], options);
      }
      return RSVP.Promise.all(finds);
    }

    /**
      If a relationship was originally populated by the adapter as a link
      (as opposed to a list of IDs), this method is called when the
      relationship is fetched.
       The link (which is usually a URL) is passed through unchanged, so the
      adapter can make whatever request it wants.
       The usual use-case is for the server to register a URL as a link, and
      then use that URL in the future to make a request for the relationship.
       @method findHasMany
      @private
      @param {InternalModel} internalModel
      @param {any} link
      @param {(Relationship)} relationship
      @return {Promise} promise
    */
    findHasMany(internalModel, link, relationship, options) {
      let adapter = this.adapterFor(internalModel.modelName);
      return _findHasMany(adapter, this, internalModel, link, relationship, options);
    }
    _findHasManyByJsonApiResource(resource, parentInternalModel, relationship, options) {
      {
        if (!resource) {
          return RSVP.resolve([]);
        }
        const {
          definition,
          state
        } = relationship;
        let adapter = this.adapterFor(definition.type);
        let {
          isStale,
          hasDematerializedInverse,
          hasReceivedData,
          isEmpty,
          shouldForceReload
        } = state;
        const allInverseRecordsAreLoaded = areAllInverseRecordsLoaded(this, resource);
        let shouldFindViaLink = resource.links && resource.links.related && (typeof adapter.findHasMany === 'function' || typeof resource.data === 'undefined') && (shouldForceReload || hasDematerializedInverse || isStale || !allInverseRecordsAreLoaded && !isEmpty);

        // fetch via link
        if (shouldFindViaLink) {
          // findHasMany, although not public, does not need to care about our upgrade relationship definitions
          // and can stick with the public definition API for now.
          const relationshipMeta = this._storeWrapper.relationshipsDefinitionFor(definition.inverseType)[definition.key];
          return this.findHasMany(parentInternalModel, resource.links.related, relationshipMeta, options);
        }
        let preferLocalCache = hasReceivedData && !isEmpty;
        let hasLocalPartialData = hasDematerializedInverse || isEmpty && Array.isArray(resource.data) && resource.data.length > 0;

        // fetch using data, pulling from local cache if possible
        if (!shouldForceReload && !isStale && (preferLocalCache || hasLocalPartialData)) {
          let internalModels = resource.data.map(json => this._internalModelForResource(json));
          return this.findMany(internalModels, options);
        }
        let hasData = hasReceivedData && !isEmpty;

        // fetch by data
        if (hasData || hasLocalPartialData) {
          let internalModels = resource.data.map(json => this._internalModelForResource(json));
          return this._scheduleFetchMany(internalModels, options);
        }

        // we were explicitly told we have no data and no links.
        //   TODO if the relationshipIsStale, should we hit the adapter anyway?
        return RSVP.resolve([]);
      }
    }

    /**
      @method findBelongsTo
      @private
      @param {InternalModel} internalModel
      @param {any} link
      @param {Relationship} relationship
      @return {Promise} promise
    */
    findBelongsTo(internalModel, link, relationship, options) {
      let adapter = this.adapterFor(internalModel.modelName);
      return _findBelongsTo(adapter, this, internalModel, link, relationship, options);
    }
    _fetchBelongsToLinkFromResource(resource, parentInternalModel, relationshipMeta, options) {
      if (!resource || !resource.links || !resource.links.related) {
        // should we warn here, not sure cause its an internal method
        return RSVP.resolve(null);
      }
      return this.findBelongsTo(parentInternalModel, resource.links.related, relationshipMeta, options).then(internalModel => {
        return internalModel ? internalModel.getRecord() : null;
      });
    }
    _findBelongsToByJsonApiResource(resource, parentInternalModel, relationshipMeta, options) {
      if (!resource) {
        return RSVP.resolve(null);
      }
      const internalModel = resource.data ? this._internalModelForResource(resource.data) : null;
      let {
        isStale,
        hasDematerializedInverse,
        hasReceivedData,
        isEmpty,
        shouldForceReload
      } = resource._relationship.state;
      const allInverseRecordsAreLoaded = areAllInverseRecordsLoaded(this, resource);
      let shouldFindViaLink = resource.links && resource.links.related && (shouldForceReload || hasDematerializedInverse || isStale || !allInverseRecordsAreLoaded && !isEmpty);
      if (internalModel) {
        // short circuit if we are already loading
        let pendingRequest = this._fetchManager.getPendingFetch(internalModel.identifier, options);
        if (pendingRequest) {
          return pendingRequest.then(() => internalModel.getRecord());
        }
      }

      // fetch via link
      if (shouldFindViaLink) {
        return this._fetchBelongsToLinkFromResource(resource, parentInternalModel, relationshipMeta, options);
      }
      let preferLocalCache = hasReceivedData && allInverseRecordsAreLoaded && !isEmpty;
      let hasLocalPartialData = hasDematerializedInverse || isEmpty && resource.data;
      // null is explicit empty, undefined is "we don't know anything"
      let localDataIsEmpty = resource.data === undefined || resource.data === null;

      // fetch using data, pulling from local cache if possible
      if (!shouldForceReload && !isStale && (preferLocalCache || hasLocalPartialData)) {
        /*
          We have canonical data, but our local state is empty
         */
        if (localDataIsEmpty) {
          return RSVP.resolve(null);
        }
        return this._findByInternalModel(internalModel, options);
      }
      let resourceIsLocal = !localDataIsEmpty && resource.data.id === null;
      if (internalModel && resourceIsLocal) {
        return RSVP.resolve(internalModel.getRecord());
      }

      // fetch by data
      if (internalModel && !localDataIsEmpty) {
        return this._scheduleFetch(internalModel, options).then(() => {
          return internalModel.getRecord();
        });
      }

      // we were explicitly told we have no data and no links.
      //   TODO if the relationshipIsStale, should we hit the adapter anyway?
      return RSVP.resolve(null);
    }

    /**
      This method delegates a query to the adapter. This is the one place where
      adapter-level semantics are exposed to the application.
       Each time this method is called a new request is made through the adapter.
       Exposing queries this way seems preferable to creating an abstract query
      language for all server-side queries, and then require all adapters to
      implement them.
       ---
       If you do something like this:
       ```javascript
      store.query('person', { page: 1 });
      ```
       The request made to the server will look something like this:
       ```
      GET "/api/v1/person?page=1"
      ```
       ---
       If you do something like this:
       ```javascript
      store.query('person', { ids: [1, 2, 3] });
      ```
       The request made to the server will look something like this:
       ```
      GET "/api/v1/person?ids%5B%5D=1&ids%5B%5D=2&ids%5B%5D=3"
      decoded: "/api/v1/person?ids[]=1&ids[]=2&ids[]=3"
      ```
       This method returns a promise, which is resolved with an
      [`AdapterPopulatedRecordArray`](/ember-data/release/classes/AdapterPopulatedRecordArray)
      once the server returns.
       @since 1.13.0
      @method query
      @public
      @param {String} modelName
      @param {any} query an opaque query to be used by the adapter
      @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.query
      @return {Promise} promise
    */
    query(modelName, query, options) {
      let adapterOptionsWrapper = {};
      if (options && options.adapterOptions) {
        adapterOptionsWrapper.adapterOptions = options.adapterOptions;
      }
      let normalizedModelName = normalizeModelName(modelName);
      return this._query(normalizedModelName, query, null, adapterOptionsWrapper);
    }
    _query(modelName, query, array, options) {
      let adapter = this.adapterFor(modelName);
      return promiseArray(_query(adapter, this, modelName, query, array, options));
    }

    /**
      This method makes a request for one record, where the `id` is not known
      beforehand (if the `id` is known, use [`findRecord`](../methods/findRecord?anchor=findRecord)
      instead).
       This method can be used when it is certain that the server will return a
      single object for the primary data.
       Each time this method is called a new request is made through the adapter.
       Let's assume our API provides an endpoint for the currently logged in user
      via:
       ```
      // GET /api/current_user
      {
        user: {
          id: 1234,
          username: 'admin'
        }
      }
      ```
       Since the specific `id` of the `user` is not known beforehand, we can use
      `queryRecord` to get the user:
       ```javascript
      store.queryRecord('user', {}).then(function(user) {
        let username = user.get('username');
        console.log(`Currently logged in as ${username}`);
      });
      ```
       The request is made through the adapters' `queryRecord`:
       ```app/adapters/user.js
      import Adapter from '@ember-data/adapter';
      import $ from 'jquery';
       export default class UserAdapter extends Adapter {
        queryRecord(modelName, query) {
          return $.getJSON('/api/current_user');
        }
      }
      ```
       Note: the primary use case for `store.queryRecord` is when a single record
      is queried and the `id` is not known beforehand. In all other cases
      `store.query` and using the first item of the array is likely the preferred
      way:
       ```
      // GET /users?username=unique
      {
        data: [{
          id: 1234,
          type: 'user',
          attributes: {
            username: "unique"
          }
        }]
      }
      ```
       ```javascript
      store.query('user', { username: 'unique' }).then(function(users) {
        return users.get('firstObject');
      }).then(function(user) {
        let id = user.get('id');
      });
      ```
       This method returns a promise, which resolves with the found record.
       If the adapter returns no data for the primary data of the payload, then
      `queryRecord` resolves with `null`:
       ```
      // GET /users?username=unique
      {
        data: null
      }
      ```
       ```javascript
      store.queryRecord('user', { username: 'unique' }).then(function(user) {
        console.log(user); // null
      });
      ```
       @since 1.13.0
      @method queryRecord
      @public
      @param {String} modelName
      @param {any} query an opaque query to be used by the adapter
      @param {Object} options optional, may include `adapterOptions` hash which will be passed to adapter.queryRecord
      @return {Promise} promise which resolves with the found record or `null`
    */
    queryRecord(modelName, query, options) {
      let normalizedModelName = normalizeModelName(modelName);
      let adapter = this.adapterFor(normalizedModelName);
      let adapterOptionsWrapper = {};
      if (options && options.adapterOptions) {
        adapterOptionsWrapper.adapterOptions = options.adapterOptions;
      }
      return promiseObject(_queryRecord(adapter, this, normalizedModelName, query, adapterOptionsWrapper).then(internalModel => {
        // the promise returned by store.queryRecord is expected to resolve with
        // an instance of Model
        if (internalModel) {
          return internalModel.getRecord();
        }
        return null;
      }));
    }

    /**
      `findAll` asks the adapter's `findAll` method to find the records for the
      given type, and returns a promise which will resolve with all records of
      this type present in the store, even if the adapter only returns a subset
      of them.
       ```app/routes/authors.js
      import Route from '@ember/routing/route';
       export default class AuthorsRoute extends Route {
        model(params) {
          return this.store.findAll('author');
        }
      }
      ```
       _When_ the returned promise resolves depends on the reload behavior,
      configured via the passed `options` hash and the result of the adapter's
      `shouldReloadAll` method.
       ### Reloading
       If `{ reload: true }` is passed or `adapter.shouldReloadAll` evaluates to
      `true`, then the returned promise resolves once the adapter returns data,
      regardless if there are already records in the store:
       ```js
      store.push({
        data: {
          id: 'first',
          type: 'author'
        }
      });
       // adapter#findAll resolves with
      // [
      //   {
      //     id: 'second',
      //     type: 'author'
      //   }
      // ]
      store.findAll('author', { reload: true }).then(function(authors) {
        authors.getEach('id'); // ['first', 'second']
      });
      ```
       If no reload is indicated via the above mentioned ways, then the promise
      immediately resolves with all the records currently loaded in the store.
       ### Background Reloading
       Optionally, if `adapter.shouldBackgroundReloadAll` evaluates to `true`,
      then a background reload is started. Once this resolves, the array with
      which the promise resolves, is updated automatically so it contains all the
      records in the store:
       ```app/adapters/application.js
      import Adapter from '@ember-data/adapter';
       export default class ApplicationAdapter extends Adapter {
        shouldReloadAll(store, snapshotsArray) {
          return false;
        },
         shouldBackgroundReloadAll(store, snapshotsArray) {
          return true;
        }
      });
       // ...
       store.push({
        data: {
          id: 'first',
          type: 'author'
        }
      });
       let allAuthors;
      store.findAll('author').then(function(authors) {
        authors.getEach('id'); // ['first']
         allAuthors = authors;
      });
       // later, once adapter#findAll resolved with
      // [
      //   {
      //     id: 'second',
      //     type: 'author'
      //   }
      // ]
       allAuthors.getEach('id'); // ['first', 'second']
      ```
       If you would like to force or prevent background reloading, you can set a
      boolean value for `backgroundReload` in the options object for
      `findAll`.
       ```app/routes/post/edit.js
      import Route from '@ember/routing/route';
       export default class PostEditRoute extends Route {
        model() {
          return this.store.findAll('post', { backgroundReload: false });
        }
      }
      ```
       If you pass an object on the `adapterOptions` property of the options
      argument it will be passed to you adapter via the `snapshotRecordArray`
       ```app/routes/posts.js
      import Route from '@ember/routing/route';
       export default class PostsRoute extends Route {
        model(params) {
          return this.store.findAll('post', {
            adapterOptions: { subscribe: false }
          });
        }
      }
      ```
       ```app/adapters/post.js
      import MyCustomAdapter from './custom-adapter';
       export default class UserAdapter extends MyCustomAdapter {
        findAll(store, type, sinceToken, snapshotRecordArray) {
          if (snapshotRecordArray.adapterOptions.subscribe) {
            // ...
          }
          // ...
        }
      }
      ```
       See [peekAll](../methods/peekAll?anchor=peekAll) to get an array of current records in the
      store, without waiting until a reload is finished.
       ### Retrieving Related Model Records
       If you use an adapter such as Ember's default
      [`JSONAPIAdapter`](/ember-data/release/classes/JSONAPIAdapter)
      that supports the [JSON API specification](http://jsonapi.org/) and if your server
      endpoint supports the use of an
      ['include' query parameter](http://jsonapi.org/format/#fetching-includes),
      you can use `findAll()` to automatically retrieve additional records related to
      those requested by supplying an `include` parameter in the `options` object.
       For example, given a `post` model that has a `hasMany` relationship with a `comment`
      model, when we retrieve all of the post records we can have the server also return
      all of the posts' comments in the same request:
       ```app/routes/posts.js
      import Route from '@ember/routing/route';
       export default class PostsRoute extends Route {
        model() {
          return this.store.findAll('post', { include: 'comments' });
        }
      }
      ```
      Multiple relationships can be requested using an `include` parameter consisting of a
      comma-separated list (without white-space) while nested relationships can be specified
      using a dot-separated sequence of relationship names. So to request both the posts'
      comments and the authors of those comments the request would look like this:
       ```app/routes/posts.js
      import Route from '@ember/routing/route';
       export default class PostsRoute extends Route {
        model() {
          return this.store.findAll('post', { include: 'comments,comments.author' });
        }
      }
      ```
       See [query](../methods/query?anchor=query) to only get a subset of records from the server.
       @since 1.13.0
      @method findAll
      @public
      @param {String} modelName
      @param {Object} options
      @return {Promise} promise
    */
    findAll(modelName, options) {
      let normalizedModelName = normalizeModelName(modelName);
      let fetch = this._fetchAll(normalizedModelName, this.peekAll(normalizedModelName), options);
      return fetch;
    }

    /**
      @method _fetchAll
      @private
      @param {Model} modelName
      @param {RecordArray} array
      @return {Promise} promise
    */
    _fetchAll(modelName, array, options) {
      if (options === void 0) {
        options = {};
      }
      let adapter = this.adapterFor(modelName);
      if (options.reload) {
        object.set(array, 'isUpdating', true);
        return promiseArray(_findAll(adapter, this, modelName, options));
      }
      let snapshotArray = array._createSnapshot(options);
      if (options.reload !== false) {
        if (adapter.shouldReloadAll && adapter.shouldReloadAll(this, snapshotArray) || !adapter.shouldReloadAll && snapshotArray.length === 0) {
          object.set(array, 'isUpdating', true);
          return promiseArray(_findAll(adapter, this, modelName, options));
        }
      }
      if (options.backgroundReload === false) {
        return promiseArray(RSVP.Promise.resolve(array));
      }
      if (options.backgroundReload || !adapter.shouldBackgroundReloadAll || adapter.shouldBackgroundReloadAll(this, snapshotArray)) {
        object.set(array, 'isUpdating', true);
        _findAll(adapter, this, modelName, options);
      }
      return promiseArray(RSVP.Promise.resolve(array));
    }

    /**
      @method _didUpdateAll
      @param {String} modelName
      @private
    */
    _didUpdateAll(modelName) {
      this.recordArrayManager._didUpdateAll(modelName);
    }

    /**
      This method returns a filtered array that contains all of the
      known records for a given type in the store.
       Note that because it's just a filter, the result will contain any
      locally created records of the type, however, it will not make a
      request to the backend to retrieve additional records. If you
      would like to request all the records from the backend please use
      [store.findAll](../methods/findAll?anchor=findAll).
       Also note that multiple calls to `peekAll` for a given type will always
      return the same `RecordArray`.
       Example
       ```javascript
      let localPosts = store.peekAll('post');
      ```
       @since 1.13.0
      @method peekAll
      @public
      @param {String} modelName
      @return {RecordArray}
    */
    peekAll(modelName) {
      let normalizedModelName = normalizeModelName(modelName);
      return this.recordArrayManager.liveRecordArrayFor(normalizedModelName);
    }

    /**
      This method unloads all records in the store.
      It schedules unloading to happen during the next run loop.
       Optionally you can pass a type which unload all records for a given type.
       ```javascript
      store.unloadAll();
      store.unloadAll('post');
      ```
       @method unloadAll
      @public
      @param {String} modelName
    */
    unloadAll(modelName) {
      const factory = internalModelFactoryFor(this);
      if (modelName === undefined) {
        factory.clear();
      } else {
        let normalizedModelName = normalizeModelName(modelName);
        factory.clear(normalizedModelName);
      }
    }
    filter() {
    }

    // ..............
    // . PERSISTING .
    // ..............

    /**
      This method is called by `record.save`, and gets passed a
      resolver for the promise that `record.save` returns.
       It schedules saving to happen at the end of the run loop.
       @method scheduleSave
      @private
      @param {InternalModel} internalModel
      @param {Resolver} resolver
      @param {Object} options
    */
    scheduleSave(internalModel, resolver, options) {
      if (internalModel._isRecordFullyDeleted()) {
        resolver.resolve();
        return resolver.promise;
      }
      internalModel.adapterWillCommit();
      if (!options) {
        options = {};
      }
      let recordData = internalModel._recordData;
      let operation = 'updateRecord';

      // TODO handle missing isNew
      if (recordData.isNew && recordData.isNew()) {
        operation = 'createRecord';
      } else if (recordData.isDeleted && recordData.isDeleted()) {
        operation = 'deleteRecord';
      }
      options[SaveOp] = operation;
      let fetchManagerPromise = this._fetchManager.scheduleSave(internalModel.identifier, options);
      let promise = fetchManagerPromise.then(payload => {
        /*
        Note to future spelunkers hoping to optimize.
        We rely on this `run` to create a run loop if needed
        that `store._push` and `store.didSaveRecord` will both share.
         We use `join` because it is often the case that we
        have an outer run loop available still from the first
        call to `store._push`;
        */
        this._backburner.join(() => {
          let data = payload && payload.data;
          this.didSaveRecord(internalModel, {
            data
          }, operation);
          if (payload && payload.included) {
            this._push({
              data: null,
              included: payload.included
            });
          }
        });
      }, e => {
        if (typeof e === 'string') {
          throw e;
        }
        const {
          error,
          parsedErrors
        } = e;
        this.recordWasInvalid(internalModel, parsedErrors, error);
        throw error;
      });
      return promise;
    }

    /**
      This method is called at the end of the run loop, and
      flushes any records passed into `scheduleSave`
       @method flushPendingSave
      @private
    */
    flushPendingSave() {
      // assert here
      return;
    }

    /**
      This method is called once the promise returned by an
      adapter's `createRecord`, `updateRecord` or `deleteRecord`
      is resolved.
       If the data provides a server-generated ID, it will
      update the record and the store's indexes.
       @method didSaveRecord
      @private
      @param {InternalModel} internalModel the in-flight internal model
      @param {Object} data optional data (see above)
      @param {string} op the adapter operation that was committed
    */
    didSaveRecord(internalModel, dataArg, op) {
      let data;
      if (dataArg) {
        data = dataArg.data;
      }
      const cache = this.identifierCache;
      const identifier = internalModel.identifier;
      if (op !== 'deleteRecord' && data) {
        cache.updateRecordIdentifier(identifier, data);
      }

      //We first make sure the primary data has been updated
      //TODO try to move notification to the user to the end of the runloop
      internalModel.adapterDidCommit(data);
    }

    /**
      This method is called once the promise returned by an
      adapter's `createRecord`, `updateRecord` or `deleteRecord`
      is rejected with a `InvalidError`.
       @method recordWasInvalid
      @private
      @param {InternalModel} internalModel
      @param {Object} errors
    */
    recordWasInvalid(internalModel, parsedErrors, error) {
      internalModel.adapterDidInvalidate(parsedErrors, error);
    }

    /**
      This method is called once the promise returned by an
      adapter's `createRecord`, `updateRecord` or `deleteRecord`
      is rejected (with anything other than a `InvalidError`).
       @method recordWasError
      @private
      @param {InternalModel} internalModel
      @param {Error} error
    */
    recordWasError(internalModel, error) {
      internalModel.adapterDidError(error);
    }

    /**
      Sets newly received ID from the adapter's `createRecord`, `updateRecord`
      or `deleteRecord`.
       @method setRecordId
      @private
      @param {String} modelName
      @param {string} newId
      @param {string} clientId
     */
    setRecordId(modelName, newId, clientId) {
      internalModelFactoryFor(this).setRecordId(modelName, newId, clientId);
    }

    // ................
    // . LOADING DATA .
    // ................

    /**
      This internal method is used by `push`.
       @method _load
      @private
      @param {Object} data
    */
    _load(data) {
      const resource = constructResource(normalizeModelName(data.type), ensureStringId(data.id), coerceId(data.lid));
      let internalModel = internalModelFactoryFor(this).lookup(resource, data);

      // store.push will be from empty
      // findRecord will be from root.loading
      // all else will be updates
      const isLoading = internalModel.currentState.stateName === 'root.loading';
      const isUpdate = internalModel.currentState.isEmpty === false && !isLoading;

      // exclude store.push (root.empty) case
      let identifier = internalModel.identifier;
      if (isUpdate || isLoading) {
        let updatedIdentifier = this.identifierCache.updateRecordIdentifier(identifier, data);
        if (updatedIdentifier !== identifier) {
          // we encountered a merge of identifiers in which
          // two identifiers (and likely two internalModels)
          // existed for the same resource. Now that we have
          // determined the correct identifier to use, make sure
          // that we also use the correct internalModel.
          identifier = updatedIdentifier;
          internalModel = internalModelFactoryFor(this).lookup(identifier);
        }
      }
      internalModel.setupData(data);
      if (!isUpdate) {
        this.recordArrayManager.recordDidChange(identifier);
      }
      return internalModel;
    }

    /**
      Push some data for a given type into the store.
       This method expects normalized [JSON API](http://jsonapi.org/) document. This means you have to follow [JSON API specification](http://jsonapi.org/format/) with few minor adjustments:
      - record's `type` should always be in singular, dasherized form
      - members (properties) should be camelCased
       [Your primary data should be wrapped inside `data` property](http://jsonapi.org/format/#document-top-level):
       ```js
      store.push({
        data: {
          // primary data for single record of type `Person`
          id: '1',
          type: 'person',
          attributes: {
            firstName: 'Daniel',
            lastName: 'Kmak'
          }
        }
      });
      ```
       [Demo.](http://ember-twiddle.com/fb99f18cd3b4d3e2a4c7)
       `data` property can also hold an array (of records):
       ```js
      store.push({
        data: [
          // an array of records
          {
            id: '1',
            type: 'person',
            attributes: {
              firstName: 'Daniel',
              lastName: 'Kmak'
            }
          },
          {
            id: '2',
            type: 'person',
            attributes: {
              firstName: 'Tom',
              lastName: 'Dale'
            }
          }
        ]
      });
      ```
       [Demo.](http://ember-twiddle.com/69cdbeaa3702159dc355)
       There are some typical properties for `JSONAPI` payload:
      * `id` - mandatory, unique record's key
      * `type` - mandatory string which matches `model`'s dasherized name in singular form
      * `attributes` - object which holds data for record attributes - `attr`'s declared in model
      * `relationships` - object which must contain any of the following properties under each relationships' respective key (example path is `relationships.achievements.data`):
        - [`links`](http://jsonapi.org/format/#document-links)
        - [`data`](http://jsonapi.org/format/#document-resource-object-linkage) - place for primary data
        - [`meta`](http://jsonapi.org/format/#document-meta) - object which contains meta-information about relationship
       For this model:
       ```app/models/person.js
      import Model, { attr, hasMany } from '@ember-data/model';
       export default class PersonRoute extends Route {
        @attr('string') firstName;
        @attr('string') lastName;
         @hasMany('person') children;
      }
      ```
       To represent the children as IDs:
       ```js
      {
        data: {
          id: '1',
          type: 'person',
          attributes: {
            firstName: 'Tom',
            lastName: 'Dale'
          },
          relationships: {
            children: {
              data: [
                {
                  id: '2',
                  type: 'person'
                },
                {
                  id: '3',
                  type: 'person'
                },
                {
                  id: '4',
                  type: 'person'
                }
              ]
            }
          }
        }
      }
      ```
       [Demo.](http://ember-twiddle.com/343e1735e034091f5bde)
       To represent the children relationship as a URL:
       ```js
      {
        data: {
          id: '1',
          type: 'person',
          attributes: {
            firstName: 'Tom',
            lastName: 'Dale'
          },
          relationships: {
            children: {
              links: {
                related: '/people/1/children'
              }
            }
          }
        }
      }
      ```
       If you're streaming data or implementing an adapter, make sure
      that you have converted the incoming data into this form. The
      store's [normalize](../methods/normalize?anchor=normalize) method is a convenience
      helper for converting a json payload into the form Ember Data
      expects.
       ```js
      store.push(store.normalize('person', data));
      ```
       This method can be used both to push in brand new
      records, as well as to update existing records.
       @method push
      @public
      @param {Object} data
      @return the record(s) that was created or
        updated.
    */

    push(data) {
      let pushed = this._push(data);
      if (Array.isArray(pushed)) {
        let records = pushed.map(internalModel => internalModel.getRecord());
        return records;
      }
      if (pushed === null) {
        return null;
      }
      let record = pushed.getRecord();
      return record;
    }

    /**
      Push some data in the form of a json-api document into the store,
      without creating materialized records.
       @method _push
      @private
      @param {Object} jsonApiDoc
      @return {InternalModel|Array<InternalModel>} pushed InternalModel(s)
    */
    _push(jsonApiDoc) {
      let internalModelOrModels = this._backburner.join(() => {
        let included = jsonApiDoc.included;
        let i, length;
        if (included) {
          for (i = 0, length = included.length; i < length; i++) {
            this._pushInternalModel(included[i]);
          }
        }
        if (Array.isArray(jsonApiDoc.data)) {
          length = jsonApiDoc.data.length;
          let internalModels = new Array(length);
          for (i = 0; i < length; i++) {
            internalModels[i] = this._pushInternalModel(jsonApiDoc.data[i]);
          }
          return internalModels;
        }
        if (jsonApiDoc.data === null) {
          return null;
        }
        return this._pushInternalModel(jsonApiDoc.data);
      });

      // this typecast is necessary because `backburner.join` is mistyped to return void
      return internalModelOrModels;
    }
    _pushInternalModel(data) {
      data.type;

      // Actually load the record into the store.
      let internalModel = this._load(data);

      //    this._setupRelationshipsForModel(internalModel, data);

      return internalModel;
    }

    /**
      Push some raw data into the store.
       This method can be used both to push in brand new
      records, as well as to update existing records. You
      can push in more than one type of object at once.
      All objects should be in the format expected by the
      serializer.
       ```app/serializers/application.js
      import RESTSerializer from '@ember-data/serializer/rest';
       export default class ApplicationSerializer extends RESTSerializer;
      ```
       ```js
      let pushData = {
        posts: [
          { id: 1, postTitle: "Great post", commentIds: [2] }
        ],
        comments: [
          { id: 2, commentBody: "Insightful comment" }
        ]
      }
       store.pushPayload(pushData);
      ```
       By default, the data will be deserialized using a default
      serializer (the application serializer if it exists).
       Alternatively, `pushPayload` will accept a model type which
      will determine which serializer will process the payload.
       ```app/serializers/application.js
      import RESTSerializer from '@ember-data/serializer/rest';
        export default class ApplicationSerializer extends RESTSerializer;
      ```
       ```app/serializers/post.js
      import JSONSerializer from '@ember-data/serializer/json';
       export default JSONSerializer;
      ```
       ```js
      store.pushPayload(pushData); // Will use the application serializer
      store.pushPayload('post', pushData); // Will use the post serializer
      ```
       @method pushPayload
      @public
      @param {String} modelName Optionally, a model type used to determine which serializer will be used
      @param {Object} inputPayload
    */
    pushPayload(modelName, inputPayload) {
      let serializer;
      let payload;
      if (!inputPayload) {
        payload = modelName;
        serializer = this.serializerFor('application');
      } else {
        payload = inputPayload;
        let normalizedModelName = normalizeModelName(modelName);
        serializer = this.serializerFor(normalizedModelName);
      }
      serializer.pushPayload(this, payload);
    }
    reloadManyArray(manyArray, internalModel, key, options) {
      return internalModel.reloadHasMany(key, options);
    }
    reloadBelongsTo(belongsToProxy, internalModel, key, options) {
      return internalModel.reloadBelongsTo(key, options);
    }
    _internalModelForResource(resource) {
      return internalModelFactoryFor(this).getByResource(resource);
    }

    /**
     * TODO Only needed temporarily for test support
     *
     * @method _internalModelForId
     * @internal
     */
    _internalModelForId(type, id, lid) {
      const resource = constructResource(type, id, lid);
      return internalModelFactoryFor(this).lookup(resource);
    }
    serializeRecord(record, options) {
      let identifier = recordIdentifierFor(record);
      let internalModel = internalModelFactoryFor(this).peek(identifier);
      // TODO we used to check if the record was destroyed here
      return internalModel.createSnapshot(options).serialize(options);
    }
    saveRecord(record, options) {
      let identifier = recordIdentifierFor(record);
      let internalModel = internalModelFactoryFor(this).peek(identifier);
      // TODO we used to check if the record was destroyed here
      // Casting can be removed once REQUEST_SERVICE ff is turned on
      // because a `Record` is provided there will always be a matching internalModel
      return internalModel.save(options).then(() => record);
    }
    relationshipReferenceFor(identifier, key) {
      let stableIdentifier = this.identifierCache.getOrCreateRecordIdentifier(identifier);
      let internalModel = internalModelFactoryFor(this).peek(stableIdentifier);
      // TODO we used to check if the record was destroyed here
      return internalModel.referenceFor(null, key);
    }

    /**
     * Manages setting setting up the recordData returned by createRecordDataFor
     *
     * @method _createRecordData
     * @internal
     */
    _createRecordData(identifier) {
      const recordData = this.createRecordDataFor(identifier.type, identifier.id, identifier.lid, this._storeWrapper);
      setRecordDataFor(identifier, recordData);
      // TODO this is invalid for v2 recordData but required
      // for v1 recordData. Remember to remove this once the
      // RecordData manager handles converting recordData to identifier
      setRecordIdentifier(recordData, identifier);
      return recordData;
    }

    /**
     * Instantiation hook allowing applications or addons to configure the store
     * to utilize a custom RecordData implementation.
     *
     * @method createRecordDataFor
     * @public
     * @param modelName
     * @param id
     * @param clientId
     * @param storeWrapper
     */
    createRecordDataFor(modelName, id, clientId, storeWrapper) {
      {
        // we can't greedily use require as this causes
        // a cycle we can't easily fix (or clearly pin point) at present.
        //
        // it can be reproduced in partner tests by running
        // node ./scripts/packages-for-commit.js && yarn test-external:ember-observer
        if (_RecordData === undefined) {
          _RecordData = require__default["default"]('@ember-data/record-data/-private').RecordData;
        }
        let identifier = this.identifierCache.getOrCreateRecordIdentifier({
          type: modelName,
          id,
          lid: clientId
        });
        return new _RecordData(identifier, storeWrapper);
      }
    }

    /**
     * @internal
     */
    __recordDataFor(resource) {
      const identifier = this.identifierCache.getOrCreateRecordIdentifier(resource);
      return this.recordDataFor(identifier, false);
    }

    /**
     * @internal
     */
    recordDataFor(identifier, isCreate) {
      let internalModel;
      if (isCreate === true) {
        internalModel = internalModelFactoryFor(this).build({
          type: identifier.type,
          id: null
        });
        internalModel.send('loadedData');
        internalModel.didCreateRecord();
      } else {
        internalModel = internalModelFactoryFor(this).lookup(identifier);
      }
      return internalModel._recordData;
    }

    /**
      `normalize` converts a json payload into the normalized form that
      [push](../methods/push?anchor=push) expects.
       Example
       ```js
      socket.on('message', function(message) {
        let modelName = message.model;
        let data = message.data;
        store.push(store.normalize(modelName, data));
      });
      ```
       @method normalize
      @public
      @param {String} modelName The name of the model type for this payload
      @param {Object} payload
      @return {Object} The normalized payload
    */
    normalize(modelName, payload) {
      let normalizedModelName = normalizeModelName(modelName);
      let serializer = this.serializerFor(normalizedModelName);
      let model = this.modelFor(normalizedModelName);
      return serializer.normalize(model, payload);
    }
    newClientId() {
    }

    // ...............
    // . DESTRUCTION .
    // ...............

    /**
     * TODO remove test usage
     *
     * @internal
     */
    _internalModelsFor(modelName) {
      return internalModelFactoryFor(this).modelMapFor(modelName);
    }

    // ......................
    // . PER-TYPE ADAPTERS
    // ......................

    /**
      Returns an instance of the adapter for a given type. For
      example, `adapterFor('person')` will return an instance of
      `App.PersonAdapter`.
       If no `App.PersonAdapter` is found, this method will look
      for an `App.ApplicationAdapter` (the default adapter for
      your entire application).
       If no `App.ApplicationAdapter` is found, it will return
      the value of the `defaultAdapter`.
       @method adapterFor
      @public
      @param {String} modelName
      @return Adapter
    */
    adapterFor(modelName) {
      let normalizedModelName = normalizeModelName(modelName);
      let {
        _adapterCache
      } = this;
      let adapter = _adapterCache[normalizedModelName];
      if (adapter) {
        return adapter;
      }
      let owner = application.getOwner(this);

      // name specific adapter
      adapter = owner.lookup(`adapter:${normalizedModelName}`);
      if (adapter !== undefined) {
        object.set(adapter, 'store', this);
        _adapterCache[normalizedModelName] = adapter;
        return adapter;
      }

      // no adapter found for the specific name, fallback and check for application adapter
      adapter = _adapterCache.application || owner.lookup('adapter:application');
      if (adapter !== undefined) {
        object.set(adapter, 'store', this);
        _adapterCache[normalizedModelName] = adapter;
        _adapterCache.application = adapter;
        return adapter;
      }

      // final fallback, no model specific adapter, no application adapter, no
      // `adapter` property on store: use json-api adapter
      // TODO we should likely deprecate this?
      adapter = _adapterCache['-json-api'] || owner.lookup('adapter:-json-api');
      object.set(adapter, 'store', this);
      _adapterCache[normalizedModelName] = adapter;
      _adapterCache['-json-api'] = adapter;
      return adapter;
    }

    // ..............................
    // . RECORD CHANGE NOTIFICATION .
    // ..............................

    /**
      Returns an instance of the serializer for a given type. For
      example, `serializerFor('person')` will return an instance of
      `App.PersonSerializer`.
       If no `App.PersonSerializer` is found, this method will look
      for an `App.ApplicationSerializer` (the default serializer for
      your entire application).
       If a serializer cannot be found on the adapter, it will fall back
      to an instance of `JSONSerializer`.
       @method serializerFor
      @public
      @param {String} modelName the record to serialize
      @return {Serializer}
    */
    serializerFor(modelName) {
      let normalizedModelName = normalizeModelName(modelName);
      let {
        _serializerCache
      } = this;
      let serializer = _serializerCache[normalizedModelName];
      if (serializer) {
        return serializer;
      }
      let owner = application.getOwner(this);

      // by name
      serializer = owner.lookup(`serializer:${normalizedModelName}`);
      if (serializer !== undefined) {
        object.set(serializer, 'store', this);
        _serializerCache[normalizedModelName] = serializer;
        return serializer;
      }

      // no serializer found for the specific model, fallback and check for application serializer
      serializer = _serializerCache.application || owner.lookup('serializer:application');
      if (serializer !== undefined) {
        object.set(serializer, 'store', this);
        _serializerCache[normalizedModelName] = serializer;
        _serializerCache.application = serializer;
        return serializer;
      }
    }
    destroy() {
      // enqueue destruction of any adapters/serializers we have created
      for (let adapterName in this._adapterCache) {
        let adapter = this._adapterCache[adapterName];
        if (typeof adapter.destroy === 'function') {
          adapter.destroy();
        }
      }
      for (let serializerName in this._serializerCache) {
        let serializer = this._serializerCache[serializerName];
        if (typeof serializer.destroy === 'function') {
          serializer.destroy();
        }
      }
      {
        const peekGraph = require__default["default"]('@ember-data/record-data/-private').peekGraph;
        let graph = peekGraph(this);
        if (graph) {
          graph.destroy();
        }
      }
      return super.destroy();
    }
    willDestroy() {
      super.willDestroy();
      this.recordArrayManager.destroy();
      this.identifierCache.destroy();

      // destroy the graph before unloadAll
      // since then we avoid churning relationships
      // during unload
      {
        const peekGraph = require__default["default"]('@ember-data/record-data/-private').peekGraph;
        let graph = peekGraph(this);
        if (graph) {
          graph.willDestroy();
        }
      }
      this.unloadAll();
    }
    _updateInternalModel(internalModel) {
      if (this._updatedInternalModels.push(internalModel) !== 1) {
        return;
      }
      runloop._backburner.schedule('actions', this, this._flushUpdatedInternalModels);
    }
    _flushUpdatedInternalModels() {
      let updated = this._updatedInternalModels;
      for (let i = 0, l = updated.length; i < l; i++) {
        updated[i]._triggerDeferredTriggers();
      }
      updated.length = 0;
    }
  }

  /**
   * Flag indicating whether all inverse records are available
   *
   * true if the inverse exists and is loaded (not empty)
   * true if there is no inverse
   * false if the inverse exists and is not loaded (empty)
   *
   * @internal
   * @return {boolean}
   */
  function areAllInverseRecordsLoaded(store, resource) {
    const cache = store.identifierCache;
    if (Array.isArray(resource.data)) {
      // treat as collection
      // check for unloaded records
      let hasEmptyRecords = resource.data.reduce((hasEmptyModel, resourceIdentifier) => {
        return hasEmptyModel || internalModelForRelatedResource(store, cache, resourceIdentifier).currentState.isEmpty;
      }, false);
      return !hasEmptyRecords;
    } else {
      // treat as single resource
      if (!resource.data) {
        return true;
      } else {
        const internalModel = internalModelForRelatedResource(store, cache, resource.data);
        return !internalModel.currentState.isEmpty;
      }
    }
  }
  function internalModelForRelatedResource(store, cache, resource) {
    const identifier = cache.getOrCreateRecordIdentifier(resource);
    return store._internalModelForResource(identifier);
  }
  function isMaybeIdentifier(maybeIdentifier) {
    return Boolean(maybeIdentifier !== null && typeof maybeIdentifier === 'object' && ('id' in maybeIdentifier && 'type' in maybeIdentifier && maybeIdentifier.id && maybeIdentifier.type || maybeIdentifier.lid));
  }

  let _modelForMixin;
  {
    let _found;
    _modelForMixin = function () {
      if (!_found) {
        _found = require__default["default"]('@ember-data/model/-private')._modelForMixin;
      }
      return _found(...arguments);
    };
  }
  class DSModelSchemaDefinitionService {
    constructor(store) {
      this._modelFactoryCache = Object.create(null);
      this._relationshipsDefCache = Object.create(null);
      this._attributesDefCache = Object.create(null);
      this.store = store;
    }

    // Following the existing RD implementation
    attributesDefinitionFor(identifier) {
      let modelName, attributes;
      if (typeof identifier === 'string') {
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
      attributes = this._attributesDefCache[modelName];
      if (attributes === undefined) {
        let modelClass = this.store.modelFor(modelName);
        let attributeMap = object.get(modelClass, 'attributes');
        attributes = Object.create(null);
        attributeMap.forEach((meta, name) => attributes[name] = meta);
        this._attributesDefCache[modelName] = attributes;
      }
      return attributes;
    }

    // Following the existing RD implementation
    relationshipsDefinitionFor(identifier) {
      let modelName, relationships;
      if (typeof identifier === 'string') {
        modelName = identifier;
      } else {
        modelName = identifier.type;
      }
      relationships = this._relationshipsDefCache[modelName];
      if (relationships === undefined) {
        let modelClass = this.store.modelFor(modelName);
        relationships = object.get(modelClass, 'relationshipsObject') || null;
        this._relationshipsDefCache[modelName] = relationships;
      }
      return relationships;
    }
    doesTypeExist(modelName) {
      let normalizedModelName = normalizeModelName(modelName);
      let factory = getModelFactory(this.store, this._modelFactoryCache, normalizedModelName);
      return factory !== null;
    }
  }
  function getModelFactory(store, cache, normalizedModelName) {
    let factory = cache[normalizedModelName];
    if (!factory) {
      factory = _lookupModelFactory(store, normalizedModelName);
      if (!factory && true /* HAS_MODEL_PACKAGE */) {
        //Support looking up mixins as base types for polymorphic relationships
        factory = _modelForMixin(store, normalizedModelName);
      }
      if (!factory) {
        // we don't cache misses in case someone wants to register a missing model
        return null;
      }
      let klass = factory.class;
      if (klass.isModel) {
        let hasOwnModelNameSet = klass.modelName && Object.prototype.hasOwnProperty.call(klass, 'modelName');
        if (!hasOwnModelNameSet) {
          Object.defineProperty(klass, 'modelName', {
            value: normalizedModelName
          });
        }
      }
      cache[normalizedModelName] = factory;
    }
    return factory;
  }
  function _lookupModelFactory(store, normalizedModelName) {
    let owner = application.getOwner(store);
    return owner.factoryFor(`model:${normalizedModelName}`);
  }

  class Store extends CoreStore {
    constructor() {
      super(...arguments);
      this._modelFactoryCache = Object.create(null);
      this._relationshipsDefCache = Object.create(null);
      this._attributesDefCache = Object.create(null);
    }
    instantiateRecord(identifier, createRecordArgs, recordDataFor, notificationManager) {
      let modelName = identifier.type;
      let internalModel = this._internalModelForResource(identifier);
      let createOptions = {
        store: this,
        _internalModel: internalModel,
        // TODO deprecate allowing unknown args setting
        _createProps: createRecordArgs,
        container: null
      };

      // ensure that `getOwner(this)` works inside a model instance
      application.setOwner(createOptions, application.getOwner(this));
      delete createOptions.container;
      let record = this._modelFactoryFor(modelName).create(createOptions);
      return record;
    }
    teardownRecord(record) {
      record.destroy();
    }
    modelFor(modelName) {
      let maybeFactory = this._modelFactoryFor(modelName);

      // for factorFor factory/class split
      let klass = maybeFactory && maybeFactory.class ? maybeFactory.class : maybeFactory;
      if (!klass || !klass.isModel) {
        if (!this.getSchemaDefinitionService().doesTypeExist(modelName)) {
          throw new EmberError__default["default"](`No model was found for '${modelName}' and no schema handles the type`);
        }
        return getShimClass(this, modelName);
      } else {
        return klass;
      }
    }
    _modelFactoryFor(modelName) {
      let normalizedModelName = normalizeModelName(modelName);
      let factory = getModelFactory(this, this._modelFactoryCache, normalizedModelName);
      return factory;
    }
    _hasModelFor(modelName) {
      return this.getSchemaDefinitionService().doesTypeExist(modelName);
    }
    _relationshipMetaFor(modelName, id, key) {
      return this._relationshipsDefinitionFor({
        type: modelName
      })[key];
    }
    _attributesDefinitionFor(identifier) {
      return this.getSchemaDefinitionService().attributesDefinitionFor(identifier);
    }
    _relationshipsDefinitionFor(identifier) {
      return this.getSchemaDefinitionService().relationshipsDefinitionFor(identifier);
    }
    getSchemaDefinitionService() {
      if (!this._schemaDefinitionService) {
        this._schemaDefinitionService = new DSModelSchemaDefinitionService(this);
      }
      return this._schemaDefinitionService;
    }
  }

  exports.AdapterPopulatedRecordArray = AdapterPopulatedRecordArray;
  exports.InternalModel = InternalModel;
  exports.PromiseArray = PromiseArray;
  exports.PromiseObject = PromiseObject;
  exports.RecordArray = RecordArray;
  exports.RecordArrayManager = RecordArrayManager;
  exports.RecordDataStoreWrapper = RecordDataStoreWrapper;
  exports.RootState = RootState$1;
  exports.Snapshot = Snapshot;
  exports.SnapshotRecordArray = SnapshotRecordArray;
  exports.Store = Store;
  exports.WeakCache = WeakCache$1;
  exports.coerceId = coerceId;
  exports.deprecatedPromiseObject = deprecatedPromiseObject;
  exports.errorsArrayToHash = errorsArrayToHash;
  exports.errorsHashToArray = errorsHashToArray;
  exports.normalizeModelName = normalizeModelName;
  exports.recordDataFor = recordDataFor;
  exports.recordIdentifierFor = recordIdentifierFor;
  exports.removeRecordDataFor = removeRecordDataFor;
  exports.setIdentifierForgetMethod = setIdentifierForgetMethod;
  exports.setIdentifierGenerationMethod = setIdentifierGenerationMethod;
  exports.setIdentifierResetMethod = setIdentifierResetMethod;
  exports.setIdentifierUpdateMethod = setIdentifierUpdateMethod;

  Object.defineProperty(exports, '__esModule', { value: true });

}));
